/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/*!
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@                                         @@@@
 *!   @@@   THIS FILE WAS AUTOGENERATED BY TOOL   @@@@
 *!   @@@   CHANGES MUST TARGET SCRIPTS/SWAGGER   @@@@
 *!   @@@                                         @@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!   @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
 *!
 */

import { stringify } from 'query-string'
import { isEmpty, pickBy } from 'lodash'

const emptyParams = Object.freeze({})

const getRawBasePath = () => `/api/v1`
const getBasePath = () => `/api/v1/regions`

function buildQueryString(parameters) {
  const nonNullParams = pickBy(parameters, (parameter) => parameter != null)
  return isEmpty(nonNullParams) ? `` : `?${stringify(nonNullParams)}`
}

export function encode(strings, ...params) {
  let result = ''

  for (let i = 0; i < strings.length; i++) {
    result += strings[i]

    if (params[i]) {
      const value = params[i]

      if (Array.isArray(value)) {
        result += value.map(encodeURIComponent).join(`,`)
      } else if (
        value != null &&
        typeof value === 'object' &&
        value.hasOwnProperty('__path') &&
        typeof value.__path === 'string'
      ) {
        result += value.__path
      } else {
        result += encodeURIComponent(value)
      }
    }
  }

  return result
}

/**
 * GET /api/v1/account
 * Operation: `get-current-account`
 * Fetch current account information.
 * @see 200 Account fetched successfully. See {@link AccountResponse}
 * @see 404 Account not found. (code: `accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCurrentAccountUrl(): string {
  const path = `/account`
  return `${getRawBasePath()}${path}`
}

/**
 * PUT /api/v1/account
 * Operation: `update-current-account`
 * Updates the current account.
 * @see Accepts a request payload of type {@link AccountUpdateRequest}
 * @see 200 Account updated successfully. See {@link AccountResponse}
 * @see 404 Account not found. (code: `accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateCurrentAccountUrl(): string {
  const path = `/account`
  return `${getRawBasePath()}${path}`
}

/**
 * PATCH /api/v1/account
 * Operation: `patch-current-account`
 * Updates the current account.
 * @see Accepts a request payload of type {@link string}
 * @see 200 Account updated successfully. See {@link AccountResponse}
 * @see 404 Account not found. (code: `accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function patchCurrentAccountUrl(): string {
  const path = `/account`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/regions/{region_id}/aliases/{alias}
 * Operation: `get-deployment-alias`
 * Return the deployment alias information for the given alias.
 * @param {string} params.alias The deployment alias to be transferred.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The deployment alias information for the given deployment alias. See {@link DeploymentAliasResponse}
 * @see 404 The alias does not exist in this region. (code: `deployment_alias.alias_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentAliasUrl(params: GetDeploymentAliasUrlParams): string {
  const path = encode`/${params.regionId}/aliases/${params.alias}`
  return `${getBasePath()}${path}`
}

export interface GetDeploymentAliasUrlParams {
  alias: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/aliases/{alias}/transfer
 * Operation: `transfer-deployment-alias-ownership`
 * Transfer the ownership of an inactive deployment alias from one organization to another.
 * @param {string} params.alias The deployment alias to be transferred.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link DeploymentAliasTransferRequest}
 * @see 200 The transferred Deployment Alias. See {@link DeploymentAliasResponse}
 * @see 400 You cannot modify an alias that is actively in use. (code: `deployment_alias.active_alias`). See {@link BasicFailedReply}
 * @see 404 The alias does not exist in this region. (code: `deployment_alias.alias_not_found`). See {@link BasicFailedReply}
 * @see 409 The alias was modified by another process. Refresh and try again. (code: `deployment_alias.alias_modified_concurrently`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function transferDeploymentAliasOwnershipUrl(
  params: TransferDeploymentAliasOwnershipUrlParams,
): string {
  const path = encode`/${params.regionId}/aliases/${params.alias}/transfer`
  return `${getBasePath()}${path}`
}

export interface TransferDeploymentAliasOwnershipUrlParams {
  alias: string
  regionId: string
}

/**
 * GET /api/v1/all/_search
 * Operation: `search-all`
 * Searches for entities matching the given query.
 * @param {string} params.q The query string to search with.
 * @param {boolean} params.showPlanLogs Includes the active, pending, and historical plan information in the attempt log. NOTE: This option can result in large responses.
 * @see 200 The results of the search. See {@link SearchResults}
 * @return {string} A fully-interpolated and escaped URL
 */
export function searchAllUrl(params: SearchAllUrlParams): string {
  const queryString = buildQueryString({
    q: params.q,
    show_plan_logs: params.showPlanLogs === undefined ? false : params.showPlanLogs,
  })
  const path = `/all/_search`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SearchAllUrlParams {
  q: string
  showPlanLogs?: boolean | null
}

/**
 * GET /api/v1/regions/{region_id}/authentication/_authenticate
 * Operation: `authenticate`
 * Authenticate a user and retrieve information about the authenticated user.
 * @param {boolean} params.showRoleAssignments If true, role assignments are returned.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Authenticated user. See {@link AuthenticationResponse}
 * @see 401 The supplied authentication is invalid. (code: `root.unauthenticated`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function authenticateUrl(params: AuthenticateUrlParams): string {
  const queryString = buildQueryString({
    show_role_assignments:
      params.showRoleAssignments === undefined ? false : params.showRoleAssignments,
  })
  const path = encode`/${params.regionId}/authentication/_authenticate`
  return `${getBasePath()}${path}${queryString}`
}

export interface AuthenticateUrlParams {
  showRoleAssignments?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/authorization/_check
 * Operation: `check-authorization`
 * Checks whether the current authenticated session has the authorization presented.
 * @see Accepts a request payload of type {@link AuthzCheckRequest}
 * @see 200 Authorization check results retrieved. See {@link AuthzCheckResponse}
 * @see 400 * Requested resource type not supported. (code: `authz.check.unsupported_kind`)
 * The owner provided could not be parsed as a valid owner ID. (code: `authz.check.unparseable_owner`)
 * Authorization request had too many checks. (code: `authz.check.too_many_checks`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function checkAuthorizationUrl(): string {
  const path = `/authorization/_check`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/authorization/roles
 * Operation: `get-authorization-roles`
 * Returns the roles that are visible to the current user.
 * @param {boolean} params.showHidden If true, hidden Roles are returned.
 * @see 200 The roles that are visible to the current user. See {@link AuthzRoles}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAuthorizationRolesUrl(
  params: GetAuthorizationRolesUrlParams = emptyParams,
): string {
  const queryString = buildQueryString({
    show_hidden: params.showHidden === undefined ? false : params.showHidden,
  })
  const path = `/authorization/roles`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetAuthorizationRolesUrlParams {
  showHidden?: boolean | null
}

/**
 * GET /api/v1/billing/costs/{organization_id}
 * Operation: `get-costs-overview`
 * EXPERIMENTAL (it may change in future versions): Retrieves an overview of the costs by organization ID.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.from A datetime for the beginning of the desired range for which to fetch costs. Defaults to start of current month.
 * @param {string} params.to A datetime for the end of the desired range for which to fetch costs. Defaults to the current date.
 * @see 200 Top-level cost overview for the organization. See {@link CostsOverview}
 * @see 400 The specified date range is invalid. (code: `costs.invalid_date_range`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 An error occurred when fetching an overview of the costs for the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCostsOverviewUrl(params: GetCostsOverviewUrlParams): string {
  const queryString = buildQueryString({
    from: params.from,
    to: params.to,
  })
  const path = encode`/billing/costs/${params.organizationId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetCostsOverviewUrlParams {
  organizationId: string
  from?: string | null
  to?: string | null
}

/**
 * GET /api/v1/billing/costs/{organization_id}/charts
 * Operation: `get-costs-charts`
 * EXPERIMENTAL (it may change in future versions): Retrieves the usage charts for the organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.from A datetime for the beginning of the desired range for which to fetch costs. Defaults to start of current month.
 * @param {string} params.to A datetime for the end of the desired range for which to fetch costs. Defaults to the current date.
 * @param {string} params.bucketingStrategy The desired bucketing strategy for the charts. Defaults to `daily`. Default: daily
 * @see 200 The usage charts of an organization. See {@link ChartItems}
 * @see 400 The specified date range is invalid. (code: `costs.invalid_date_range`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 429 Too many requests. (code: `billing_service.rate_limited`). See {@link BasicFailedReply}
 * @see 500 Error fetching the itemized costs for the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCostsChartsUrl(params: GetCostsChartsUrlParams): string {
  const queryString = buildQueryString({
    from: params.from,
    to: params.to,
    bucketing_strategy: params.bucketingStrategy === undefined ? `daily` : params.bucketingStrategy,
  })
  const path = encode`/billing/costs/${params.organizationId}/charts`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetCostsChartsUrlParams {
  organizationId: string
  from?: string | null
  to?: string | null
  bucketingStrategy?: string | null
}

/**
 * GET /api/v1/billing/costs/{organization_id}/deployments
 * Operation: `get-costs-deployments`
 * EXPERIMENTAL (it may change in future versions): Retrieves the costs associated with all deployments for the organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.from A datetime for the beginning of the desired range for which to fetch activity. Defaults to start of current month.
 * @param {string} params.to A datetime for the end of the desired range for which to fetch activity. Defaults to the current date.
 * @see 200 The costs associated to a set of products. See {@link DeploymentsCosts}
 * @see 400 Invalid date range specified. (code: `costs.invalid_date_range`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 An error occurred when fetching the costs associated with all deployments for the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCostsDeploymentsUrl(params: GetCostsDeploymentsUrlParams): string {
  const queryString = buildQueryString({
    from: params.from,
    to: params.to,
  })
  const path = encode`/billing/costs/${params.organizationId}/deployments`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetCostsDeploymentsUrlParams {
  organizationId: string
  from?: string | null
  to?: string | null
}

/**
 * GET /api/v1/billing/costs/{organization_id}/deployments/{deployment_id}/charts
 * Operation: `get-costs-charts-by-deployment`
 * EXPERIMENTAL (it may change in future versions): Retrieves the usage charts for the given  deployment.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.deploymentId Id of a Deployment.
 * @param {string} params.from A datetime for the beginning of the desired range for which to fetch costs. Defaults to start of current month.
 * @param {string} params.to A datetime for the end of the desired range for which to fetch costs. Defaults to the current date.
 * @param {string} params.bucketingStrategy The desired bucketing strategy for the charts. Defaults to `daily`. Default: daily
 * @see 200 The usage charts of a deployment. See {@link ChartItems}
 * @see 400 The specified date range is invalid. (code: `costs.invalid_date_range`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 429 Too many requests. (code: `billing_service.rate_limited`). See {@link BasicFailedReply}
 * @see 500 Error fetching the itemized costs for the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCostsChartsByDeploymentUrl(params: GetCostsChartsByDeploymentUrlParams): string {
  const queryString = buildQueryString({
    from: params.from,
    to: params.to,
    bucketing_strategy: params.bucketingStrategy === undefined ? `daily` : params.bucketingStrategy,
  })
  const path = encode`/billing/costs/${params.organizationId}/deployments/${params.deploymentId}/charts`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetCostsChartsByDeploymentUrlParams {
  organizationId: string
  deploymentId: string
  from?: string | null
  to?: string | null
  bucketingStrategy?: string | null
}

/**
 * GET /api/v1/billing/costs/{organization_id}/deployments/{deployment_id}/items
 * Operation: `get-costs-items-by-deployment`
 * EXPERIMENTAL (it may change in future versions): Retrieves the itemized costs for the given deployment.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.deploymentId Id of a Deployment.
 * @param {string} params.from A datetime for the beginning of the desired range for which to fetch costs. Defaults to start of current month.
 * @param {string} params.to A datetime for the end of the desired range for which to fetch costs. Defaults to the current date.
 * @see 200 The costs associated to a set items billed for a single deployment. See {@link ItemsCosts}
 * @see 400 The specified date range is invalid. (code: `costs.invalid_date_range`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 * The organization was not found. (code: `organization.not_found`)
 * The Elasticsearch cluster was not found. (code: `billing_service.es_cluster_id_not_found`). See {@link BasicFailedReply}
 * @see 500 An error occurred when fetching the itemized costs for the given deployment of the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCostsItemsByDeploymentUrl(params: GetCostsItemsByDeploymentUrlParams): string {
  const queryString = buildQueryString({
    from: params.from,
    to: params.to,
  })
  const path = encode`/billing/costs/${params.organizationId}/deployments/${params.deploymentId}/items`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetCostsItemsByDeploymentUrlParams {
  organizationId: string
  deploymentId: string
  from?: string | null
  to?: string | null
}

/**
 * GET /api/v1/billing/costs/{organization_id}/items
 * Operation: `get-costs-items`
 * EXPERIMENTAL (it may change in future versions): Retrieves the itemized costs for the organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.from A datetime for the beginning of the desired range for which to fetch costs. Defaults to start of current month.
 * @param {string} params.to A datetime for the end of the desired range for which to fetch costs. Defaults to the current date.
 * @see 200 The costs associated to a set of items. See {@link ItemsCosts}
 * @see 400 The specified date range is invalid. (code: `costs.invalid_date_range`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 Error fetching the itemized costs for the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCostsItemsUrl(params: GetCostsItemsUrlParams): string {
  const queryString = buildQueryString({
    from: params.from,
    to: params.to,
  })
  const path = encode`/billing/costs/${params.organizationId}/items`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetCostsItemsUrlParams {
  organizationId: string
  from?: string | null
  to?: string | null
}

/**
 * GET /api/v1/regions/{region_id}/comments/{resource_type}/{resource_id}
 * Operation: `list-comment`
 * Retrieves all the comments for a given Resource, in reverse modified time order.
 * @param {string} params.resourceType The kind of Resource that a Comment belongs to. Should be one of [elasticsearch, kibana, apm, appsearch, enterprise_search, integrations_server, allocator, constructor, runner, proxy].
 * @param {string} params.resourceId Id of the Resource that a Comment belongs to.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Comments. See {@link CommentsWithMetas}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listCommentUrl(params: ListCommentUrlParams): string {
  const path = encode`/${params.regionId}/comments/${params.resourceType}/${params.resourceId}`
  return `${getBasePath()}${path}`
}

export interface ListCommentUrlParams {
  resourceType: string
  resourceId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/comments/{resource_type}/{resource_id}
 * Operation: `create-comment`
 * Creates a comment for the given Resource.
 * @param {string} params.resourceType The kind of Resource that a Comment belongs to. Should be one of [elasticsearch, kibana, apm, appsearch, enterprise_search, integrations_server, allocator, constructor, runner, proxy].
 * @param {string} params.resourceId Id of the Resource that a Comment belongs to.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link CommentCreateRequest}
 * @see 201 The Comment that was just created. See {@link Comment}
 * @see 401 Your current session does not have a user id associated with it. (code: `comments.no_user_id`). See {@link BasicFailedReply}
 * @see 404 No Resource of the given type and id exist. (code: `comments.resource_does_not_exist`). See {@link BasicFailedReply}
 * @see 409 A Comment already exists with the generated id. Please try again. (code: `comments.id_already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createCommentUrl(params: CreateCommentUrlParams): string {
  const path = encode`/${params.regionId}/comments/${params.resourceType}/${params.resourceId}`
  return `${getBasePath()}${path}`
}

export interface CreateCommentUrlParams {
  resourceType: string
  resourceId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/comments/{resource_type}/{resource_id}/{comment_id}
 * Operation: `get-comment`
 * Retrieves a Comment.
 * @param {string} params.resourceType The kind of Resource that a Comment belongs to. Should be one of [elasticsearch, kibana, apm, appsearch, enterprise_search, integrations_server, allocator, constructor, runner, proxy].
 * @param {string} params.resourceId Id of the Resource that a Comment belongs to.
 * @param {string} params.commentId Id of a Comment.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Comment. See {@link Comment}
 * @see 404 The Comment you want does not exist. (code: `comments.comment_does_not_exist`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCommentUrl(params: GetCommentUrlParams): string {
  const path = encode`/${params.regionId}/comments/${params.resourceType}/${params.resourceId}/${params.commentId}`
  return `${getBasePath()}${path}`
}

export interface GetCommentUrlParams {
  resourceType: string
  resourceId: string
  commentId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/comments/{resource_type}/{resource_id}/{comment_id}
 * Operation: `update-comment`
 * Updates a Comment.
 * @param {string} params.resourceType The kind of Resource that a Comment belongs to. Should be one of [elasticsearch, kibana, apm, appsearch, enterprise_search, integrations_server, allocator, constructor, runner, proxy].
 * @param {string} params.resourceId Id of the Resource that a Comment belongs to.
 * @param {string} params.commentId Id of a Comment.
 * @param {string} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link CommentUpdateRequest}
 * @see 200 Comment updated successfully. See {@link Comment}
 * @see 401 * The Comment does not belong to you. (code: `comments.unauthorised`)
 * Your current session does not have a user id associated with it. (code: `comments.no_user_id`). See {@link BasicFailedReply}
 * @see 404 The Comment you want does not exist. (code: `comments.comment_does_not_exist`). See {@link BasicFailedReply}
 * @see 409 The version you sent does not match the persisted version. (code: `comments.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateCommentUrl(params: UpdateCommentUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/comments/${params.resourceType}/${params.resourceId}/${params.commentId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface UpdateCommentUrlParams {
  resourceType: string
  resourceId: string
  commentId: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/comments/{resource_type}/{resource_id}/{comment_id}
 * Operation: `delete-comment`
 * Deletes a Comment.
 * @param {string} params.resourceType The kind of Resource that a Comment belongs to. Should be one of [elasticsearch, kibana, apm, appsearch, enterprise_search, integrations_server, allocator, constructor, runner, proxy].
 * @param {string} params.resourceId Id of the Resource that a Comment belongs to.
 * @param {string} params.commentId Id of a Comment.
 * @param {string} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Comment deleted successfully.
 * @see 401 * The Comment does not belong to you. (code: `comments.unauthorised`)
 * Your current session does not have a user id associated with it. (code: `comments.no_user_id`). See {@link BasicFailedReply}
 * @see 404 The Comment you want does not exist. (code: `comments.comment_does_not_exist`). See {@link BasicFailedReply}
 * @see 409 The version you sent does not match the persisted version. (code: `comments.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteCommentUrl(params: DeleteCommentUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/comments/${params.resourceType}/${params.resourceId}/${params.commentId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteCommentUrlParams {
  resourceType: string
  resourceId: string
  commentId: string
  version?: string | null
  regionId: string
}

/**
 * GET /api/v1/deployments
 * Operation: `list-deployments`
 * List Deployments.
 * @param {string} params.deploymentIds Optional comma separated list of deployment IDs to return in the list response.
 * @see 200 The list of deployments that belong to the authenticated user. See {@link DeploymentsListResponse}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listDeploymentsUrl(params: ListDeploymentsUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    deployment_ids: params.deploymentIds,
  })
  const path = `/deployments`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface ListDeploymentsUrlParams {
  deploymentIds?: string | null
}

/**
 * POST /api/v1/deployments
 * Operation: `create-deployment`
 * Creates a Deployment.
 * @param {string} params.requestId An optional idempotency token - if two create requests share the same request_id token (min size 32 characters, max 128) then only one deployment will be created, the second request will return the info of that deployment (in the same format described below, but with blanks for auth-related fields).
 * @param {boolean} params.validateOnly If true, will just validate the Deployment definition but will not perform the creation.
 * @param {string} params.templateId An optional template id - if present, the referenced template will be used to fill in the resources field of the deployment creation request. If any resources are present in the request together with the template, the ones coming in the request will prevail and no merging with the template will be performed.
 * @see Accepts a request payload of type {@link DeploymentCreateRequest}
 * @see 200 The request was valid (used when validate_only is true). See {@link DeploymentCreateResponse}
 * @see 201 The request was valid and a new deployment was created. See {@link DeploymentCreateResponse}
 * @see 202 The request was valid and deployment creation had already been started. See {@link DeploymentCreateResponse}
 * @see 400 The deployment request had errors. See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createDeploymentUrl(params: CreateDeploymentUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    request_id: params.requestId,
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
    template_id: params.templateId,
  })
  const path = `/deployments`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface CreateDeploymentUrlParams {
  requestId?: string | null
  validateOnly?: boolean | null
  templateId?: string | null
}

/**
 * POST /api/v1/regions/{region_id}/deployments/_resync
 * Operation: `resync-deployments`
 * Resynchronizes the search index for all the deployments.
 * @param {boolean} params.skipMatchingVersion When true, skips the document indexing when the version matches the in-memory copy. Default: true
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The deployments resync operation executed successfully. See {@link IndexSynchronizationResults}
 * @see 500 The deployments resync operation failed. (code: `deployments.resync_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resyncDeploymentsUrl(params: ResyncDeploymentsUrlParams): string {
  const queryString = buildQueryString({
    skip_matching_version:
      params.skipMatchingVersion === undefined ? true : params.skipMatchingVersion,
  })
  const path = encode`/${params.regionId}/deployments/_resync`
  return `${getBasePath()}${path}${queryString}`
}

export interface ResyncDeploymentsUrlParams {
  skipMatchingVersion?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/deployments/_search
 * Operation: `search-deployments`
 * Retrieves the information for all of the deployments that match the specified query.
 * @param {string} params.minimalMetadata Comma separated list of attributes to include in response for deployments found. Useful for reducing response size when retrieving many deployments. Use of this parameter moves the result to the minimal_metadata section of the response.
 * @see Accepts an optional request payload of type {@link SearchRequest}
 * @see 200 The list of deployments that match the specified query and belong to the authenticated user. See {@link DeploymentsSearchResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function searchDeploymentsUrl(params: SearchDeploymentsUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    minimal_metadata: params.minimalMetadata,
  })
  const path = `/deployments/_search`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SearchDeploymentsUrlParams {
  minimalMetadata?: string | null
}

/**
 * GET /api/v1/deployments/byok-configuration-parameters
 * Operation: `get-byok-configuration-parameters`
 * Get BYOK configuration parameters. Currently unavailable in self-hosted ECE.
 * @param {string} params.region Region of the BYOK configuration parameters.
 * @see 200 The BYOK configuration parameters for the specified region. See {@link ByokConfigurationParameters}
 * @see 400 The requested region is not supported. (code: `byok_configuration.region_not_found`). See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getByokConfigurationParametersUrl(
  params: GetByokConfigurationParametersUrlParams,
): string {
  const queryString = buildQueryString({
    region: params.region,
  })
  const path = `/deployments/byok-configuration-parameters`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetByokConfigurationParametersUrlParams {
  region: string
}

/**
 * POST /api/v1/deployments/eligible-remote-clusters
 * Operation: `search-eligible-remote-clusters`
 * Returns the list of deployments which contain eligible remote clusters for a specific version.
 * @param {string} params.version The version of the Elasticsearch cluster cluster that will potentially be configured to have remote clusters.
 * @see Accepts an optional request payload of type {@link SearchRequest}
 * @see 200 List of deployments which contains eligible remote clusters for a specific version. See {@link DeploymentsSearchResponse}
 * @see 400 The provided version is invalid. (code: `stackpack.invalid_version`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function searchEligibleRemoteClustersUrl(
  params: SearchEligibleRemoteClustersUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = `/deployments/eligible-remote-clusters`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SearchEligibleRemoteClustersUrlParams {
  version: string
}

/**
 * GET /api/v1/deployments/extensions
 * Operation: `list-extensions`
 * Retrieves all of the available extensions.
 * @param {string} params.organizationId When specified, filters extensions returned to those owned by the given organization.
 * @see 200 The extensions that are available. See {@link Extensions}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listExtensionsUrl(params: ListExtensionsUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    organization_id: params.organizationId,
  })
  const path = `/deployments/extensions`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface ListExtensionsUrlParams {
  organizationId?: string | null
}

/**
 * POST /api/v1/deployments/extensions
 * Operation: `create-extension`
 * Creates the extension.  The extensions API supports two types of usage patterns.  A). Specify a `download_url`, http or https URL,  where the extension is currently hosted. This will result in extension being copied to elastic repository.  B). Create only the extension metadata using the `POST` endpoint and then use `PUT` to upload the extension file. Leave the `download_url` unspecified in this case. .
 * @see Accepts a request payload of type {@link CreateExtensionRequest}
 * @see 201 The extension that was just created. See {@link Extension}
 * @see 400 Could not download the extension from the specified URL. (code: `extensions.request_execution_failed`). See {@link BasicFailedReply}
 * @see 404 Your current session does not have a user id associated with it. (code: `extensions.no_user_id`). See {@link BasicFailedReply}
 * @see 409 An extension already exists with the generated id. Please try again. (code: `extensions.id_already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createExtensionUrl(): string {
  const path = `/deployments/extensions`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/deployments/extensions/{extension_id}
 * Operation: `get-extension`
 * Retrieves an extension.
 * @param {string} params.extensionId Id of an extension.
 * @param {boolean} params.includeDeployments Include deployments referencing this extension. Up to only 10000 deployments will be included.
 * @see 200 The Extension. See {@link Extension}
 * @see 404 The extension requested does not exist. (code: `extensions.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getExtensionUrl(params: GetExtensionUrlParams): string {
  const queryString = buildQueryString({
    include_deployments:
      params.includeDeployments === undefined ? false : params.includeDeployments,
  })
  const path = encode`/deployments/extensions/${params.extensionId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetExtensionUrlParams {
  extensionId: string
  includeDeployments?: boolean | null
}

/**
 * POST /api/v1/deployments/extensions/{extension_id}
 * Operation: `update-extension`
 * Updates an extension.
 * @param {string} params.extensionId Id of an extension.
 * @see Accepts a request payload of type {@link UpdateExtensionRequest}
 * @see 200 Extension updated successfully. See {@link Extension}
 * @see 400 Could not download the extension from the specified URL. (code: `extensions.request_execution_failed`). See {@link BasicFailedReply}
 * @see 401 * The extension does not belong to you. (code: `extensions.unauthorised`)
 * Your current session does not have a user id associated with it. (code: `extensions.no_user_id`). See {@link BasicFailedReply}
 * @see 404 The extension you want does not exist. (code: `extensions.not_found`). See {@link BasicFailedReply}
 * @see 409 The version you sent does not match the persisted version. (code: `extensions.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateExtensionUrl(params: UpdateExtensionUrlParams): string {
  const path = encode`/deployments/extensions/${params.extensionId}`
  return `${getRawBasePath()}${path}`
}

export interface UpdateExtensionUrlParams {
  extensionId: string
}

/**
 * PUT /api/v1/deployments/extensions/{extension_id}
 * Operation: `upload-extension`
 * Uploads archive for an extension.
 * @param {string} params.extensionId Id of an extension.
 * @see Accepts a request payload of type {@link File}
 * @see 200 Archive uploaded successfully. See {@link Extension}
 * @see 400 Could not accept the extensions file. (code: `extensions.request_execution_failed`). See {@link BasicFailedReply}
 * @see 401 * The extension does not belong to you. (code: `extensions.unauthorised`)
 * Your current session does not have a user id associated with it. (code: `extensions.no_user_id`). See {@link BasicFailedReply}
 * @see 404 The extension you want does not exist. (code: `extensions.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function uploadExtensionUrl(params: UploadExtensionUrlParams): string {
  const path = encode`/deployments/extensions/${params.extensionId}`
  return `${getRawBasePath()}${path}`
}

export interface UploadExtensionUrlParams {
  extensionId: string
}

/**
 * DELETE /api/v1/deployments/extensions/{extension_id}
 * Operation: `delete-extension`
 * Deletes a Extension.
 * @param {string} params.extensionId Id of an extension.
 * @see 200 Extension deleted successfully.
 * @see 401 * The extension does not belong to you. (code: `extensions.unauthorised`)
 * Your current session does not have a user id associated with it. (code: `extensions.no_user_id`). See {@link BasicFailedReply}
 * @see 404 The extension requested does not exist. (code: `extensions.not_found`). See {@link BasicFailedReply}
 * @see 409 The version you sent does not match the persisted version. (code: `extensions.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteExtensionUrl(params: DeleteExtensionUrlParams): string {
  const path = encode`/deployments/extensions/${params.extensionId}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteExtensionUrlParams {
  extensionId: string
}

/**
 * GET /api/v1/deployments/templates
 * Operation: `get-deployment-templates-v2`
 * Retrieves all deployment templates.
 * @param {string} params.metadata An optional key/value pair in the form of (key:value) that will act as a filter and exclude any templates that do not have a matching metadata item associated.
 * @param {boolean} params.showInstanceConfigurations If true, will return details for each instance configuration referenced by the template. Default: true
 * @param {boolean} params.showMaxZones If true, will populate the max_zones field in the instance configurations. Only relevant if show_instance_configurations=true.
 * @param {string} params.stackVersion If present, it will cause the returned deployment templates to be adapted to return only the elements allowed in that version.
 * @param {boolean} params.hideDeprecated If true, templates flagged as deprecated will NOT be returned.
 * @param {boolean} params.showHidden If true, templates flagged as hidden will be returned.
 * @param {string} params.region Region of the deployment templates.
 * @see 200 The deployment templates were returned successfully. See {@link Array<DeploymentTemplateInfoV2>}
 * @see 400 The requested region was not found. (code: `templates.region_not_found`). See {@link BasicFailedReply}
 * @see 401 The user is not authorized to access requested region. (code: `templates.region_not_allowed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentTemplatesV2Url(params: GetDeploymentTemplatesV2UrlParams): string {
  const queryString = buildQueryString({
    metadata: params.metadata,
    show_instance_configurations:
      params.showInstanceConfigurations === undefined ? true : params.showInstanceConfigurations,
    show_max_zones: params.showMaxZones === undefined ? false : params.showMaxZones,
    stack_version: params.stackVersion,
    hide_deprecated: params.hideDeprecated === undefined ? false : params.hideDeprecated,
    show_hidden: params.showHidden === undefined ? false : params.showHidden,
    region: params.region,
  })
  const path = `/deployments/templates`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetDeploymentTemplatesV2UrlParams {
  metadata?: string | null
  showInstanceConfigurations?: boolean | null
  showMaxZones?: boolean | null
  stackVersion?: string | null
  hideDeprecated?: boolean | null
  showHidden?: boolean | null
  region: string
}

/**
 * POST /api/v1/deployments/templates
 * Operation: `create-deployment-template-v2`
 * Creates a deployment template.
 * @param {boolean} params.validateOnly If true, the deployment template definition will be validated but the template will not be created.
 * @param {string} params.region Region of the deployment template.
 * @see Accepts a request payload of type {@link DeploymentTemplateRequestBody}
 * @see 200 The request was valid (used when validate_only is true). See {@link IdResponse}
 * @see 201 The deployment definition was valid and the template has been created. See {@link IdResponse}
 * @see 400 The requested region is not supported. (code: `templates.region_not_found`). See {@link BasicFailedReply}
 * @see 401 The user is not authorized to access requested region. (code: `templates.region_not_allowed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createDeploymentTemplateV2Url(params: CreateDeploymentTemplateV2UrlParams): string {
  const queryString = buildQueryString({
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
    region: params.region,
  })
  const path = `/deployments/templates`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface CreateDeploymentTemplateV2UrlParams {
  validateOnly?: boolean | null
  region: string
}

/**
 * GET /api/v1/deployments/templates/{template_id}
 * Operation: `get-deployment-template-v2`
 * Retrieves a deployment template by id.
 * @param {string} params.templateId The identifier for the deployment template.
 * @param {boolean} params.showInstanceConfigurations If true, will return details for each instance configuration referenced by the template. Default: true
 * @param {boolean} params.showMaxZones If true, will populate the max_zones field in the instance configurations. Only relevant if show_instance_configurations=true.
 * @param {string} params.stackVersion If present, it will cause the returned deployment template to be adapted to return only the elements allowed in that version.
 * @param {string} params.region Region of the deployment template.
 * @see 200 The deployment template was found and returned successfully. See {@link DeploymentTemplateInfoV2}
 * @see 400 The requested region is not supported. (code: `templates.region_not_found`). See {@link BasicFailedReply}
 * @see 401 The user is not authorized to access requested region. (code: `templates.region_not_allowed`). See {@link BasicFailedReply}
 * @see 404 The deployment template specified by {template_id} cannot be found. (code: `templates.template_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentTemplateV2Url(params: GetDeploymentTemplateV2UrlParams): string {
  const queryString = buildQueryString({
    show_instance_configurations:
      params.showInstanceConfigurations === undefined ? true : params.showInstanceConfigurations,
    show_max_zones: params.showMaxZones === undefined ? false : params.showMaxZones,
    stack_version: params.stackVersion,
    region: params.region,
  })
  const path = encode`/deployments/templates/${params.templateId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetDeploymentTemplateV2UrlParams {
  templateId: string
  showInstanceConfigurations?: boolean | null
  showMaxZones?: boolean | null
  stackVersion?: string | null
  region: string
}

/**
 * PUT /api/v1/deployments/templates/{template_id}
 * Operation: `set-deployment-template-v2`
 * Creates or updates a deployment template.
 * @param {string} params.templateId The identifier for the deployment template.
 * @param {string} params.version If specified, checks for conflicts against the version of the template (returned in 'x-cloud-resource-version' of the GET request).
 * @param {boolean} params.createOnly If true, will fail if the deployment template already exists at the given id.
 * @param {boolean} params.validateOnly If true, the deployment template definition will be validated but the template will not be updated.
 * @param {string} params.region Region of the deployment template.
 * @see Accepts a request payload of type {@link DeploymentTemplateRequestBody}
 * @see 200 The deployment definition was valid and the template has been updated. See {@link IdResponse}
 * @see 201 The deployment definition was valid and the template was created. See {@link IdResponse}
 * @see 400 The requested region is not supported. (code: `templates.region_not_found`). See {@link BasicFailedReply}
 * @see 401 The user is not authorized to access requested region. (code: `templates.region_not_allowed`). See {@link BasicFailedReply}
 * @see 404 The deployment template specified by {template_id} cannot be found. (code: `templates.template_not_found`). See {@link BasicFailedReply}
 * @see 409 The version supplied in the request conflicted with the version found on the server. (code: `templates.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setDeploymentTemplateV2Url(params: SetDeploymentTemplateV2UrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
    create_only: params.createOnly === undefined ? false : params.createOnly,
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
    region: params.region,
  })
  const path = encode`/deployments/templates/${params.templateId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SetDeploymentTemplateV2UrlParams {
  templateId: string
  version?: string | null
  createOnly?: boolean | null
  validateOnly?: boolean | null
  region: string
}

/**
 * DELETE /api/v1/deployments/templates/{template_id}
 * Operation: `delete-deployment-template-v2`
 * Deletes a deployment template by id.
 * @param {string} params.templateId The identifier for the deployment template.
 * @param {string} params.region Region of the deployment template.
 * @see 200 The deployment template was successfully deleted.
 * @see 400 The requested region is not supported. (code: `templates.region_not_found`). See {@link BasicFailedReply}
 * @see 401 The user is not authorized to access requested region. (code: `templates.region_not_allowed`). See {@link BasicFailedReply}
 * @see 404 The deployment template specified by {template_id} cannot be found. (code: `templates.template_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteDeploymentTemplateV2Url(params: DeleteDeploymentTemplateV2UrlParams): string {
  const queryString = buildQueryString({
    region: params.region,
  })
  const path = encode`/deployments/templates/${params.templateId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface DeleteDeploymentTemplateV2UrlParams {
  templateId: string
  region: string
}

/**
 * GET /api/v1/deployments/traffic-filter/associations/{association_type}/{associated_entity_id}/rulesets
 * Operation: `get-traffic-filter-deployment-ruleset-associations`
 * Retrieves the rulesets associated with a deployment.
 * @param {string} params.associationType Association type.
 * @param {string} params.associatedEntityId Associated entity ID.
 * @see 200 Rulesets in the deployment were successfully returned. See {@link TrafficFilterSettings}
 * @see 400 Invalid association type. (code: `traffic_filter.invalid_association_type`). See {@link BasicFailedReply}
 * @see 500 Error fetching traffic filter rulesets. (code: `traffic_filter.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTrafficFilterDeploymentRulesetAssociationsUrl(
  params: GetTrafficFilterDeploymentRulesetAssociationsUrlParams,
): string {
  const path = encode`/deployments/traffic-filter/associations/${params.associationType}/${params.associatedEntityId}/rulesets`
  return `${getRawBasePath()}${path}`
}

export interface GetTrafficFilterDeploymentRulesetAssociationsUrlParams {
  associationType: string
  associatedEntityId: string
}

/**
 * GET /api/v1/regions/{region_id}/deployments/traffic-filter/link-ids
 * Operation: `get-traffic-filter-claimed-link-ids`
 * List all of the traffic filter claimed link id.
 * @param {string} params.region If provided limits the claimed id to that region only.
 * @param {string} params.organizationId Retrieves a list of resources that are associated to the specified organization ID. It only takes effect if the user is an admin.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The collection of traffic filter claimed link id. See {@link TrafficFilterClaimedLinkIds}
 * @see 500 Error fetching traffic filter claimed link ids. (code: `traffic_filter_claimed_link_id.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTrafficFilterClaimedLinkIdsUrl(
  params: GetTrafficFilterClaimedLinkIdsUrlParams,
): string {
  const queryString = buildQueryString({
    region: params.region,
    organization_id: params.organizationId,
  })
  const path = encode`/${params.regionId}/deployments/traffic-filter/link-ids`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetTrafficFilterClaimedLinkIdsUrlParams {
  region?: string | null
  organizationId?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/deployments/traffic-filter/link-ids/_claim
 * Operation: `claim-traffic-filter-link-id`
 * Claim the ownership of a link id.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link TrafficFilterClaimedLinkIdRequest}
 * @see 201 The ruleset definition is valid and the creation has started. See {@link TrafficFilterClaimedLinkIdInfo}
 * @see 500 Error creating the traffic filter ruleset. (code: `traffic_filter_claimed_link_id.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function claimTrafficFilterLinkIdUrl(params: ClaimTrafficFilterLinkIdUrlParams): string {
  const path = encode`/${params.regionId}/deployments/traffic-filter/link-ids/_claim`
  return `${getBasePath()}${path}`
}

export interface ClaimTrafficFilterLinkIdUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/deployments/traffic-filter/link-ids/_unclaim
 * Operation: `unclaim-traffic-filter-link-id`
 * Unclaims the ownership of a link id.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link TrafficFilterClaimedLinkIdRequest}
 * @see 200 The claimed link id was successfully deleted.
 * @see 400 Error validating the request. (code: `traffic_filter_claimed_link_id.invalid_input`). See {@link BasicFailedReply}
 * @see 500 Error deleting the traffic filter claimed link id. (code: `traffic_filter_claimed_link_id.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function unclaimTrafficFilterLinkIdUrl(params: UnclaimTrafficFilterLinkIdUrlParams): string {
  const path = encode`/${params.regionId}/deployments/traffic-filter/link-ids/_unclaim`
  return `${getBasePath()}${path}`
}

export interface UnclaimTrafficFilterLinkIdUrlParams {
  regionId: string
}

/**
 * GET /api/v1/deployments/traffic-filter/rulesets
 * Operation: `get-traffic-filter-rulesets`
 * List all of the traffic filter rulesets.
 * @param {boolean} params.includeAssociations Retrieves a list of resources that are associated to the specified ruleset.
 * @param {string} params.region If provided limits the rulesets to that region only.
 * @param {string} params.organizationId Retrieves a list of resources that are associated to the specified organization ID. It only takes effect if the user is an admin.
 * @see 200 The collection of traffic filter routes. See {@link TrafficFilterRulesets}
 * @see 500 Error fetching traffic filter rulesets. (code: `traffic_filter.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTrafficFilterRulesetsUrl(
  params: GetTrafficFilterRulesetsUrlParams = emptyParams,
): string {
  const queryString = buildQueryString({
    include_associations:
      params.includeAssociations === undefined ? false : params.includeAssociations,
    region: params.region,
    organization_id: params.organizationId,
  })
  const path = `/deployments/traffic-filter/rulesets`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetTrafficFilterRulesetsUrlParams {
  includeAssociations?: boolean | null
  region?: string | null
  organizationId?: string | null
}

/**
 * POST /api/v1/deployments/traffic-filter/rulesets
 * Operation: `create-traffic-filter-ruleset`
 * Creates a ruleset that consists of a set of rules.
 * @see Accepts a request payload of type {@link TrafficFilterRulesetRequest}
 * @see 201 The ruleset definition is valid and the creation has started. See {@link TrafficFilterRulesetResponse}
 * @see 500 Error creating the traffic filter ruleset. (code: `traffic_filter.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createTrafficFilterRulesetUrl(): string {
  const path = `/deployments/traffic-filter/rulesets`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/deployments/traffic-filter/rulesets/{ruleset_id}
 * Operation: `get-traffic-filter-ruleset`
 * Retrieves a list of resources that are associated to the specified ruleset.
 * @param {string} params.rulesetId The mandatory ruleset ID.
 * @param {boolean} params.includeAssociations Retrieves a list of resources that are associated to the specified ruleset.
 * @see 200 The container for a set of traffic filter rules. See {@link TrafficFilterRulesetInfo}
 * @see 404 The traffic filter ruleset specified by {ruleset_id} cannot be found. (code: `traffic_filter.not_found`). See {@link BasicFailedReply}
 * @see 500 Error reading the traffic filter ruleset. (code: `traffic_filter.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTrafficFilterRulesetUrl(params: GetTrafficFilterRulesetUrlParams): string {
  const queryString = buildQueryString({
    include_associations:
      params.includeAssociations === undefined ? false : params.includeAssociations,
  })
  const path = encode`/deployments/traffic-filter/rulesets/${params.rulesetId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetTrafficFilterRulesetUrlParams {
  rulesetId: string
  includeAssociations?: boolean | null
}

/**
 * PUT /api/v1/deployments/traffic-filter/rulesets/{ruleset_id}
 * Operation: `update-traffic-filter-ruleset`
 * Updates the ruleset with the definition.
 * @param {string} params.rulesetId The mandatory ruleset ID.
 * @see Accepts a request payload of type {@link TrafficFilterRulesetRequest}
 * @see 200 The ruleset definition was valid and the update has started. See {@link TrafficFilterRulesetResponse}
 * @see 404 The traffic filter ruleset specified by {ruleset_id} cannot be found. (code: `traffic_filter.not_found`). See {@link BasicFailedReply}
 * @see 500 Error reading the traffic filter ruleset. (code: `traffic_filter.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateTrafficFilterRulesetUrl(params: UpdateTrafficFilterRulesetUrlParams): string {
  const path = encode`/deployments/traffic-filter/rulesets/${params.rulesetId}`
  return `${getRawBasePath()}${path}`
}

export interface UpdateTrafficFilterRulesetUrlParams {
  rulesetId: string
}

/**
 * DELETE /api/v1/deployments/traffic-filter/rulesets/{ruleset_id}
 * Operation: `delete-traffic-filter-ruleset`
 * Deletes the ruleset by ID.
 * @param {string} params.rulesetId The mandatory ruleset ID.
 * @param {boolean} params.ignoreAssociations When true, ignores the associations and deletes the ruleset. When false, recognizes the associations, which prevents the deletion of the rule set.
 * @see 200 The traffic filter ruleset was successfully deleted.
 * @see 404 The traffic filter ruleset specified by {ruleset_id} cannot be found. (code: `traffic_filter.not_found`). See {@link BasicFailedReply}
 * @see 500 Error deleting the traffic filter ruleset. (code: `traffic_filter.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteTrafficFilterRulesetUrl(params: DeleteTrafficFilterRulesetUrlParams): string {
  const queryString = buildQueryString({
    ignore_associations:
      params.ignoreAssociations === undefined ? false : params.ignoreAssociations,
  })
  const path = encode`/deployments/traffic-filter/rulesets/${params.rulesetId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface DeleteTrafficFilterRulesetUrlParams {
  rulesetId: string
  ignoreAssociations?: boolean | null
}

/**
 * GET /api/v1/deployments/traffic-filter/rulesets/{ruleset_id}/associations
 * Operation: `get-traffic-filter-ruleset-deployment-associations`
 * Retrieves a list of deployments that are associated to the specified ruleset.
 * @param {string} params.rulesetId The mandatory ruleset ID.
 * @see 200 Associations referred by traffic filter rulesets were successfully returned. See {@link RulesetAssociations}
 * @see 500 Error fetching deployments. (code: `traffic_filter.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTrafficFilterRulesetDeploymentAssociationsUrl(
  params: GetTrafficFilterRulesetDeploymentAssociationsUrlParams,
): string {
  const path = encode`/deployments/traffic-filter/rulesets/${params.rulesetId}/associations`
  return `${getRawBasePath()}${path}`
}

export interface GetTrafficFilterRulesetDeploymentAssociationsUrlParams {
  rulesetId: string
}

/**
 * POST /api/v1/deployments/traffic-filter/rulesets/{ruleset_id}/associations
 * Operation: `create-traffic-filter-ruleset-association`
 * Applies the ruleset to the specified deployment.
 * @param {string} params.rulesetId The mandatory ruleset ID.
 * @see Accepts a request payload of type {@link FilterAssociation}
 * @see 200 Create association request was valid and the association already exists
 * @see 201 Create association request was valid and the association has been created
 * @see 404 The traffic filter ruleset specified by {ruleset_id} cannot be found. (code: `traffic_filter.not_found`). See {@link BasicFailedReply}
 * @see 500 Creation failed. (code: `traffic_filter.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createTrafficFilterRulesetAssociationUrl(
  params: CreateTrafficFilterRulesetAssociationUrlParams,
): string {
  const path = encode`/deployments/traffic-filter/rulesets/${params.rulesetId}/associations`
  return `${getRawBasePath()}${path}`
}

export interface CreateTrafficFilterRulesetAssociationUrlParams {
  rulesetId: string
}

/**
 * DELETE /api/v1/deployments/traffic-filter/rulesets/{ruleset_id}/associations/{association_type}/{associated_entity_id}
 * Operation: `delete-traffic-filter-ruleset-association`
 * Deletes the traffic rules in the ruleset from the deployment.
 * @param {string} params.rulesetId The mandatory ruleset ID.
 * @param {string} params.associationType Association type.
 * @param {string} params.associatedEntityId Associated entity ID.
 * @see 200 Delete association request was valid and the association has been deleted
 * @see 404 The traffic filter ruleset association cannot be found. (code: `traffic_filter.not_found`). See {@link BasicFailedReply}
 * @see 500 Deletion failed. (code: `traffic_filter.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteTrafficFilterRulesetAssociationUrl(
  params: DeleteTrafficFilterRulesetAssociationUrlParams,
): string {
  const path = encode`/deployments/traffic-filter/rulesets/${params.rulesetId}/associations/${params.associationType}/${params.associatedEntityId}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteTrafficFilterRulesetAssociationUrlParams {
  rulesetId: string
  associationType: string
  associatedEntityId: string
}

/**
 * GET /api/v1/regions/{region_id}/deployments/traffic-filter/tokens/{deployment_id}/{resource_kind}/{ref_id}
 * Operation: `get-traffic-filter-token`
 * Creates a fresh bypass token for the specified deployment resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.duration For how long the token will remain valid. Minimum 1 minute and maximum 72 hours. Example: 5m, 3h or 2 days.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The token for the resource. See {@link TrafficFilterTokenResponse}
 * @see 400 * The given duration was longer than 72 hours. (code: `traffic_filter_token.duration_too_long`)
 * The given duration was shorter than 1 minute. (code: `traffic_filter_token.duration_too_short`). See {@link BasicFailedReply}
 * @see 403 Insufficient credentials. (code: `traffic_filter.invalid_principal`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTrafficFilterTokenUrl(params: GetTrafficFilterTokenUrlParams): string {
  const queryString = buildQueryString({
    duration: params.duration,
  })
  const path = encode`/${params.regionId}/deployments/traffic-filter/tokens/${params.deploymentId}/${params.resourceKind}/${params.refId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetTrafficFilterTokenUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  duration?: string | null
  regionId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}
 * Operation: `get-deployment`
 * Retrieves information about a Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {boolean} params.showSecurity Whether to include the Elasticsearch 2.x security information in the response - can be large per cluster and also include credentials.
 * @param {boolean} params.showMetadata Whether to include the full cluster metadata in the response - can be large per cluster and also include credentials.
 * @param {boolean} params.showPlans Whether to include the full current and pending plan information in the response - can be large per cluster. Default: true
 * @param {boolean} params.showPlanLogs Whether to include with the current and pending plan information the attempt log - can be very large per cluster.
 * @param {boolean} params.showPlanHistory Whether to include the plan history with the current and pending plan information. The results can be very large per cluster.
 By default, if a given resource kind (e.g. Elasticsearch, Kibana, etc.) has more than 100 plans
 (which should be very rare, most likely caused by a bug) only 100 plans are returned for the given resource type:
 The first 10 plans, and the last 90 plans for that resource type.
 If ALL of the plans are desired, pass the `force_all_plan_history` parameter with a value of `true`.
.
 * @param {boolean} params.showPlanDefaults If showing plans, whether to show values that are left at their default value (less readable but more informative).
 * @param {boolean} params.convertLegacyPlans If showing plans, whether to leave pre-2.0.0 plans in their legacy format (the default), or whether to update them to 2.0.x+ format (if 'true').
 * @param {integer} params.showSystemAlerts Number of system alerts (such as forced restarts due to memory limits) to be included in the response - can be large per cluster. Negative numbers or 0 will not return field.
 * @param {boolean} params.showSettings Whether to show cluster settings in the response.
 * @param {boolean} params.showInstanceMetrics Whether to show resources instance metrics in the response. Default: true
 * @param {boolean} params.showInstanceConfigurations If true, will return details for each instance configuration referenced by the deployment. Default: true
 * @param {boolean} params.enrichWithTemplate If showing plans, whether to enrich the plan by including the missing elements from the deployment template it is based on. Default: true
 * @param {boolean} params.forceAllPlanHistory Force show the entire plan history no matter how long.
 As noted in the `show_plan_history` parameter description, by default, a maximum of 100 plans are shown per resource. 
 If `true`, this parameter overrides the default, and ALL plans are returned.
 Use with care as the plan history can be VERY large. Consider pairing with `show_plan_logs=false`.
 .
 * @param {boolean} params.clearTransient If set (defaults to false) then removes the transient section from all child resources, making it safe to reapply via an update.
 * @see 200 The Deployment info response. See {@link DeploymentGetResponse}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentUrl(params: GetDeploymentUrlParams): string {
  const queryString = buildQueryString({
    show_security: params.showSecurity === undefined ? false : params.showSecurity,
    show_metadata: params.showMetadata === undefined ? false : params.showMetadata,
    show_plans: params.showPlans === undefined ? true : params.showPlans,
    show_plan_logs: params.showPlanLogs === undefined ? false : params.showPlanLogs,
    show_plan_history: params.showPlanHistory === undefined ? false : params.showPlanHistory,
    show_plan_defaults: params.showPlanDefaults === undefined ? false : params.showPlanDefaults,
    convert_legacy_plans:
      params.convertLegacyPlans === undefined ? false : params.convertLegacyPlans,
    show_system_alerts: params.showSystemAlerts === undefined ? 0 : params.showSystemAlerts,
    show_settings: params.showSettings === undefined ? false : params.showSettings,
    show_instance_metrics:
      params.showInstanceMetrics === undefined ? true : params.showInstanceMetrics,
    show_instance_configurations:
      params.showInstanceConfigurations === undefined ? true : params.showInstanceConfigurations,
    enrich_with_template:
      params.enrichWithTemplate === undefined ? true : params.enrichWithTemplate,
    force_all_plan_history:
      params.forceAllPlanHistory === undefined ? false : params.forceAllPlanHistory,
    clear_transient: params.clearTransient === undefined ? false : params.clearTransient,
  })
  const path = encode`/deployments/${params.deploymentId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetDeploymentUrlParams {
  deploymentId: string
  showSecurity?: boolean | null
  showMetadata?: boolean | null
  showPlans?: boolean | null
  showPlanLogs?: boolean | null
  showPlanHistory?: boolean | null
  showPlanDefaults?: boolean | null
  convertLegacyPlans?: boolean | null
  showSystemAlerts?: number | null
  showSettings?: boolean | null
  showInstanceMetrics?: boolean | null
  showInstanceConfigurations?: boolean | null
  enrichWithTemplate?: boolean | null
  forceAllPlanHistory?: boolean | null
  clearTransient?: boolean | null
}

/**
 * PUT /api/v1/deployments/{deployment_id}
 * Operation: `update-deployment`
 * Updates a Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {boolean} params.hidePrunedOrphans Whether or not to hide orphaned resources that were shut down (relevant if prune on the request is true).
 * @param {boolean} params.skipSnapshot Whether or not to skip snapshots before shutting down orphaned resources (relevant if prune on the request is true).
 * @param {boolean} params.validateOnly If true, will just validate the Deployment definition but will not perform the update.
 * @param {string} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @see Accepts a request payload of type {@link DeploymentUpdateRequest}
 * @see 200 The request was valid and the deployment was updated. See {@link DeploymentUpdateResponse}
 * @see 400 The deployment request had errors. See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateDeploymentUrl(params: UpdateDeploymentUrlParams): string {
  const queryString = buildQueryString({
    hide_pruned_orphans: params.hidePrunedOrphans === undefined ? false : params.hidePrunedOrphans,
    skip_snapshot: params.skipSnapshot === undefined ? false : params.skipSnapshot,
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
    version: params.version,
  })
  const path = encode`/deployments/${params.deploymentId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface UpdateDeploymentUrlParams {
  deploymentId: string
  hidePrunedOrphans?: boolean | null
  skipSnapshot?: boolean | null
  validateOnly?: boolean | null
  version?: string | null
}

/**
 * DELETE /api/v1/deployments/{deployment_id}
 * Operation: `delete-deployment`
 * Deletes a Deployment and all its resources.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @see 200 The request was valid and the deployment was deleted. See {@link DeploymentDeleteResponse}
 * @see 400 The Deployment resources have not been shutdown yet. See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteDeploymentUrl(params: DeleteDeploymentUrlParams): string {
  const path = encode`/deployments/${params.deploymentId}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteDeploymentUrlParams {
  deploymentId: string
}

/**
 * POST /api/v1/regions/{region_id}/deployments/{deployment_id}/_migrate
 * Operation: `migrate-deployment`
 * > WARNING > This endpoint is deprecated and scheduled to be removed in the next major version. Use [migrate deployment template](#migrate-deployment-template) instead.  Returns a deployment update request that would transform this deployment from its template to the provided one.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.template The ID of the deployment template to migrate to.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The request was valid and the deployment can be migrated to the template with the provided id. See {@link DeploymentTemplateMigrateResponse}
 * @see 400 The deployment cannot be successfully migrated to the template with the provided id. See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function migrateDeploymentUrl(params: MigrateDeploymentUrlParams): string {
  const queryString = buildQueryString({
    template: params.template,
  })
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/_migrate`
  return `${getBasePath()}${path}${queryString}`
}

export interface MigrateDeploymentUrlParams {
  deploymentId: string
  template: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/deployments/{deployment_id}/_notify-revoked-key
 * Operation: `notify-revoked-key`
 * Stops all the Running instances of all resources(elasticsearch, kibana and apm) of this deployment and locks the directory, if deployment is BYOK enabled.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 All running instances stopped and data locked. See {@link DeploymentResourceCommandResponse}
 * @see 403 The notifyRevokedKey command prohibited for the given delpoyment. (code: `deployments.instance_update_prohibited_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * One or more resource under deployment specified by {deployment_id} are missing. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of resource under deployment specified by {deployment_id} are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @see 500 Update of One or more resource belonging to deployment: deployment_id failed. (code: `deployments.deployment_update_failed_on_resource_update`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function notifyRevokedKeyUrl(params: NotifyRevokedKeyUrlParams): string {
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/_notify-revoked-key`
  return `${getBasePath()}${path}`
}

export interface NotifyRevokedKeyUrlParams {
  deploymentId: string
  regionId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/_restore
 * Operation: `restore-deployment`
 * Restores all resources in a Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {boolean} params.restoreSnapshot Whether or not to restore a snapshot for those resources that allow it.
 * @see 200 The request was valid and the resources of the deployment were restored. See {@link DeploymentRestoreResponse}
 * @see 400 There are Elasticsearch resources in the deployment which are not shut down. See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function restoreDeploymentUrl(params: RestoreDeploymentUrlParams): string {
  const queryString = buildQueryString({
    restore_snapshot: params.restoreSnapshot === undefined ? false : params.restoreSnapshot,
  })
  const path = encode`/deployments/${params.deploymentId}/_restore`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface RestoreDeploymentUrlParams {
  deploymentId: string
  restoreSnapshot?: boolean | null
}

/**
 * POST /api/v1/regions/{region_id}/deployments/{deployment_id}/_resync
 * Operation: `resync-deployment`
 * Immediately resynchronizes the search index for the selected deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The deployment resync operation executed successfully. See {@link IndexSynchronizationResults}
 * @see 500 The deployment resync operation failed for deployment {deployment_id}. (code: `deployments.resync_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resyncDeploymentUrl(params: ResyncDeploymentUrlParams): string {
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/_resync`
  return `${getBasePath()}${path}`
}

export interface ResyncDeploymentUrlParams {
  deploymentId: string
  regionId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/_shutdown
 * Operation: `shutdown-deployment`
 * Shuts down all resources in a Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {boolean} params.hide Whether or not to hide the deployment and its resources.Only applicable for Platform administrators.
 * @param {boolean} params.skipSnapshot Whether or not to skip snapshots before shutting down the resources.
 * @see 200 The request was valid and the resources of the deployment were shutdown. See {@link DeploymentShutdownResponse}
 * @see 400 Parameter is restricted and can only be set by a Platform administrator. (code: `deployments.restricted_parameter`). See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function shutdownDeploymentUrl(params: ShutdownDeploymentUrlParams): string {
  const queryString = buildQueryString({
    hide: params.hide,
    skip_snapshot: params.skipSnapshot === undefined ? false : params.skipSnapshot,
  })
  const path = encode`/deployments/${params.deploymentId}/_shutdown`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface ShutdownDeploymentUrlParams {
  deploymentId: string
  hide?: boolean | null
  skipSnapshot?: boolean | null
}

/**
 * GET /api/v1/regions/{region_id}/deployments/{deployment_id}/apm/{ref_id}
 * Operation: `get-deployment-apm-resource-info`
 * Get info about an APM Resource belonging to a given Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.showMetadata Whether to include the full cluster metadata in the response - can be large per cluster and also include credentials.
 * @param {boolean} params.showPlans Whether to include the full current and pending plan information in the response - can be large per cluster. Default: true
 * @param {boolean} params.showPlanLogs Whether to include with the current and pending plan information the attempt log - can be very large per cluster.
 * @param {boolean} params.showPlanHistory Whether to include with the current and pending plan information the plan history- can be very large per cluster.
 * @param {boolean} params.showPlanDefaults If showing plans, whether to show values that are left at their default value (less readable but more informative).
 * @param {boolean} params.showSettings Whether to show cluster settings in the response.
 * @param {boolean} params.clearTransient If set (defaults to false) then removes the transient section from all child resources, making it safe to reapply via an update.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Standard response. See {@link ApmResourceInfo}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentApmResourceInfoUrl(
  params: GetDeploymentApmResourceInfoUrlParams,
): string {
  const queryString = buildQueryString({
    show_metadata: params.showMetadata === undefined ? false : params.showMetadata,
    show_plans: params.showPlans === undefined ? true : params.showPlans,
    show_plan_logs: params.showPlanLogs === undefined ? false : params.showPlanLogs,
    show_plan_history: params.showPlanHistory === undefined ? false : params.showPlanHistory,
    show_plan_defaults: params.showPlanDefaults === undefined ? false : params.showPlanDefaults,
    show_settings: params.showSettings === undefined ? false : params.showSettings,
    clear_transient: params.clearTransient === undefined ? false : params.clearTransient,
  })
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/apm/${params.refId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetDeploymentApmResourceInfoUrlParams {
  deploymentId: string
  refId: string
  showMetadata?: boolean | null
  showPlans?: boolean | null
  showPlanLogs?: boolean | null
  showPlanHistory?: boolean | null
  showPlanDefaults?: boolean | null
  showSettings?: boolean | null
  clearTransient?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/apm/{ref_id}/_reset-token
 * Operation: `deployment-apm-reset-secret-token`
 * Reset the token of an APM resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 202 Response containing the new secret token, plan to apply it starts. See {@link ApmCrudResponse}
 * @see 400 Reset token is not supported when APM is managed by Elastic Agent. (code: `clusters.cluster_plan_state_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deploymentApmResetSecretTokenUrl(
  params: DeploymentApmResetSecretTokenUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/apm/${params.refId}/_reset-token`
  return `${getRawBasePath()}${path}`
}

export interface DeploymentApmResetSecretTokenUrlParams {
  deploymentId: string
  refId: string
}

/**
 * GET /api/v1/regions/{region_id}/deployments/{deployment_id}/appsearch/{ref_id}
 * Operation: `get-deployment-appsearch-resource-info`
 * Get info about an App Search Resource belonging to a given Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.showMetadata Whether to include the full cluster metadata in the response - can be large per cluster and also include credentials.
 * @param {boolean} params.showPlans Whether to include the full current and pending plan information in the response - can be large per cluster. Default: true
 * @param {boolean} params.showPlanLogs Whether to include with the current and pending plan information the attempt log - can be very large per cluster.
 * @param {boolean} params.showPlanHistory Whether to include with the current and pending plan information the plan history- can be very large per cluster.
 * @param {boolean} params.showPlanDefaults If showing plans, whether to show values that are left at their default value (less readable but more informative).
 * @param {boolean} params.showSettings Whether to show cluster settings in the response.
 * @param {boolean} params.clearTransient If set (defaults to false) then removes the transient section from all child resources, making it safe to reapply via an update.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Standard response. See {@link AppSearchResourceInfo}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentAppsearchResourceInfoUrl(
  params: GetDeploymentAppsearchResourceInfoUrlParams,
): string {
  const queryString = buildQueryString({
    show_metadata: params.showMetadata === undefined ? false : params.showMetadata,
    show_plans: params.showPlans === undefined ? true : params.showPlans,
    show_plan_logs: params.showPlanLogs === undefined ? false : params.showPlanLogs,
    show_plan_history: params.showPlanHistory === undefined ? false : params.showPlanHistory,
    show_plan_defaults: params.showPlanDefaults === undefined ? false : params.showPlanDefaults,
    show_settings: params.showSettings === undefined ? false : params.showSettings,
    clear_transient: params.clearTransient === undefined ? false : params.clearTransient,
  })
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/appsearch/${params.refId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetDeploymentAppsearchResourceInfoUrlParams {
  deploymentId: string
  refId: string
  showMetadata?: boolean | null
  showPlans?: boolean | null
  showPlanLogs?: boolean | null
  showPlanHistory?: boolean | null
  showPlanDefaults?: boolean | null
  showSettings?: boolean | null
  clearTransient?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/appsearch/{ref_id}/read_only_mode
 * Operation: `get-appsearch-read-only-mode`
 * Enable/Disable read-only mode on the given App Search resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 200 Standard response. See {@link ReadOnlyResponse}
 * @see 404 The Resource specified by ref_id cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAppsearchReadOnlyModeUrl(params: GetAppsearchReadOnlyModeUrlParams): string {
  const path = encode`/deployments/${params.deploymentId}/appsearch/${params.refId}/read_only_mode`
  return `${getRawBasePath()}${path}`
}

export interface GetAppsearchReadOnlyModeUrlParams {
  deploymentId: string
  refId: string
}

/**
 * PUT /api/v1/deployments/{deployment_id}/appsearch/{ref_id}/read_only_mode
 * Operation: `set-appsearch-read-only-mode`
 * Enable/Disable read-only mode on the given App Search resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see Accepts a request payload of type {@link ReadOnlyRequest}
 * @see 200 Standard response. See {@link ReadOnlyResponse}
 * @see 404 The Resource specified by ref_id cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setAppsearchReadOnlyModeUrl(params: SetAppsearchReadOnlyModeUrlParams): string {
  const path = encode`/deployments/${params.deploymentId}/appsearch/${params.refId}/read_only_mode`
  return `${getRawBasePath()}${path}`
}

export interface SetAppsearchReadOnlyModeUrlParams {
  deploymentId: string
  refId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/certificate-authority
 * Operation: `get-deployment-certificate-authority`
 * Returns the certificate authority used to sign the certificates of this deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @see 200 The certificate authority used to sign the certificates of this deployment. See {@link CertificateAuthority}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Elasticsearch Resource cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentCertificateAuthorityUrl(
  params: GetDeploymentCertificateAuthorityUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/certificate-authority`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentCertificateAuthorityUrlParams {
  deploymentId: string
}

/**
 * GET /api/v1/regions/{region_id}/deployments/{deployment_id}/elasticsearch/{ref_id}
 * Operation: `get-deployment-es-resource-info`
 * Get info about an Elasticsearch Resource belonging to a given Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.showSecurity Whether to include the Elasticsearch 2.x security information in the response - can be large per cluster and also include credentials.
 * @param {boolean} params.showMetadata Whether to include the full cluster metadata in the response - can be large per cluster and also include credentials.
 * @param {boolean} params.showPlans Whether to include the full current and pending plan information in the response - can be large per cluster. Default: true
 * @param {boolean} params.showPlanLogs Whether to include with the current and pending plan information the attempt log - can be very large per cluster.
 * @param {boolean} params.showPlanHistory Whether to include with the current and pending plan information the plan history- can be very large per cluster.
 * @param {boolean} params.showPlanDefaults If showing plans, whether to show values that are left at their default value (less readable but more informative).
 * @param {boolean} params.convertLegacyPlans If showing plans, whether to leave pre-2.0.0 plans in their legacy format (the default), or whether to update them to 2.0.x+ format (if 'true').
 * @param {integer} params.showSystemAlerts Number of system alerts (such as forced restarts due to memory limits) to be included in the response - can be large per cluster. Negative numbers or 0 will not return field.
 * @param {boolean} params.showSettings Whether to show cluster settings in the response.
 * @param {boolean} params.enrichWithTemplate If showing plans, whether to enrich the plan by including the missing elements from the deployment template it is based on. Default: true
 * @param {boolean} params.clearTransient If set (defaults to false) then removes the transient section from all child resources, making it safe to reapply via an update.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Standard response. See {@link ElasticsearchResourceInfo}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentEsResourceInfoUrl(
  params: GetDeploymentEsResourceInfoUrlParams,
): string {
  const queryString = buildQueryString({
    show_security: params.showSecurity === undefined ? false : params.showSecurity,
    show_metadata: params.showMetadata === undefined ? false : params.showMetadata,
    show_plans: params.showPlans === undefined ? true : params.showPlans,
    show_plan_logs: params.showPlanLogs === undefined ? false : params.showPlanLogs,
    show_plan_history: params.showPlanHistory === undefined ? false : params.showPlanHistory,
    show_plan_defaults: params.showPlanDefaults === undefined ? false : params.showPlanDefaults,
    convert_legacy_plans:
      params.convertLegacyPlans === undefined ? false : params.convertLegacyPlans,
    show_system_alerts: params.showSystemAlerts === undefined ? 0 : params.showSystemAlerts,
    show_settings: params.showSettings === undefined ? false : params.showSettings,
    enrich_with_template:
      params.enrichWithTemplate === undefined ? true : params.enrichWithTemplate,
    clear_transient: params.clearTransient === undefined ? false : params.clearTransient,
  })
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/elasticsearch/${params.refId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetDeploymentEsResourceInfoUrlParams {
  deploymentId: string
  refId: string
  showSecurity?: boolean | null
  showMetadata?: boolean | null
  showPlans?: boolean | null
  showPlanLogs?: boolean | null
  showPlanHistory?: boolean | null
  showPlanDefaults?: boolean | null
  convertLegacyPlans?: boolean | null
  showSystemAlerts?: number | null
  showSettings?: boolean | null
  enrichWithTemplate?: boolean | null
  clearTransient?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/_enable-ccr
 * Operation: `enable-deployment-resource-ccr`
 * Migrates the specified Elasticsearch resource and its associated Kibana to enable CCR (if supported).
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.validateOnly When `true`, will not enable CCR but returns warnings if any elements may lose availability during CCR enablement.
 * @see 200 Standard response. See {@link DeploymentResourceCommandResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function enableDeploymentResourceCcrUrl(
  params: EnableDeploymentResourceCcrUrlParams,
): string {
  const queryString = buildQueryString({
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
  })
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/_enable-ccr`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface EnableDeploymentResourceCcrUrlParams {
  deploymentId: string
  refId: string
  validateOnly?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/_enable-ilm
 * Operation: `enable-deployment-resource-ilm`
 * Migrates the specified Elasticsearch resource to use ILM.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.validateOnly When `true`, does not enable ILM but returns warnings if any applications may lose availability during ILM migration.
 * @see Accepts a request payload of type {@link EnableIlmRequest}
 * @see 200 Standard response. See {@link DeploymentResourceCommandResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function enableDeploymentResourceIlmUrl(
  params: EnableDeploymentResourceIlmUrlParams,
): string {
  const queryString = buildQueryString({
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
  })
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/_enable-ilm`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface EnableDeploymentResourceIlmUrlParams {
  deploymentId: string
  refId: string
  validateOnly?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/_enable-slm
 * Operation: `enable-deployment-resource-slm`
 * Migrates the specified Elasticsearch resource to use SLM.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.validateOnly When `true`, does not enable SLM but returns warnings if any applications may lose availability during SLM migration.
 * @see 200 Standard response. See {@link DeploymentResourceCommandResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function enableDeploymentResourceSlmUrl(
  params: EnableDeploymentResourceSlmUrlParams,
): string {
  const queryString = buildQueryString({
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
  })
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/_enable-slm`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface EnableDeploymentResourceSlmUrlParams {
  deploymentId: string
  refId: string
  validateOnly?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/_lock
 * Operation: `lock-deployment`
 * Users cannot initiate configuration changes when deployments are locked.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see Accepts a request payload of type {@link DeploymentLockRequest}
 * @see 200 Standard response. See {@link DeploymentResourceCommandResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function lockDeploymentUrl(params: LockDeploymentUrlParams): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/_lock`
  return `${getRawBasePath()}${path}`
}

export interface LockDeploymentUrlParams {
  deploymentId: string
  refId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/_reset-password
 * Operation: `reset-elasticsearch-user-password`
 * Resets the password of the 'elastic' user.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.checkCompletion If true, will not reset elastic user password and instead will return a status code signaling whether or not the current credentials are ready to use (eg from creation or the last call to _reset_password).
 * @see 200 The password reset was out carried successfully. See {@link ElasticsearchElasticUserPasswordResetResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 Failed to reset the 'elastic' user's password. (code: `deployments.elasticsearch.password_reset_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resetElasticsearchUserPasswordUrl(
  params: ResetElasticsearchUserPasswordUrlParams,
): string {
  const queryString = buildQueryString({
    check_completion: params.checkCompletion === undefined ? false : params.checkCompletion,
  })
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/_reset-password`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface ResetElasticsearchUserPasswordUrlParams {
  deploymentId: string
  refId: string
  checkCompletion?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/_restart
 * Operation: `restart-deployment-es-resource`
 * Restarts an Elasticsearch Resource. If a Resource is active: this command re-applies the existing plan but applies a "cluster_reboot", which issues a restart command and waits for it to complete. If a Resource is inactive: this command starts it up with the most recent successful plan.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.restoreSnapshot When set to true and restoring from shutdown, then will restore the cluster from the last snapshot (if available). Default: true
 * @param {boolean} params.skipSnapshot If true, will not take a snapshot of the cluster before restarting. Default: true
 * @param {boolean} params.cancelPending If true, cancels any pending plans before restarting. If false and there are pending plans, returns an error.
 * @param {string} params.groupAttribute Indicates the property or properties used to divide the list of instances to restart in groups. Valid options are: '__all__' (restart all at once), '__zone__' by logical zone, '__name__' one instance at a time, or a comma-separated list of attributes of the instances. Default: __zone__
 * @param {integer} params.shardInitWaitTime The time, in seconds, to wait for shards that show no progress of initializing, before rolling the next group (default: 10 minutes). Default: 600
 * @see 202 The restart command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 422 The command sent to a Resource found the Resource in an illegal state, the error message gives more details. (code: `deployments.deployment_resource_plan_change_error`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function restartDeploymentEsResourceUrl(
  params: RestartDeploymentEsResourceUrlParams,
): string {
  const queryString = buildQueryString({
    restore_snapshot: params.restoreSnapshot === undefined ? true : params.restoreSnapshot,
    skip_snapshot: params.skipSnapshot === undefined ? true : params.skipSnapshot,
    cancel_pending: params.cancelPending === undefined ? false : params.cancelPending,
    group_attribute: params.groupAttribute === undefined ? `__zone__` : params.groupAttribute,
    shard_init_wait_time: params.shardInitWaitTime === undefined ? 600 : params.shardInitWaitTime,
  })
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/_restart`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface RestartDeploymentEsResourceUrlParams {
  deploymentId: string
  refId: string
  restoreSnapshot?: boolean | null
  skipSnapshot?: boolean | null
  cancelPending?: boolean | null
  groupAttribute?: string | null
  shardInitWaitTime?: number | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/_shutdown
 * Operation: `shutdown-deployment-es-resource`
 * Shutdown Elasticsearch Resource belonging to a given Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.hide Hide cluster on shutdown. Hidden clusters are not listed by default. Only applicable for Platform administrators.
 * @param {boolean} params.skipSnapshot If true, will skip taking a snapshot of the cluster before shutting the cluster down (if even possible).
 * @see 200 Standard response. See {@link DeploymentResourceCommandResponse}
 * @see 400 Parameter is restricted and can only be set by a Platform administrator. (code: `deployments.restricted_parameter`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function shutdownDeploymentEsResourceUrl(
  params: ShutdownDeploymentEsResourceUrlParams,
): string {
  const queryString = buildQueryString({
    hide: params.hide,
    skip_snapshot: params.skipSnapshot === undefined ? false : params.skipSnapshot,
  })
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/_shutdown`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface ShutdownDeploymentEsResourceUrlParams {
  deploymentId: string
  refId: string
  hide?: boolean | null
  skipSnapshot?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/eligible-remote-clusters
 * Operation: `get-deployment-es-resource-eligible-remote-clusters`
 * Returns the list of deployments which contain eligible remote clusters for the elasticsearch resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see Accepts an optional request payload of type {@link SearchRequest}
 * @see 200 List of deployments which contains eligible remote clusters for the resource. See {@link DeploymentsSearchResponse}
 * @see 400 The resource specified doesn't yet have a valid version for the current plan or the pending plan. (code: `deployments.resource_does_not_have_a_valid_version`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentEsResourceEligibleRemoteClustersUrl(
  params: GetDeploymentEsResourceEligibleRemoteClustersUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/eligible-remote-clusters`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentEsResourceEligibleRemoteClustersUrlParams {
  deploymentId: string
  refId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/instances/{instance_ids}/_move
 * Operation: `move-deployment-elasticsearch-resource-instances`
 * Moves one or more instances belonging to the given Elasticsearch resource to a different allocator.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {array} params.instanceIds A comma-separated list of instance identifiers.
 * @param {boolean} params.ignoreMissing When `true` and the instance does not exist, proceeds to the next instance, or treats the instance as an error.
 * @param {boolean} params.forceUpdate When `true`, cancels and overwrites the pending plans, or treats the instance as an error.
 * @param {boolean} params.instancesDown When `true`, the instances specified by `instance_ids` are considered permanently shut down for data migration logic.
 * @param {boolean} params.moveOnly When `true`, moves the specified instances and ignores the changes for the cluster state. Default: true
 * @param {boolean} params.forceMove When `true`, moves instance data at file-system level (not via Elasticsearch), and re-creates instances on target allocator(s).
 * @param {boolean} params.validateOnly When `true`, validates the move request, then returns the calculated plan without applying the plan.
 * @see Accepts an optional request payload of type {@link TransientElasticsearchPlanConfiguration}
 * @see 200 If `validate_only` is `true`, the calculated plan is returned. See {@link TransientElasticsearchPlanConfiguration}
 * @see 202 The move command was issued successfully. Use the "GET" command on the /{deployment_id} resource to monitor progress
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Elasticsearch Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of the given resource type are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function moveDeploymentElasticsearchResourceInstancesUrl(
  params: MoveDeploymentElasticsearchResourceInstancesUrlParams,
): string {
  const queryString = buildQueryString({
    ignore_missing: params.ignoreMissing === undefined ? false : params.ignoreMissing,
    force_update: params.forceUpdate === undefined ? false : params.forceUpdate,
    instances_down: params.instancesDown === undefined ? false : params.instancesDown,
    move_only: params.moveOnly === undefined ? true : params.moveOnly,
    force_move: params.forceMove === undefined ? false : params.forceMove,
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
  })
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/instances/${params.instanceIds}/_move`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface MoveDeploymentElasticsearchResourceInstancesUrlParams {
  deploymentId: string
  refId: string
  instanceIds: string[]
  ignoreMissing?: boolean | null
  forceUpdate?: boolean | null
  instancesDown?: boolean | null
  moveOnly?: boolean | null
  forceMove?: boolean | null
  validateOnly?: boolean | null
}

/**
 * GET /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/keystore
 * Operation: `get-deployment-es-resource-keystore`
 * Fetches the current fields and metadata (but not the values) of the keystore for the Elasticsearch resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 200 The contents of the Elasticsearch keystore, with values redacted. See {@link KeystoreContents}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentEsResourceKeystoreUrl(
  params: GetDeploymentEsResourceKeystoreUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/keystore`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentEsResourceKeystoreUrlParams {
  deploymentId: string
  refId: string
}

/**
 * PATCH /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/keystore
 * Operation: `set-deployment-es-resource-keystore`
 * Adds the specified values to the Elasticsearch keystore, or removes the keys for the unspecified values.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see Accepts a request payload of type {@link KeystoreContents}
 * @see 200 The new contents of the Elasticsearch keystore. See {@link KeystoreContents}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setDeploymentEsResourceKeystoreUrl(
  params: SetDeploymentEsResourceKeystoreUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/keystore`
  return `${getRawBasePath()}${path}`
}

export interface SetDeploymentEsResourceKeystoreUrlParams {
  deploymentId: string
  refId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/logs/_download
 * Operation: `download-resource-logs`
 * Download logs for the given Elasticsearch resource. 24 hours of logs for the given Elasticsearch resource will be returned from the given start date.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.date The log retrieval start date in standard format. For example, `YYYY-MM-DD [THH [:mm] ]`.
 * @see 200 A zip archive of the given resources logs. See {@link string}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function downloadResourceLogsUrl(params: DownloadResourceLogsUrlParams): string {
  const queryString = buildQueryString({
    date: params.date,
  })
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/logs/_download`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface DownloadResourceLogsUrlParams {
  deploymentId: string
  refId: string
  date: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/logs/_search
 * Operation: `search-deployment-es-resource-logs`
 * Search across last 24h of Elasticsearch logs.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {integer} params.limit (Optional) maximum results in a page. Default: 20
 * @param {integer} params.offset (Optional) increment offset to scroll through pages of results.
 * @param {string} params.q (Optional) search query.
 * @param {string} params.filter (Optional) comma-separated list of filters for instance name and/or log level.
 * @see 200 Log lines from the last 24h of the Elasticsearch resource based on the search pattern. Maximum of 20 logs per page. See {@link ElasticsearchLogsSearchResponse}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @see 503 Logging cluster is unavailable. (code: `deployments.logging_cluster_data_fetch_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function searchDeploymentEsResourceLogsUrl(
  params: SearchDeploymentEsResourceLogsUrlParams,
): string {
  const queryString = buildQueryString({
    limit: params.limit === undefined ? 20 : params.limit,
    offset: params.offset === undefined ? 0 : params.offset,
    q: params.q,
    filter: params.filter,
  })
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/logs/_search`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SearchDeploymentEsResourceLogsUrlParams {
  deploymentId: string
  refId: string
  limit?: number | null
  offset?: number | null
  q?: string | null
  filter?: string | null
}

/**
 * GET /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/performance-metrics
 * Operation: `get-elasticsearch-performance-metrics`
 * Fetch the deployment's Elasticsearch performance metrics in the last 24h.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource.
 * @see 200 An aggregation of elasticsearch metrics. See {@link SimpleElasticsearchMetrics}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 503 Metric cluster is unavailable. (code: `logging_and_metrics.metrics_cluster_data_fetch_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getElasticsearchPerformanceMetricsUrl(
  params: GetElasticsearchPerformanceMetricsUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/performance-metrics`
  return `${getRawBasePath()}${path}`
}

export interface GetElasticsearchPerformanceMetricsUrlParams {
  deploymentId: string
  refId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/remote-clusters
 * Operation: `get-deployment-es-resource-remote-clusters`
 * Returns the list of certificate based remote clusters for the elasticsearch resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 200 List of certificate based remote clusters for the resource. See {@link RemoteResources}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentEsResourceRemoteClustersUrl(
  params: GetDeploymentEsResourceRemoteClustersUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/remote-clusters`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentEsResourceRemoteClustersUrlParams {
  deploymentId: string
  refId: string
}

/**
 * PUT /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/remote-clusters
 * Operation: `set-deployment-es-resource-remote-clusters`
 * Overwrites or creates the list of certificate based remote clusters for the elasticsearch resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see Accepts a request payload of type {@link RemoteResources}
 * @see 202 The Remote Clusters were updated
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setDeploymentEsResourceRemoteClustersUrl(
  params: SetDeploymentEsResourceRemoteClustersUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/remote-clusters`
  return `${getRawBasePath()}${path}`
}

export interface SetDeploymentEsResourceRemoteClustersUrlParams {
  deploymentId: string
  refId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/snapshot
 * Operation: `get-deployment-es-resource-snapshot`
 * Returns the current configuration of the ESS bucket management.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 200 Snapshot bucket configuration for this cluster. See {@link SnapshotConfiguration}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentEsResourceSnapshotUrl(
  params: GetDeploymentEsResourceSnapshotUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/snapshot`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentEsResourceSnapshotUrlParams {
  deploymentId: string
  refId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/snapshot/dependency
 * Operation: `get-deployment-es-resource-snapshot-dependency`
 * Fetches the current dependencies of the snapshot for the Elasticsearch resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 200 List of snapshot dependency. See {@link SnapshotDependencies}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentEsResourceSnapshotDependencyUrl(
  params: GetDeploymentEsResourceSnapshotDependencyUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/snapshot/dependency`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentEsResourceSnapshotDependencyUrlParams {
  deploymentId: string
  refId: string
}

/**
 * DELETE /api/v1/deployments/{deployment_id}/elasticsearch/{ref_id}/snapshot/dependency/{resource_id}
 * Operation: `delete-deployment-es-resource-snapshot-dependency`
 * Delete the dependency of a Elasticsearch resource snapshot. Doing so will cause the cloned snapshot to be inaccessible.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.resourceId System generated UUID for the Resource (Elasticsearch).
 * @see 200
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteDeploymentEsResourceSnapshotDependencyUrl(
  params: DeleteDeploymentEsResourceSnapshotDependencyUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/elasticsearch/${params.refId}/snapshot/dependency/${params.resourceId}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteDeploymentEsResourceSnapshotDependencyUrlParams {
  deploymentId: string
  refId: string
  resourceId: string
}

/**
 * GET /api/v1/regions/{region_id}/deployments/{deployment_id}/elasticsearch/{ref_id}/users
 * Operation: `get-deployment-es-resource-users`
 * Returns the list of users for the elasticsearch resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 List of users for the resource. See {@link DeploymentUsers}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentEsResourceUsersUrl(
  params: GetDeploymentEsResourceUsersUrlParams,
): string {
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/elasticsearch/${params.refId}/users`
  return `${getBasePath()}${path}`
}

export interface GetDeploymentEsResourceUsersUrlParams {
  deploymentId: string
  refId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/deployments/{deployment_id}/elasticsearch/{ref_id}/users/{user_name}
 * Operation: `get-deployment-es-resource-user`
 * Returns the requested user for the elasticsearch resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.userName Name of the user to create, delete or modify.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Requested user for the resource. See {@link DeploymentUser}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * The user specifiec by {user_name} cannot be found. (code: `deployments.deployment_user_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentEsResourceUserUrl(
  params: GetDeploymentEsResourceUserUrlParams,
): string {
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/elasticsearch/${params.refId}/users/${params.userName}`
  return `${getBasePath()}${path}`
}

export interface GetDeploymentEsResourceUserUrlParams {
  deploymentId: string
  refId: string
  userName: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/deployments/{deployment_id}/elasticsearch/{ref_id}/users/{user_name}
 * Operation: `add-deployment-es-resource-user`
 * Overwrites or creates a user with the given name and definition.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.userName Name of the user to create, delete or modify.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link DeploymentUser}
 * @see 200 The user added. See {@link DeploymentUser}
 * @see 400 The user name given in path doesn't match the one in the body. (code: `deployments.user_name_notmatching_request`). See {@link BasicFailedReply}
 * @see 403 One or more of the roles specified are not allowed. (code: `deployments.user_role_forbidden`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function addDeploymentEsResourceUserUrl(
  params: AddDeploymentEsResourceUserUrlParams,
): string {
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/elasticsearch/${params.refId}/users/${params.userName}`
  return `${getBasePath()}${path}`
}

export interface AddDeploymentEsResourceUserUrlParams {
  deploymentId: string
  refId: string
  userName: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/deployments/{deployment_id}/elasticsearch/{ref_id}/users/{user_name}
 * Operation: `delete-deployment-es-resource-user`
 * Deletes the user with the given name.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.userName Name of the user to create, delete or modify.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The user was deleted
 * @see 403 The user specified has one more roles this api user is not allowed to modify. (code: `deployments.user_role_forbidden`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * The user specifiec by {user_name} cannot be found. (code: `deployments.deployment_user_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteDeploymentEsResourceUserUrl(
  params: DeleteDeploymentEsResourceUserUrlParams,
): string {
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/elasticsearch/${params.refId}/users/${params.userName}`
  return `${getBasePath()}${path}`
}

export interface DeleteDeploymentEsResourceUserUrlParams {
  deploymentId: string
  refId: string
  userName: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/deployments/{deployment_id}/enterprise_search/{ref_id}
 * Operation: `get-deployment-enterprise-search-resource-info`
 * Get info about an Enterprise Search Resource belonging to a given Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.showMetadata Whether to include the full cluster metadata in the response - can be large per cluster and also include credentials.
 * @param {boolean} params.showPlans Whether to include the full current and pending plan information in the response - can be large per cluster. Default: true
 * @param {boolean} params.showPlanLogs Whether to include with the current and pending plan information the attempt log - can be very large per cluster.
 * @param {boolean} params.showPlanHistory Whether to include with the current and pending plan information the plan history- can be very large per cluster.
 * @param {boolean} params.showPlanDefaults If showing plans, whether to show values that are left at their default value (less readable but more informative).
 * @param {boolean} params.showSettings Whether to show cluster settings in the response.
 * @param {boolean} params.clearTransient If set (defaults to false) then removes the transient section from all child resources, making it safe to reapply via an update.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Standard response. See {@link EnterpriseSearchResourceInfo}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentEnterpriseSearchResourceInfoUrl(
  params: GetDeploymentEnterpriseSearchResourceInfoUrlParams,
): string {
  const queryString = buildQueryString({
    show_metadata: params.showMetadata === undefined ? false : params.showMetadata,
    show_plans: params.showPlans === undefined ? true : params.showPlans,
    show_plan_logs: params.showPlanLogs === undefined ? false : params.showPlanLogs,
    show_plan_history: params.showPlanHistory === undefined ? false : params.showPlanHistory,
    show_plan_defaults: params.showPlanDefaults === undefined ? false : params.showPlanDefaults,
    show_settings: params.showSettings === undefined ? false : params.showSettings,
    clear_transient: params.clearTransient === undefined ? false : params.clearTransient,
  })
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/enterprise_search/${params.refId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetDeploymentEnterpriseSearchResourceInfoUrlParams {
  deploymentId: string
  refId: string
  showMetadata?: boolean | null
  showPlans?: boolean | null
  showPlanLogs?: boolean | null
  showPlanHistory?: boolean | null
  showPlanDefaults?: boolean | null
  showSettings?: boolean | null
  clearTransient?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/health
 * Operation: `get-deployment-health`
 * Retrieves health info for an elastic deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {boolean} params.elasticsearchVerbose A boolean flag to specify if Elasticsearch health indicators should include the `details` and `diagnosis` fields.
 These fields are included by default when the parameter is not specified.
 * @see 200 Standard response with the deployment health info. See {@link DeploymentHealth}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentHealthUrl(params: GetDeploymentHealthUrlParams): string {
  const queryString = buildQueryString({
    elasticsearch_verbose: params.elasticsearchVerbose,
  })
  const path = encode`/deployments/${params.deploymentId}/health`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetDeploymentHealthUrlParams {
  deploymentId: string
  elasticsearchVerbose?: boolean | null
}

/**
 * GET /api/v1/deployments/{deployment_id}/heap_dumps
 * Operation: `get-deployment-heap-dumps`
 * Gets details about existing heap dumps and in progress heap dump captures across all instances belonging to the deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @see 200 The heap dump details for all instances belonging to the deployment. See {@link DeploymentHeapDumps}
 * @see 404 The deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentHeapDumpsUrl(params: GetDeploymentHeapDumpsUrlParams): string {
  const path = encode`/deployments/${params.deploymentId}/heap_dumps`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentHeapDumpsUrlParams {
  deploymentId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/integrations_server/{ref_id}
 * Operation: `get-deployment-integrations-server-resource-info`
 * Get info about an Integrations Server Resource belonging to a given Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.showMetadata Whether to include the full cluster metadata in the response - can be large per cluster and also include credentials.
 * @param {boolean} params.showPlans Whether to include the full current and pending plan information in the response - can be large per cluster. Default: true
 * @param {boolean} params.showPlanLogs Whether to include with the current and pending plan information the attempt log - can be very large per cluster.
 * @param {boolean} params.showPlanHistory Whether to include with the current and pending plan information the plan history- can be very large per cluster.
 * @param {boolean} params.showPlanDefaults If showing plans, whether to show values that are left at their default value (less readable but more informative).
 * @param {boolean} params.showSettings Whether to show cluster settings in the response.
 * @param {boolean} params.clearTransient If set (defaults to false) then removes the transient section from all child resources, making it safe to reapply via an update.
 * @see 200 Standard response. See {@link IntegrationsServerResourceInfo}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentIntegrationsServerResourceInfoUrl(
  params: GetDeploymentIntegrationsServerResourceInfoUrlParams,
): string {
  const queryString = buildQueryString({
    show_metadata: params.showMetadata === undefined ? false : params.showMetadata,
    show_plans: params.showPlans === undefined ? true : params.showPlans,
    show_plan_logs: params.showPlanLogs === undefined ? false : params.showPlanLogs,
    show_plan_history: params.showPlanHistory === undefined ? false : params.showPlanHistory,
    show_plan_defaults: params.showPlanDefaults === undefined ? false : params.showPlanDefaults,
    show_settings: params.showSettings === undefined ? false : params.showSettings,
    clear_transient: params.clearTransient === undefined ? false : params.clearTransient,
  })
  const path = encode`/deployments/${params.deploymentId}/integrations_server/${params.refId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetDeploymentIntegrationsServerResourceInfoUrlParams {
  deploymentId: string
  refId: string
  showMetadata?: boolean | null
  showPlans?: boolean | null
  showPlanLogs?: boolean | null
  showPlanHistory?: boolean | null
  showPlanDefaults?: boolean | null
  showSettings?: boolean | null
  clearTransient?: boolean | null
}

/**
 * GET /api/v1/regions/{region_id}/deployments/{deployment_id}/kibana/{ref_id}
 * Operation: `get-deployment-kib-resource-info`
 * Get info about a Kibana Resource belonging to a given Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.showMetadata Whether to include the full cluster metadata in the response - can be large per cluster and also include credentials.
 * @param {boolean} params.showPlans Whether to include the full current and pending plan information in the response - can be large per cluster. Default: true
 * @param {boolean} params.showPlanLogs Whether to include with the current and pending plan information the attempt log - can be very large per cluster.
 * @param {boolean} params.showPlanHistory Whether to include with the current and pending plan information the plan history- can be very large per cluster.
 * @param {boolean} params.showPlanDefaults If showing plans, whether to show values that are left at their default value (less readable but more informative).
 * @param {boolean} params.convertLegacyPlans If showing plans, whether to leave pre-2.0.0 plans in their legacy format (the default), or whether to update them to 2.0.x+ format (if 'true').
 * @param {boolean} params.showSettings Whether to show cluster settings in the response.
 * @param {boolean} params.clearTransient If set (defaults to false) then removes the transient section from all child resources, making it safe to reapply via an update.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Standard response. See {@link KibanaResourceInfo}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentKibResourceInfoUrl(
  params: GetDeploymentKibResourceInfoUrlParams,
): string {
  const queryString = buildQueryString({
    show_metadata: params.showMetadata === undefined ? false : params.showMetadata,
    show_plans: params.showPlans === undefined ? true : params.showPlans,
    show_plan_logs: params.showPlanLogs === undefined ? false : params.showPlanLogs,
    show_plan_history: params.showPlanHistory === undefined ? false : params.showPlanHistory,
    show_plan_defaults: params.showPlanDefaults === undefined ? false : params.showPlanDefaults,
    convert_legacy_plans:
      params.convertLegacyPlans === undefined ? false : params.convertLegacyPlans,
    show_settings: params.showSettings === undefined ? false : params.showSettings,
    clear_transient: params.clearTransient === undefined ? false : params.clearTransient,
  })
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/kibana/${params.refId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetDeploymentKibResourceInfoUrlParams {
  deploymentId: string
  refId: string
  showMetadata?: boolean | null
  showPlans?: boolean | null
  showPlanLogs?: boolean | null
  showPlanHistory?: boolean | null
  showPlanDefaults?: boolean | null
  convertLegacyPlans?: boolean | null
  showSettings?: boolean | null
  clearTransient?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/migrate_template
 * Operation: `migrate-deployment-template`
 * Returns a deployment update request that would migrate this deployment to a different template.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.templateId The ID of the deployment template to migrate to.
 * @see 200 The request was valid and the deployment can be migrated to the template with the provided ID. See {@link DeploymentUpdateRequest}
 * @see 400 The deployment cannot be successfully migrated to the template with the provided ID. See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function migrateDeploymentTemplateUrl(params: MigrateDeploymentTemplateUrlParams): string {
  const queryString = buildQueryString({
    template_id: params.templateId,
  })
  const path = encode`/deployments/${params.deploymentId}/migrate_template`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface MigrateDeploymentTemplateUrlParams {
  deploymentId: string
  templateId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/upgrade_assistant/status
 * Operation: `get-deployment-upgrade-assistant-status`
 * Get details about an Elasticsearch resource belonging to a given deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @see 200 The Upgrade Assistant returned the status successfully. See {@link DeploymentUpgradeAssistantStatusResponse}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentUpgradeAssistantStatusUrl(
  params: GetDeploymentUpgradeAssistantStatusUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/upgrade_assistant/status`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentUpgradeAssistantStatusUrlParams {
  deploymentId: string
}

/**
 * POST /api/v1/regions/{region_id}/deployments/{deployment_id}/{resource_kind}/{ref_id}/_restore
 * Operation: `restore-deployment-resource`
 * Restores a shutdown resource belonging to a given Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.restoreSnapshot Whether or not to restore a snapshot for those resources that allow it.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Standard Deployment Resource Crud Response. See {@link DeploymentResourceCrudResponse}
 * @see 400 * The Resource does not have a pending plan. (code: `deployments.resource_does_not_have_a_pending_plan`)
 * The resource is not shut down. (code: `deployments.resource_not_shutdown`). See {@link BasicFailedReply}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function restoreDeploymentResourceUrl(params: RestoreDeploymentResourceUrlParams): string {
  const queryString = buildQueryString({
    restore_snapshot: params.restoreSnapshot === undefined ? false : params.restoreSnapshot,
  })
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/_restore`
  return `${getBasePath()}${path}${queryString}`
}

export interface RestoreDeploymentResourceUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  restoreSnapshot?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/activity/{attempt_id}
 * Operation: `get-deployment-resource-plan-activity`
 * Retrieves details for a single plan activity for the given resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.attemptId Attempt id for the activity.
 * @see 200 Returning the single plan activity for the specified resource. See {@link CommonClusterPlanInfo}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentResourcePlanActivityUrl(
  params: GetDeploymentResourcePlanActivityUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/activity/${params.attemptId}`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentResourcePlanActivityUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  attemptId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/diagnostics/_capture
 * Operation: `capture-deployment-resource-diagnostics`
 * Retrieves a diagnostic bundle from an active cluster. To successfully retrieve a diagnostic bundle, the cluster must be responsive.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch or kibana).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.mode Capture mode - whether to obtain all the diagnostics data or the lightweight, essential-only subset of it. Applicable only to Elasticsearch, ignored for any other components which have just one capture mode implemented. Default: full
 * @see 200 The diagnostic bundle as a zip file. See {@link string}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 412 The diagnostics cannot be generated because the cluster is either stopped or initializing. (code: `clusters.cluster_plan_state_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function captureDeploymentResourceDiagnosticsUrl(
  params: CaptureDeploymentResourceDiagnosticsUrlParams,
): string {
  const queryString = buildQueryString({
    mode: params.mode === undefined ? `full` : params.mode,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/diagnostics/_capture`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface CaptureDeploymentResourceDiagnosticsUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  mode?: string | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/_start
 * Operation: `start-deployment-resource-instances-all`
 * Starts all instances belonging to a Deployment Resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana, apm, or integrations_server).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 202 The start command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 403 The start maintenance mode command was prohibited for the given Resource. (code: `deployments.instance_update_prohibited_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of the given resource type are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function startDeploymentResourceInstancesAllUrl(
  params: StartDeploymentResourceInstancesAllUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/_start`
  return `${getRawBasePath()}${path}`
}

export interface StartDeploymentResourceInstancesAllUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/_stop
 * Operation: `stop-deployment-resource-instances-all`
 * Stops all instances belonging to a Deployment Resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana, apm, or integrations_server).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 202 The stop command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 403 The stop maintenance mode command was prohibited for the given Resource. (code: `deployments.instance_update_prohibited_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of the given resource type are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function stopDeploymentResourceInstancesAllUrl(
  params: StopDeploymentResourceInstancesAllUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/_stop`
  return `${getRawBasePath()}${path}`
}

export interface StopDeploymentResourceInstancesAllUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/maintenance-mode/_start
 * Operation: `start-deployment-resource-instances-all-maintenance-mode`
 * Starts maintenance mode of all instances belonging to a Deployment Resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana, apm, or integrations_server).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 202 The start maintenance command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 403 The start maintenance mode command was prohibited for the given Resource. (code: `deployments.instance_update_prohibited_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of the given resource type are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function startDeploymentResourceInstancesAllMaintenanceModeUrl(
  params: StartDeploymentResourceInstancesAllMaintenanceModeUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/maintenance-mode/_start`
  return `${getRawBasePath()}${path}`
}

export interface StartDeploymentResourceInstancesAllMaintenanceModeUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/maintenance-mode/_stop
 * Operation: `stop-deployment-resource-instances-all-maintenance-mode`
 * Stops maintenance mode of all instances belonging to a Deployment Resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana, apm, or integrations_server).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 202 The stop maintenance mode command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 403 The stop maintenance mode command was prohibited for the given Resource. (code: `deployments.instance_update_prohibited_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of the given resource type are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function stopDeploymentResourceInstancesAllMaintenanceModeUrl(
  params: StopDeploymentResourceInstancesAllMaintenanceModeUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/maintenance-mode/_stop`
  return `${getRawBasePath()}${path}`
}

export interface StopDeploymentResourceInstancesAllMaintenanceModeUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
}

/**
 * PUT /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/overrides
 * Operation: `set-all-instances-settings-overrides`
 * Applies size overrides for all of the instances belonging to the given resource. Instance size overrides will be reset with the next plan change.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource. Only Elasticsearch resources are currently supported.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.restartAfterUpdate After overrides are set, restarts the instance to apply the changes.
 * @see Accepts a request payload of type {@link InstanceOverrides}
 * @see 200 The updated overrides. See {@link InstanceOverrides}
 * @see 404 * The deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot by found. (code: `deployments.deployment_resource_not_found`)
 * The specified override was out of range. Capacity must be in the range [1024, 65536] and storage multiplier must be in the range [1.0, 1000.0]. (code: `instance_overrides.out_of_range`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setAllInstancesSettingsOverridesUrl(
  params: SetAllInstancesSettingsOverridesUrlParams,
): string {
  const queryString = buildQueryString({
    restart_after_update:
      params.restartAfterUpdate === undefined ? false : params.restartAfterUpdate,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/overrides`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SetAllInstancesSettingsOverridesUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  restartAfterUpdate?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/{instance_ids}/_start
 * Operation: `start-deployment-resource-instances`
 * Starts instances belonging to a Deployment Resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana, apm, or integrations_server).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {array} params.instanceIds A comma-separated list of instance identifiers.
 * @param {boolean} params.ignoreMissing If true and the instance does not exist then quietly proceed to the next instance, otherwise treated as an error.
 * @see 202 The start command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 403 The start maintenance mode command was prohibited for the given Resource. (code: `deployments.instance_update_prohibited_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of the given resource type are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function startDeploymentResourceInstancesUrl(
  params: StartDeploymentResourceInstancesUrlParams,
): string {
  const queryString = buildQueryString({
    ignore_missing: params.ignoreMissing === undefined ? false : params.ignoreMissing,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/${params.instanceIds}/_start`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface StartDeploymentResourceInstancesUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  instanceIds: string[]
  ignoreMissing?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/{instance_ids}/_stop
 * Operation: `stop-deployment-resource-instances`
 * Stops instances belonging to a Deployment Resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana, apm, or integrations_server).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {array} params.instanceIds A comma-separated list of instance identifiers.
 * @param {boolean} params.ignoreMissing If true and the instance does not exist then quietly proceed to the next instance, otherwise treated as an error.
 * @see 202 The stop command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 403 The start maintenance mode command was prohibited for the given Resource. (code: `deployments.instance_update_prohibited_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of the given resource type are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function stopDeploymentResourceInstancesUrl(
  params: StopDeploymentResourceInstancesUrlParams,
): string {
  const queryString = buildQueryString({
    ignore_missing: params.ignoreMissing === undefined ? false : params.ignoreMissing,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/${params.instanceIds}/_stop`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface StopDeploymentResourceInstancesUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  instanceIds: string[]
  ignoreMissing?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/{instance_ids}/maintenance-mode/_start
 * Operation: `start-deployment-resource-maintenance-mode`
 * Starts maintenance mode of instances belonging to a Deployment Resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana, apm, or integrations_server).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {array} params.instanceIds A comma-separated list of instance identifiers.
 * @param {boolean} params.ignoreMissing If true and the instance does not exist then quietly proceed to the next instance, otherwise treated as an error.
 * @see 202 The start maintenance command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 403 The start maintenance mode command was prohibited for the given Resource. (code: `deployments.instance_update_prohibited_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of the given resource type are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function startDeploymentResourceMaintenanceModeUrl(
  params: StartDeploymentResourceMaintenanceModeUrlParams,
): string {
  const queryString = buildQueryString({
    ignore_missing: params.ignoreMissing === undefined ? false : params.ignoreMissing,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/${params.instanceIds}/maintenance-mode/_start`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface StartDeploymentResourceMaintenanceModeUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  instanceIds: string[]
  ignoreMissing?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/{instance_ids}/maintenance-mode/_stop
 * Operation: `stop-deployment-resource-maintenance-mode`
 * Stops maintenance mode of instances belonging to a Resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana, apm, or integrations_server).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {array} params.instanceIds A comma-separated list of instance identifiers.
 * @param {boolean} params.ignoreMissing If true and the instance does not exist then quietly proceed to the next instance, otherwise treated as an error.
 * @see 202 The stop maintenance mode command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 403 The stop maintenance mode command was prohibited for the given Resource. (code: `deployments.instance_update_prohibited_error`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances of the given resource type are missing. (code: `deployments.instances_missing_on_update_error`). See {@link BasicFailedReply}
 * @see 500 A Resource that was previously stored no longer exists. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function stopDeploymentResourceMaintenanceModeUrl(
  params: StopDeploymentResourceMaintenanceModeUrlParams,
): string {
  const queryString = buildQueryString({
    ignore_missing: params.ignoreMissing === undefined ? false : params.ignoreMissing,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/${params.instanceIds}/maintenance-mode/_stop`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface StopDeploymentResourceMaintenanceModeUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  instanceIds: string[]
  ignoreMissing?: boolean | null
}

/**
 * PUT /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/{instance_ids}/overrides
 * Operation: `set-instance-settings-overrides`
 * Applies size overrides for the given instances belonging to the given resource. Instance size overrides will be reset with the next plan change.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource. Only Elasticsearch resources are currently supported.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {array} params.instanceIds A comma-separated list of instance identifiers.
 * @param {boolean} params.restartAfterUpdate After overrides are set, restarts the instance to apply the changes.
 * @param {boolean} params.ignoreMissing When `false` and a specified instance is missing, will return an error and not update any instances. When `true` will update only existing instances and not treat a missing instance as an error.
 * @see Accepts a request payload of type {@link InstanceOverrides}
 * @see 200 The updated overrides. See {@link InstanceOverrides}
 * @see 404 * The deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot by found. (code: `deployments.deployment_resource_not_found`)
 * One or more instances weren't found, and `ignore_missing` is false or unspecified. (code: `deployments.instances_missing_on_update_error`)
 * The specified override was out of range. Capacity must be in the range [1024, 65536] and storage multiplier must be in the range [1.0, 1000.0]. (code: `instance_overrides.out_of_range`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setInstanceSettingsOverridesUrl(
  params: SetInstanceSettingsOverridesUrlParams,
): string {
  const queryString = buildQueryString({
    restart_after_update:
      params.restartAfterUpdate === undefined ? false : params.restartAfterUpdate,
    ignore_missing: params.ignoreMissing === undefined ? false : params.ignoreMissing,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/${params.instanceIds}/overrides`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SetInstanceSettingsOverridesUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  instanceIds: string[]
  restartAfterUpdate?: boolean | null
  ignoreMissing?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/{instance_id}/heap_dump/_capture
 * Operation: `capture-deployment-instance-heap-dump`
 * Captures a new on-demand heap dump for the given instance. The capture is completed asynchronously, and you can check its progress by getting heap dumps for the given resource.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana or apm).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.instanceId The instance identifier.
 * @see 202 Indicates that the capture has started
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * The specified instance cannot be found. (code: `deployments.instance_not_found_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function captureDeploymentInstanceHeapDumpUrl(
  params: CaptureDeploymentInstanceHeapDumpUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/${params.instanceId}/heap_dump/_capture`
  return `${getRawBasePath()}${path}`
}

export interface CaptureDeploymentInstanceHeapDumpUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  instanceId: string
}

/**
 * GET /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/{instance_id}/heap_dump/_download
 * Operation: `download-deployment-instance-heap-dump`
 * Returns the given instance's heap dump for download.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana or apm).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.instanceId The instance identifier.
 * @see 200 The given instance's heap dump. See {@link string}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * The specified instance cannot be found. (code: `deployments.instance_not_found_error`)
 * The specified instance does not have a heap dump available to download. (code: `heap_dumps.heap_dump_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function downloadDeploymentInstanceHeapDumpUrl(
  params: DownloadDeploymentInstanceHeapDumpUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/${params.instanceId}/heap_dump/_download`
  return `${getRawBasePath()}${path}`
}

export interface DownloadDeploymentInstanceHeapDumpUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  instanceId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/instances/{instance_id}/thread_dump/_capture
 * Operation: `capture-deployment-instance-thread-dump`
 * Captures a new thread dump for the given instance and returns the thread dump contents.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource (one of elasticsearch, kibana or apm).
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.instanceId The instance identifier.
 * @see 200 The thread dump contents. See {@link string}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * The specified instance cannot be found. (code: `deployments.instance_not_found_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function captureDeploymentInstanceThreadDumpUrl(
  params: CaptureDeploymentInstanceThreadDumpUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/instances/${params.instanceId}/thread_dump/_capture`
  return `${getRawBasePath()}${path}`
}

export interface CaptureDeploymentInstanceThreadDumpUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  instanceId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/metadata/raw
 * Operation: `set-deployment-resource-raw-metadata`
 * Advanced use only. Sets the internal metadata, in free-form JSON, for the resource. Only use the parameter to set the modified JSON that is returned from the get version of the metadata.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.version If specified, checks for conflicts against the metadata version (returned in 'x-cloud-resource-version' of the GET request).
 * @see Accepts a request payload of type {@link string}
 * @see 200 The resource metadata was successfully changed (the updated JSON is returned). See {@link string}
 * @see 400 The metadata is invalid. (code: `deployments.invalid_metadata`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 449 Please retry. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setDeploymentResourceRawMetadataUrl(
  params: SetDeploymentResourceRawMetadataUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/metadata/raw`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SetDeploymentResourceRawMetadataUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  version?: string | null
}

/**
 * PATCH /api/v1/regions/{region_id}/deployments/{deployment_id}/{resource_kind}/{ref_id}/metadata/raw
 * Operation: `patch-deployment-resource-raw-metadata`
 * Advanced use only. Patches the internal metadata with the given data.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 200 The resource metadata was successfully changed (the updated JSON is returned). See {@link string}
 * @see 400 The metadata is invalid. (code: `deployments.invalid_metadata`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 449 Please retry. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.metadata_internal_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function patchDeploymentResourceRawMetadataUrl(
  params: PatchDeploymentResourceRawMetadataUrlParams,
): string {
  const path = encode`/${params.regionId}/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/metadata/raw`
  return `${getBasePath()}${path}`
}

export interface PatchDeploymentResourceRawMetadataUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  regionId: string
}

/**
 * DELETE /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/plan/pending
 * Operation: `cancel-deployment-resource-pending-plan`
 * Cancels the pending plan of a Resource belonging to a given Deployment.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.forceDelete When `true`, deletes the pending plan instead of attempting a graceful cancellation. The default is `false`.
 * @param {boolean} params.ignoreMissing When `true`, returns successfully, even when plans are missing. The default is `false`.
 * @see 200 Standard Deployment Resource Crud Response. See {@link DeploymentResourceCrudResponse}
 * @see 400 The Resource does not have a pending plan. (code: `deployments.resource_does_not_have_a_pending_plan`). See {@link BasicFailedReply}
 * @see 404 The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function cancelDeploymentResourcePendingPlanUrl(
  params: CancelDeploymentResourcePendingPlanUrlParams,
): string {
  const queryString = buildQueryString({
    force_delete: params.forceDelete === undefined ? false : params.forceDelete,
    ignore_missing: params.ignoreMissing === undefined ? false : params.ignoreMissing,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${params.refId}/plan/pending`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface CancelDeploymentResourcePendingPlanUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  forceDelete?: boolean | null
  ignoreMissing?: boolean | null
}

/**
 * GET /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/proxy/{proxy_path}
 * Operation: `get-deployment-resource-proxy-requests`
 * Proxies the HTTP GET request to the deployment resource. You must specify the `X-Management-Request` HTTP header. NOTE: Use this endpoint for management purposes. It does not provide high performance.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.proxyPath The URL part to proxy to the deployment resource. Example: _cat/indices, /api/spaces/space or /api/ent/v1/internal/health.
 * @see Accepts an optional request payload of type {@link string}
 * @see 200 The request has been processed successfully through the proxy. See {@link GenericResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `clusters.cluster_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDeploymentResourceProxyRequestsUrl(
  params: GetDeploymentResourceProxyRequestsUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${
    params.refId
  }/proxy/${{ __path: params.proxyPath }}`
  return `${getRawBasePath()}${path}`
}

export interface GetDeploymentResourceProxyRequestsUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  proxyPath: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/proxy/{proxy_path}
 * Operation: `post-deployment-resource-proxy-requests`
 * Proxies the HTTP POST request to the deployment resource. You must specify the `X-Management-Request` HTTP header. NOTE: Use this endpoint for management purposes. It does not provide high performance.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.proxyPath The URL part to proxy to the deployment resource. Example: _cat/indices, /api/spaces/space or /api/ent/v1/internal/health.
 * @see Accepts an optional request payload of type {@link string}
 * @see 200 The request has been processed successfully through the proxy. See {@link GenericResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `clusters.cluster_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function postDeploymentResourceProxyRequestsUrl(
  params: PostDeploymentResourceProxyRequestsUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${
    params.refId
  }/proxy/${{ __path: params.proxyPath }}`
  return `${getRawBasePath()}${path}`
}

export interface PostDeploymentResourceProxyRequestsUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  proxyPath: string
}

/**
 * PUT /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/proxy/{proxy_path}
 * Operation: `put-deployment-resource-proxy-requests`
 * Proxies the HTTP PUT request to the deployment resource. You must specify the `X-Management-Request` HTTP header. NOTE: Use this endpoint for management purposes. It does not provide high performance.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.proxyPath The URL part to proxy to the deployment resource. Example: _cat/indices, /api/spaces/space or /api/ent/v1/internal/health.
 * @see Accepts an optional request payload of type {@link string}
 * @see 200 The request has been processed successfully through the proxy. See {@link GenericResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `clusters.cluster_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function putDeploymentResourceProxyRequestsUrl(
  params: PutDeploymentResourceProxyRequestsUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${
    params.refId
  }/proxy/${{ __path: params.proxyPath }}`
  return `${getRawBasePath()}${path}`
}

export interface PutDeploymentResourceProxyRequestsUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  proxyPath: string
}

/**
 * DELETE /api/v1/deployments/{deployment_id}/{resource_kind}/{ref_id}/proxy/{proxy_path}
 * Operation: `delete-deployment-resource-proxy-requests`
 * Proxies the HTTP DELETE request to the deployment resource. You must specify the `X-Management-Request` HTTP header. NOTE: Use this endpoint for management purposes. It does not provide high performance.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.resourceKind The kind of resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {string} params.proxyPath The URL part to proxy to the deployment resource. Example: _cat/indices, /api/spaces/space or /api/ent/v1/internal/health.
 * @see Accepts an optional request payload of type {@link string}
 * @see 200 The request has been processed successfully through the proxy. See {@link GenericResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `clusters.cluster_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteDeploymentResourceProxyRequestsUrl(
  params: DeleteDeploymentResourceProxyRequestsUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.resourceKind}/${
    params.refId
  }/proxy/${{ __path: params.proxyPath }}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteDeploymentResourceProxyRequestsUrlParams {
  deploymentId: string
  resourceKind: string
  refId: string
  proxyPath: string
}

/**
 * DELETE /api/v1/deployments/{deployment_id}/{stateless_resource_kind}/{ref_id}
 * Operation: `delete-deployment-stateless-resource`
 * Delete Stateless Resource belonging to a given Deployment. Deployment must be shutdown already.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.statelessResourceKind The kind of stateless resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @see 200 Standard Deployment Resource Crud Response. See {@link DeploymentResourceCrudResponse}
 * @see 400 Resource has still instances. (code: `deployments.resource_has_instances`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteDeploymentStatelessResourceUrl(
  params: DeleteDeploymentStatelessResourceUrlParams,
): string {
  const path = encode`/deployments/${params.deploymentId}/${params.statelessResourceKind}/${params.refId}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteDeploymentStatelessResourceUrlParams {
  deploymentId: string
  statelessResourceKind: string
  refId: string
}

/**
 * POST /api/v1/deployments/{deployment_id}/{stateless_resource_kind}/{ref_id}/_restart
 * Operation: `restart-deployment-stateless-resource`
 * Restarts a Stateless Resource. If a Resource is active: this command re-applies the existing plan but applies a "cluster_reboot", which issues a restart command and waits for it to complete. If a Resource is inactive: this command starts it up with the most recent successful plan.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.statelessResourceKind The kind of stateless resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.cancelPending If true, cancels any pending plans before restarting. If false and there are pending plans, returns an error.
 * @see 202 The restart command was issued successfully. See {@link DeploymentResourceCommandResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 422 The command sent to a Resource found the Resource in an illegal state, the error message gives more details. (code: `deployments.deployment_resource_plan_change_error`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function restartDeploymentStatelessResourceUrl(
  params: RestartDeploymentStatelessResourceUrlParams,
): string {
  const queryString = buildQueryString({
    cancel_pending: params.cancelPending === undefined ? false : params.cancelPending,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.statelessResourceKind}/${params.refId}/_restart`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface RestartDeploymentStatelessResourceUrlParams {
  deploymentId: string
  statelessResourceKind: string
  refId: string
  cancelPending?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/{stateless_resource_kind}/{ref_id}/_shutdown
 * Operation: `shutdown-deployment-stateless-resource`
 * Shut down Stateless Resource belonging to a given Deployment. Kibana cannot be shut down on Elasticsearch Service as it is required for Elasticsearch administrative functions, such as Snapshot Lifecycle Management and version upgrades.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.statelessResourceKind The kind of stateless resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.hide Hide cluster on shutdown. Hidden clusters are not listed by default. Only applicable for Platform administrators.
 * @param {boolean} params.skipSnapshot If true, will skip taking a snapshot of the cluster before shutting the cluster down (if even possible).
 * @see 200 Standard response. See {@link DeploymentResourceCommandResponse}
 * @see 400 Parameter is restricted and can only be set by a Platform administrator. (code: `deployments.restricted_parameter`). See {@link BasicFailedReply}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @see 500 We have failed you. (code: `deployments.deployment_resource_no_longer_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function shutdownDeploymentStatelessResourceUrl(
  params: ShutdownDeploymentStatelessResourceUrlParams,
): string {
  const queryString = buildQueryString({
    hide: params.hide,
    skip_snapshot: params.skipSnapshot === undefined ? false : params.skipSnapshot,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.statelessResourceKind}/${params.refId}/_shutdown`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface ShutdownDeploymentStatelessResourceUrlParams {
  deploymentId: string
  statelessResourceKind: string
  refId: string
  hide?: boolean | null
  skipSnapshot?: boolean | null
}

/**
 * POST /api/v1/deployments/{deployment_id}/{stateless_resource_kind}/{ref_id}/_upgrade
 * Operation: `upgrade-deployment-stateless-resource`
 * > WARNING > This endpoint is deprecated and scheduled to be removed in the next major version. Use [Update deployment](#update-deployment) instead.  Upgrades a running cluster.
 * @param {string} params.deploymentId Identifier for the Deployment.
 * @param {string} params.statelessResourceKind The kind of stateless resource.
 * @param {string} params.refId User-specified RefId for the Resource (or '_main' if there is only one).
 * @param {boolean} params.validateOnly When `true`, returns the update version without performing the upgrade.
 * @see 202 The upgrade command was issued successfully. Use the "GET" command on the /{deployment_id} resource to monitor progress. See {@link DeploymentResourceUpgradeResponse}
 * @see 404 * The Deployment specified by {deployment_id} cannot be found. (code: `deployments.deployment_not_found`)
 * The Resource specified by {ref_id} cannot be found. (code: `deployments.deployment_resource_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function upgradeDeploymentStatelessResourceUrl(
  params: UpgradeDeploymentStatelessResourceUrlParams,
): string {
  const queryString = buildQueryString({
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
  })
  const path = encode`/deployments/${params.deploymentId}/${params.statelessResourceKind}/${params.refId}/_upgrade`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface UpgradeDeploymentStatelessResourceUrlParams {
  deploymentId: string
  statelessResourceKind: string
  refId: string
  validateOnly?: boolean | null
}

/**
 * GET /api/v1/regions/{region_id}/health
 * Operation: `get-adminconsole-health`
 * Retrieves the Adminconsole health.
 * @param {string} params.expectStatus If specified, returns a 417 status code if the Adminconsole health status is worse than the expected status. Must be one of [green, red, yellow].
 * @param {boolean} params.verbose When true, returns the list of all health check results.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Adminconsole health. See {@link AdminconsoleHealth}
 * @see 417 Returned when expect_status is specified and the Adminconsole health status is worse than the expected status. See {@link AdminconsoleHealth}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAdminconsoleHealthUrl(params: GetAdminconsoleHealthUrlParams): string {
  const queryString = buildQueryString({
    expect_status: params.expectStatus,
    verbose: params.verbose,
  })
  const path = encode`/${params.regionId}/health`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetAdminconsoleHealthUrlParams {
  expectStatus?: string | null
  verbose?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/organizations
 * Operation: `list-organizations`
 * Fetch organizations available to the current user. Currently unavailable in self-hosted ECE.
 * @param {string} params.q Optional query string for filtering. Only `name:$nameToMatch` is supported for this parameter.
 * @param {string} params.nextPage Pagination cursor to get the next page of records.
 * @param {integer} params.limit The max records to return (max: 100). Default: 100
 * @see 200 Organizations fetched successfully. See {@link OrganizationList}
 * @see 401 User not found. (code: `user.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listOrganizationsUrl(params: ListOrganizationsUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    q: params.q,
    next_page: params.nextPage,
    limit: params.limit === undefined ? 100 : params.limit,
  })
  const path = `/organizations`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface ListOrganizationsUrlParams {
  q?: string | null
  nextPage?: string | null
  limit?: number | null
}

/**
 * POST /api/v1/organizations
 * Operation: `create-organization`
 * Creates a new organization. Currently unavailable in self-hosted ECE.
 * @see Accepts a request payload of type {@link OrganizationRequest}
 * @see 201 Organization created successfully. See {@link Organization}
 * @see 400 * Name must be between 2 and 30 characters. (code: `organization.invalid_name`)
 * An organization with this id already exists. (code: `organization.already_exists`). See {@link BasicFailedReply}
 * @see 401 User not found. (code: `user.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createOrganizationUrl(): string {
  const path = `/organizations`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/organizations/invitations/{invitation_token}
 * Operation: `get-organization-invitation`
 * Gets a single invitation to an organization by token. Currently unavailable in self-hosted ECE.
 * @param {string} params.invitationToken Organization invitation token.
 * @see 200 Organization invitation fetched successfully. See {@link OrganizationInvitation}
 * @see 404 Invitation not found. (code: `organization.invitation_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getOrganizationInvitationUrl(params: GetOrganizationInvitationUrlParams): string {
  const path = encode`/organizations/invitations/${params.invitationToken}`
  return `${getRawBasePath()}${path}`
}

export interface GetOrganizationInvitationUrlParams {
  invitationToken: string
}

/**
 * POST /api/v1/organizations/invitations/{invitation_token}/_accept
 * Operation: `accept-organization-invitation`
 * Accepts an organization invitation. Currently unavailable in self-hosted ECE.
 * @param {string} params.invitationToken Organization invitation token.
 * @see 200 Organization invitation accepted successfully
 * @see 400 User already belongs to organization. (code: `organization.user_organization_already_belongs`). See {@link BasicFailedReply}
 * @see 403 The current user authentication is not valid. (code: `root.invalid_authentication`). See {@link BasicFailedReply}
 * @see 404 * Organization not found. (code: `organization.not_found`)
 * User not found. (code: `user.not_found`)
 * Invitation not found. (code: `organization.invitation_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function acceptOrganizationInvitationUrl(
  params: AcceptOrganizationInvitationUrlParams,
): string {
  const path = encode`/organizations/invitations/${params.invitationToken}/_accept`
  return `${getRawBasePath()}${path}`
}

export interface AcceptOrganizationInvitationUrlParams {
  invitationToken: string
}

/**
 * GET /api/v1/organizations/{organization_id}
 * Operation: `get-organization`
 * Fetch a single organization by id. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 Organization fetched successfully. See {@link Organization}
 * @see 401 User not found. (code: `user.not_found`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getOrganizationUrl(params: GetOrganizationUrlParams): string {
  const path = encode`/organizations/${params.organizationId}`
  return `${getRawBasePath()}${path}`
}

export interface GetOrganizationUrlParams {
  organizationId: string
}

/**
 * PUT /api/v1/organizations/{organization_id}
 * Operation: `update-organization`
 * > WARNING > This endpoint is deprecated and scheduled to be removed in the next major version.  Updates an existing organization. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link OrganizationRequest}
 * @see 200 Organization updated successfully. See {@link Organization}
 * @see 400 * Name must be between 2 and 30 characters. (code: `organization.invalid_name`)
 * User already has an organization. (code: `organization.user_organization_already_exists`). See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateOrganizationUrl(params: UpdateOrganizationUrlParams): string {
  const path = encode`/organizations/${params.organizationId}`
  return `${getRawBasePath()}${path}`
}

export interface UpdateOrganizationUrlParams {
  organizationId: string
}

/**
 * DELETE /api/v1/organizations/{organization_id}
 * Operation: `delete-organization`
 * Deletes an organization. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 Organization deleted successfully
 * @see 400 * Organization has more than one member and cannot be deleted. (code: `organization.has_members`)
 * Organization has active deployments and cannot be deleted. (code: `organization.has_deployments`). See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteOrganizationUrl(params: DeleteOrganizationUrlParams): string {
  const path = encode`/organizations/${params.organizationId}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteOrganizationUrlParams {
  organizationId: string
}

/**
 * PATCH /api/v1/organizations/{organization_id}
 * Operation: `patch-organization`
 * Patches an existing organization. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link string}
 * @see 200 Organization updated successfully. See {@link Organization}
 * @see 400 * Name must be between 2 and 30 characters. (code: `organization.invalid_name`)
 * User already has an organization. (code: `organization.user_organization_already_exists`). See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function patchOrganizationUrl(params: PatchOrganizationUrlParams): string {
  const path = encode`/organizations/${params.organizationId}`
  return `${getRawBasePath()}${path}`
}

export interface PatchOrganizationUrlParams {
  organizationId: string
}

/**
 * GET /api/v1/regions/{region_id}/organizations/{organization_id}/audit_logs
 * Operation: `get-audit-logs-status`
 * Get status of audit logs for the organization.
 * @param {string} params.organizationId Identifier for the Organization.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Status of audit logs for the organization. See {@link AuditLogsStatus}
 * @see 404 * Organization not found. (code: `organization.not_found`)
 * Audit Logs configuration not found. (code: `organization.audit_service_not_configured`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAuditLogsStatusUrl(params: GetAuditLogsStatusUrlParams): string {
  const path = encode`/${params.regionId}/organizations/${params.organizationId}/audit_logs`
  return `${getBasePath()}${path}`
}

export interface GetAuditLogsStatusUrlParams {
  organizationId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/organizations/{organization_id}/audit_logs
 * Operation: `enable-audit-logs`
 * Enable Audit Logging for an organization.
 * @param {string} params.organizationId Identifier for the Organization.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link AuditLogsDestinationSettings}
 * @see 201 Audit Logs enabled successfully. See {@link AuditLogsStatus}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 Audit Logs Service capacity is beyond configured threshold. (code: `organization.audit_service_at_capacity`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function enableAuditLogsUrl(params: EnableAuditLogsUrlParams): string {
  const path = encode`/${params.regionId}/organizations/${params.organizationId}/audit_logs`
  return `${getBasePath()}${path}`
}

export interface EnableAuditLogsUrlParams {
  organizationId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/organizations/{organization_id}/audit_logs
 * Operation: `disable-audit-logs`
 * Disable audit logs for this organization.
 * @param {string} params.organizationId Identifier for the Organization.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Disable audit logs for the organization.
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function disableAuditLogsUrl(params: DisableAuditLogsUrlParams): string {
  const path = encode`/${params.regionId}/organizations/${params.organizationId}/audit_logs`
  return `${getBasePath()}${path}`
}

export interface DisableAuditLogsUrlParams {
  organizationId: string
  regionId: string
}

/**
 * GET /api/v1/organizations/{organization_id}/configuration
 * Operation: `get-organization-configuration`
 * Get configuration data associated to an organization.
 * @param {string} params.organizationId Organization id whom you would like to configure access data for.
 * @see 200 The Organization configuration data was retrieved successfully. See {@link OrganizationConfigurationResponse}
 * @see 400 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getOrganizationConfigurationUrl(
  params: GetOrganizationConfigurationUrlParams,
): string {
  const path = encode`/organizations/${params.organizationId}/configuration`
  return `${getRawBasePath()}${path}`
}

export interface GetOrganizationConfigurationUrlParams {
  organizationId: string
}

/**
 * PUT /api/v1/organizations/{organization_id}/configuration
 * Operation: `update-organization-configuration`
 * Updates the organization configuration using PATCH semantics. Only the fields that are referenced in the update are changed.
 * @param {string} params.organizationId Organization id whom you would like to configure access data for.
 * @param {boolean} params.injectDefaultValues Injects default values to the missing fields in the body.
 * @see Accepts a request payload of type {@link OrganizationConfigurationUpdateRequest}
 * @see 200 The organization configuration data was updated successfully. See {@link OrganizationConfigurationResponse}
 * @see 400 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateOrganizationConfigurationUrl(
  params: UpdateOrganizationConfigurationUrlParams,
): string {
  const queryString = buildQueryString({
    injectDefaultValues:
      params.injectDefaultValues === undefined ? false : params.injectDefaultValues,
  })
  const path = encode`/organizations/${params.organizationId}/configuration`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface UpdateOrganizationConfigurationUrlParams {
  organizationId: string
  injectDefaultValues?: boolean | null
}

/**
 * PATCH /api/v1/organizations/{organization_id}/configuration
 * Operation: `patch-organization-configuration`
 * Patches the organization configuration. Only the fields that are referenced in the update are changed.
 * @param {string} params.organizationId Organization id whom you would like to configure access data for.
 * @see Accepts a request payload of type {@link OrganizationConfigurationUpdateRequest}
 * @see 200 The organization configuration data was patched successfully. See {@link OrganizationConfigurationResponse}
 * @see 400 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function patchOrganizationConfigurationUrl(
  params: PatchOrganizationConfigurationUrlParams,
): string {
  const path = encode`/organizations/${params.organizationId}/configuration`
  return `${getRawBasePath()}${path}`
}

export interface PatchOrganizationConfigurationUrlParams {
  organizationId: string
}

/**
 * GET /api/v1/organizations/{organization_id}/domains
 * Operation: `domain-claim-get-domain-claims`
 * Get domain claims for the organization.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 The claimed domains for the organization. See {@link GetOrganizationClaimedDomainsResponse}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function domainClaimGetDomainClaimsUrl(params: DomainClaimGetDomainClaimsUrlParams): string {
  const path = encode`/organizations/${params.organizationId}/domains`
  return `${getRawBasePath()}${path}`
}

export interface DomainClaimGetDomainClaimsUrlParams {
  organizationId: string
}

/**
 * DELETE /api/v1/organizations/{organization_id}/domains
 * Operation: `domain-claim-delete`
 * Delete domain claim.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link DeleteDomainClaimRequest}
 * @see 200 With status 200 ok to signal the domain has been unclaimed
 * @see 400 An IdP is configured for the organization. (code: `org.idp.idp_configured`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 409 Concurrent organization modification. (code: `org.domain_claim.concurrent_organization_modification`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function domainClaimDeleteUrl(params: DomainClaimDeleteUrlParams): string {
  const path = encode`/organizations/${params.organizationId}/domains`
  return `${getRawBasePath()}${path}`
}

export interface DomainClaimDeleteUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/organizations/{organization_id}/domains/_generate_verification_code
 * Operation: `domain-claim-generate-verification-code`
 * Generate verification code for the domain claim challenge.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link VerificationCodeRequest}
 * @see 200 The verification code response. See {@link VerificationCodeResponse}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function domainClaimGenerateVerificationCodeUrl(
  params: DomainClaimGenerateVerificationCodeUrlParams,
): string {
  const path = encode`/organizations/${params.organizationId}/domains/_generate_verification_code`
  return `${getRawBasePath()}${path}`
}

export interface DomainClaimGenerateVerificationCodeUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/organizations/{organization_id}/domains/_verify
 * Operation: `domain-claim-verify-domain`
 * Verify domain claim challenge.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link DomainVerificationRequest}
 * @see 200 With status 200 ok to signal the domain has been claimed
 * @see 400 Domain claim already exists. (code: `org.domain_claim.already_exists`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 409 Concurrent organization modification. (code: `org.domain_claim.concurrent_organization_modification`). See {@link BasicFailedReply}
 * @see 449 The challenge can not be verified at the moment, please retry later
 * @return {string} A fully-interpolated and escaped URL
 */
export function domainClaimVerifyDomainUrl(params: DomainClaimVerifyDomainUrlParams): string {
  const path = encode`/organizations/${params.organizationId}/domains/_verify`
  return `${getRawBasePath()}${path}`
}

export interface DomainClaimVerifyDomainUrlParams {
  organizationId: string
}

/**
 * GET /api/v1/organizations/{organization_id}/idp
 * Operation: `get-organization-idp`
 * Gets the oranization's IdP. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 The IdP info. See {@link OrganizationIdp}
 * @see 404 IdP was not configured yet. (code: `org.idp.idp_not_configured`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getOrganizationIdpUrl(params: GetOrganizationIdpUrlParams): string {
  const path = encode`/organizations/${params.organizationId}/idp`
  return `${getRawBasePath()}${path}`
}

export interface GetOrganizationIdpUrlParams {
  organizationId: string
}

/**
 * PUT /api/v1/organizations/{organization_id}/idp
 * Operation: `setup-organization-idp`
 * Sets up an organization's IdP. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link IdpConfigurationRequest}
 * @see 200 The IdP info. See {@link OrganizationIdp}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setupOrganizationIdpUrl(params: SetupOrganizationIdpUrlParams): string {
  const path = encode`/organizations/${params.organizationId}/idp`
  return `${getRawBasePath()}${path}`
}

export interface SetupOrganizationIdpUrlParams {
  organizationId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/organizations/{organization_id}/idp
 * Operation: `teardown-organization-idp`
 * Tear down up an organization's IdP. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The IdP was successfully removed
 * @see 404 Organization IdP not configured. (code: `org.idp.idp_not_configured`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function teardownOrganizationIdpUrl(params: TeardownOrganizationIdpUrlParams): string {
  const path = encode`/${params.regionId}/organizations/${params.organizationId}/idp`
  return `${getBasePath()}${path}`
}

export interface TeardownOrganizationIdpUrlParams {
  organizationId: string
  regionId: string
}

/**
 * GET /api/v1/organizations/{organization_id}/idp/metadata.xml
 * Operation: `get-organization-idp-metadata`
 * Gets the oranization's IdP metadata.xml. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 The IdP info. See {@link string}
 * @see 404 IdP was not configured yet. (code: `org.idp.idp_not_configured`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getOrganizationIdpMetadataUrl(params: GetOrganizationIdpMetadataUrlParams): string {
  const path = encode`/organizations/${params.organizationId}/idp/metadata.xml`
  return `${getRawBasePath()}${path}`
}

export interface GetOrganizationIdpMetadataUrlParams {
  organizationId: string
}

/**
 * GET /api/v1/organizations/{organization_id}/invitations
 * Operation: `list-organization-invitations`
 * Fetch open invitations to the selected organization. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 Organization invitations fetched successfully. See {@link OrganizationInvitations}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listOrganizationInvitationsUrl(
  params: ListOrganizationInvitationsUrlParams,
): string {
  const path = encode`/organizations/${params.organizationId}/invitations`
  return `${getRawBasePath()}${path}`
}

export interface ListOrganizationInvitationsUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/organizations/{organization_id}/invitations
 * Operation: `create-organization-invitations`
 * Creates or refreshes organization invitations. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link OrganizationInvitationRequest}
 * @see 201 Organization invitations created successfully. See {@link OrganizationInvitations}
 * @see 400 * Invitation email was not valid. (code: `organization.invitation_invalid_email`)
 * Invitation already sent. (code: `organization.invitation_already_exists`)
 * User already belongs to organization. (code: `organization.user_organization_already_belongs`). See {@link BasicFailedReply}
 * @see 403 The current user authentication is not valid. (code: `root.invalid_authentication`). See {@link BasicFailedReply}
 * @see 404 * Organization not found. (code: `organization.not_found`)
 * User not found. (code: `user.not_found`)
 * Invitation sender does not belong to organization. (code: `organization.user_organization_does_not_belong`). See {@link BasicFailedReply}
 * @see 429 Request exceeds organization invitation creation rate limits. (code: `organization.invitations_rate_limit_exceeded`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createOrganizationInvitationsUrl(
  params: CreateOrganizationInvitationsUrlParams,
): string {
  const path = encode`/organizations/${params.organizationId}/invitations`
  return `${getRawBasePath()}${path}`
}

export interface CreateOrganizationInvitationsUrlParams {
  organizationId: string
}

/**
 * DELETE /api/v1/organizations/{organization_id}/invitations/{invitation_tokens}
 * Operation: `delete-organization-invitations`
 * Deletes one or more organization invitations. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @param {string} params.invitationTokens CSV list of Invitation tokens.
 * @see 200 Organization invitations deleted successfully
 * @see 400 No valid invitation token was supplied. (code: `root.invalid_data`). See {@link BasicFailedReply}
 * @see 404 * Organization not found. (code: `organization.not_found`)
 * Invitation not found. (code: `organization.invitation_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteOrganizationInvitationsUrl(
  params: DeleteOrganizationInvitationsUrlParams,
): string {
  const path = encode`/organizations/${params.organizationId}/invitations/${params.invitationTokens}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteOrganizationInvitationsUrlParams {
  organizationId: string
  invitationTokens: string
}

/**
 * GET /api/v1/organizations/{organization_id}/members
 * Operation: `list-organization-members`
 * Fetch users belonging to the selected organization. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @param {boolean} params.showHiddenRoleAssignments If true, hidden Role Assignments are returned.
 * @see 200 Organization members fetched successfully. See {@link OrganizationMemberships}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listOrganizationMembersUrl(params: ListOrganizationMembersUrlParams): string {
  const queryString = buildQueryString({
    show_hidden_role_assignments:
      params.showHiddenRoleAssignments === undefined ? false : params.showHiddenRoleAssignments,
  })
  const path = encode`/organizations/${params.organizationId}/members`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface ListOrganizationMembersUrlParams {
  organizationId: string
  showHiddenRoleAssignments?: boolean | null
}

/**
 * POST /api/v1/organizations/{organization_id}/members
 * Operation: `create-organization-membership`
 * Creates an organization membership. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link OrganizationMembershipRequest}
 * @see 201 Organization membership created successfully. See {@link OrganizationMembership}
 * @see 400 User already has an organization. (code: `organization.user_organization_already_exists`). See {@link BasicFailedReply}
 * @see 404 * User not found. (code: `user.not_found`)
 * Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createOrganizationMembershipUrl(
  params: CreateOrganizationMembershipUrlParams,
): string {
  const path = encode`/organizations/${params.organizationId}/members`
  return `${getRawBasePath()}${path}`
}

export interface CreateOrganizationMembershipUrlParams {
  organizationId: string
}

/**
 * DELETE /api/v1/organizations/{organization_id}/members/{user_ids}
 * Operation: `delete-organization-memberships`
 * Deletes one or more organization memberships. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @param {string} params.userIds CSV list of User identifiers.
 * @param {boolean} params.force Whether or not to force the removal of Org memberships (effective only for Platform Admins).
 * @see 200 Organization membership deleted successfully
 * @see 404 * User not found. (code: `user.not_found`)
 * Organization not found. (code: `organization.not_found`)
 * Organization membership not found. (code: `organization.membership_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteOrganizationMembershipsUrl(
  params: DeleteOrganizationMembershipsUrlParams,
): string {
  const queryString = buildQueryString({
    force: params.force === undefined ? false : params.force,
  })
  const path = encode`/organizations/${params.organizationId}/members/${params.userIds}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface DeleteOrganizationMembershipsUrlParams {
  organizationId: string
  userIds: string
  force?: boolean | null
}

/**
 * GET /api/v1/organizations/{organization_id}/role_mappings
 * Operation: `get-role-mappings`
 * Gets the organization role  mappings. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 The IdP info. See {@link GetRoleMappingsResponse}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getRoleMappingsUrl(params: GetRoleMappingsUrlParams): string {
  const path = encode`/organizations/${params.organizationId}/role_mappings`
  return `${getRawBasePath()}${path}`
}

export interface GetRoleMappingsUrlParams {
  organizationId: string
}

/**
 * PUT /api/v1/organizations/{organization_id}/role_mappings
 * Operation: `update-role-mappings`
 * Updates the role assignments applicable when logging via SSO. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link UpdateOrganizationRoleMappingRequest}
 * @see 200 An empty response
 * @see 400 Validation error. (code: `org.role_mapping_rule.syntax_error`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateRoleMappingsUrl(params: UpdateRoleMappingsUrlParams): string {
  const path = encode`/organizations/${params.organizationId}/role_mappings`
  return `${getRawBasePath()}${path}`
}

export interface UpdateRoleMappingsUrlParams {
  organizationId: string
}

/**
 * DELETE /api/v1/organizations/{organization_id}/role_mappings
 * Operation: `delete-role-mappings`
 * Deletes the organization role  mappings. Currently unavailable in self-hosted ECE.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 The mappings were successfully deleted
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteRoleMappingsUrl(params: DeleteRoleMappingsUrlParams): string {
  const path = encode`/organizations/${params.organizationId}/role_mappings`
  return `${getRawBasePath()}${path}`
}

export interface DeleteRoleMappingsUrlParams {
  organizationId: string
}

/**
 * GET /api/v1/phone-home/config
 * Operation: `get-telemetry-config`
 * Returns whether ECE telemetry is enabled.
 * @see 200 The current ECE telemetry configuration. See {@link TelemetryConfig}
 * @see 403 User must have Platform level permissions. (code: `root.unauthorized.rbac`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTelemetryConfigUrl(): string {
  const path = `/phone-home/config`
  return `${getRawBasePath()}${path}`
}

/**
 * PUT /api/v1/phone-home/config
 * Operation: `set-telemetry-config`
 * Sets whether to enable ECE telemetry.
 * @see Accepts a request payload of type {@link TelemetryConfigRequest}
 * @see 200 Telemetry configuration updated successfully. See {@link TelemetryConfig}
 * @see 403 User must have Platform level permissions. (code: `root.unauthorized.rbac`). See {@link BasicFailedReply}
 * @see 409 The telemetry configuration did not exist so there was an attempt to create one. Another request resulted in the creation of a telemetry configuration before this request completed, resulting in the failure of this request to create a configuration. Please retry. (code: `telemetry.already_exists`). See {@link BasicFailedReply}
 * @see 500 Failed to set the configuration due to an internal server error. (code: `telemetry.request_execution_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setTelemetryConfigUrl(): string {
  const path = `/phone-home/config`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/phone-home/data
 * Operation: `get-phone-home-data`
 * Returns ECE usage statistics for Elastic's internal use.
 * @see 200 Telemetry for the ECE installation. See {@link PhoneHomeTelemetry}
 * @see 403 User must have Platform level permissions. (code: `root.unauthorized.rbac`). See {@link BasicFailedReply}
 * @see 404 Data is unavailable because telemetry is disabled for this ECE installation. (code: `telemetry.not_enabled`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getPhoneHomeDataUrl(): string {
  const path = `/phone-home/data`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1(/regions/{region_id})?/platform
 * Operation: `get-platform`
 * Retrieves information about the active platform.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The platform information is retrieved. See {@link PlatformInfo}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getPlatformUrl(params: GetPlatformUrlParams = emptyParams): string {
  const path =
    (params.regionId == null ? '' : '/' + encodeURIComponent(params.regionId)) + `/platform`
  return `${params.regionId == null ? getRawBasePath() : getBasePath()}${path}`
}

export interface GetPlatformUrlParams {
  regionId?: string | null
}

/**
 * GET /api/v1/platform/configuration/api_base_url
 * Operation: `get-api-base-url`
 * Gets the API base Url configuration value.
 * @see 200 The API base Url was successfully retrieved. See {@link ApiBaseUrlData}
 * @see 404 There is no configured API base value but optimistic locking was sent. (code: `adminconsole.base_url.not_found`). See {@link BasicFailedReply}
 * @see 500 The persisted Url is malformed. (code: `adminconsole.base_url.invalid_persisted_data`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getApiBaseUrlUrl(): string {
  const path = `/platform/configuration/api_base_url`
  return `${getRawBasePath()}${path}`
}

/**
 * PUT /api/v1/platform/configuration/api_base_url
 * Operation: `set-api-base-url`
 * Saves the API base Url configuration value.
 * @param {string} params.version If specified, then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request). If not specified, will unconditionally upsert.
 * @param {boolean} params.skipCascadingOperations Whether or not to skip cascading operations, such as re-provisioning the Security Deployment.
 * @see Accepts a request payload of type {@link ApiBaseUrlData}
 * @see 200 The API base Url was successfully saved. See {@link ApiBaseUrlData}
 * @see 400 The optimistic locking version format was wrong. (code: `adminconsole.base_url.bad_version_format`). See {@link BasicFailedReply}
 * @see 404 There is no configured API base value but optimistic locking was sent. (code: `adminconsole.base_url.not_found`). See {@link BasicFailedReply}
 * @see 409 There was an optimistic locking version conflict. (code: `adminconsole.base_url.version_conflict`). See {@link BasicFailedReply}
 * @see 412 skip_cascading_operations was false but the Security Deployment already had a pending plan. (code: `security_deployment.cluster_pending_plan_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setApiBaseUrlUrl(params: SetApiBaseUrlUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    version: params.version,
    skip_cascading_operations:
      params.skipCascadingOperations === undefined ? false : params.skipCascadingOperations,
  })
  const path = `/platform/configuration/api_base_url`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SetApiBaseUrlUrlParams {
  version?: string | null
  skipCascadingOperations?: boolean | null
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/azure/privatelink/linkids
 * Operation: `list-resolved-link-ids`
 * Lists the resolved Azure Private Link IDs.
 * @param {string} params.region Return results only from this region.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The request successfully returned resolved link IDs. See {@link ResolvedLinkIds}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listResolvedLinkIdsUrl(params: ListResolvedLinkIdsUrlParams): string {
  const queryString = buildQueryString({
    region: params.region,
  })
  const path = encode`/${params.regionId}/platform/configuration/azure/privatelink/linkids`
  return `${getBasePath()}${path}${queryString}`
}

export interface ListResolvedLinkIdsUrlParams {
  region?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/azure/privatelink/linkids/{name-and-id}
 * Operation: `get-resolved-link-id`
 * Retrieves the Azure Private Link ID for a given Private Endpoint name and ID.
 * @param {string} params.nameAndId Name and ID of the Private Endpoint connection.
 * @param {string} params.region Region in which to check for a resolved link ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The request successfully returned a resolved link ID. See {@link ResolvedLinkId}
 * @see 400 Invalid Private Link connection name format. (code: `azure_private_link_configuration_link_id.invalid_format`). See {@link BasicFailedReply}
 * @see 404 Private endpoint connection not found. (code: `azure_private_link_configuration_link_id.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getResolvedLinkIdUrl(params: GetResolvedLinkIdUrlParams): string {
  const queryString = buildQueryString({
    region: params.region,
  })
  const path = encode`/${params.regionId}/platform/configuration/azure/privatelink/linkids/{name-and-id}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetResolvedLinkIdUrlParams {
  nameAndId: string
  region: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/azure/privatelink/linkids/{name-and-id}
 * Operation: `delete-resolved-link-id`
 * Deletes the resolved link ID based on connection name and ID. Doesn't delete the underlying Azure Private Link connection.
 * @param {string} params.nameAndId Name and ID of the Private Endpoint connection.
 * @param {string} params.region Region in which to check for a resolved link ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The request successfully returned a resolved link ID
 * @see 400 Invalid Private Link connection name format. (code: `azure_private_link_configuration_link_id.invalid_format`). See {@link BasicFailedReply}
 * @see 404 Private endpoint connection not found. (code: `azure_private_link_configuration_link_id.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteResolvedLinkIdUrl(params: DeleteResolvedLinkIdUrlParams): string {
  const queryString = buildQueryString({
    region: params.region,
  })
  const path = encode`/${params.regionId}/platform/configuration/azure/privatelink/linkids/{name-and-id}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteResolvedLinkIdUrlParams {
  nameAndId: string
  region: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/azure/privatelink/linkids/{name-and-id}/_refresh
 * Operation: `refresh-link-id`
 * Ensures the Private Link connection from an endpoint with given name and ID is accepted.
 * @param {string} params.nameAndId Name and ID of the Private Endpoint connection.
 * @param {string} params.region Region in which to check for a resolved link ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The request successfully returned a resolved link ID. See {@link ResolvedLinkId}
 * @see 400 Invalid Private Link connection name format. (code: `azure_private_link_configuration_link_id.invalid_format`). See {@link BasicFailedReply}
 * @see 404 Private endpoint connection not found. (code: `azure_private_link_configuration_link_id.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function refreshLinkIdUrl(params: RefreshLinkIdUrlParams): string {
  const queryString = buildQueryString({
    region: params.region,
  })
  const path = encode`/${params.regionId}/platform/configuration/azure/privatelink/linkids/{name-and-id}/_refresh`
  return `${getBasePath()}${path}${queryString}`
}

export interface RefreshLinkIdUrlParams {
  nameAndId: string
  region: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/feature-flags
 * Operation: `get-feature-flags`
 * Retrieves an overview of all feature flags and strategies in an ECE installation.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An overview of all feature flags. See {@link FeatureFlagOverview}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getFeatureFlagsUrl(params: GetFeatureFlagsUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/feature-flags`
  return `${getBasePath()}${path}`
}

export interface GetFeatureFlagsUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/feature-flags/{feature_flag}
 * Operation: `get-feature-flag`
 * Retrieve a feature flags current state by name.
 * @param {string} params.featureFlag Name of a given feature flag.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Retrieve feature flag state. See {@link FeatureFlagState}
 * @see 404 The feature specified by {feature_flag} cannot be found. (code: `feature_flags.feature_flag_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getFeatureFlagUrl(params: GetFeatureFlagUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/feature-flags/${params.featureFlag}`
  return `${getBasePath()}${path}`
}

export interface GetFeatureFlagUrlParams {
  featureFlag: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/feature-flags/{feature_flag}
 * Operation: `set-feature-flag`
 * Sets a given feature flags state.
 * @param {string} params.featureFlag Name of a given feature flag.
 * @param {boolean} params.validateOnly If specified, validates the supplied feature flag state only.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link FeatureFlagState}
 * @see 200 Sets the given feature flag state. See {@link FeatureFlagState}
 * @see 400 The feature flag state contained errors. (code: `feature_flags.invalid_state`). See {@link BasicFailedReply}
 * @see 404 The Feature specified by {feature_flag} cannot be found. (code: `feature_flags.feature_flag_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setFeatureFlagUrl(params: SetFeatureFlagUrlParams): string {
  const queryString = buildQueryString({
    validate_only: params.validateOnly,
  })
  const path = encode`/${params.regionId}/platform/configuration/feature-flags/${params.featureFlag}`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetFeatureFlagUrlParams {
  featureFlag: string
  validateOnly?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/instances
 * Operation: `get-instance-configurations`
 * Get instance configurations.
 * @param {boolean} params.showDeleted If true, instance configurations marked for deletions are also returned. Otherwise, only instance configurations not marked for deletion are returned.
 * @param {boolean} params.showMaxZones If true, will populate the max_zones field of the instance configurations.
 * @param {boolean} params.includeVersions If true, will return all existing versions of each instance configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 A InstanceConfiguration. See {@link Array<InstanceConfiguration>}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getInstanceConfigurationsUrl(params: GetInstanceConfigurationsUrlParams): string {
  const queryString = buildQueryString({
    show_deleted: params.showDeleted,
    show_max_zones: params.showMaxZones === undefined ? false : params.showMaxZones,
    include_versions: params.includeVersions === undefined ? false : params.includeVersions,
  })
  const path = encode`/${params.regionId}/platform/configuration/instances`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetInstanceConfigurationsUrlParams {
  showDeleted?: boolean | null
  showMaxZones?: boolean | null
  includeVersions?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/instances
 * Operation: `create-instance-configuration`
 * Create instance configuration and return the auto-generated ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link InstanceConfiguration}
 * @see 201 Instance Configuration added with the auto generated ID returned. See {@link VersionedIdResponse}
 * @see 400 cluster_type in the InstanceConfiguration model is invalid (code: 'configuration.invalid_cluster_type') or the discrete_sizes in the InstanceConfiguration model is invalid (code: 'configuration.invalid_discrete_sizes') or the metadata in the InstanceConfiguration model has empty keys or values (code: 'configuration.bad_meta_data') . See {@link BasicFailedReply}
 * @see 403 system_owned or deleted_on cannot be set externally (code: 'configuration.system_owned') . See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createInstanceConfigurationUrl(
  params: CreateInstanceConfigurationUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/configuration/instances`
  return `${getBasePath()}${path}`
}

export interface CreateInstanceConfigurationUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/instances/{id}
 * Operation: `get-instance-configuration`
 * Get instance configuration by id.
 * @param {string} params.id ID of the instance configuration.
 * @param {integer} params.configVersion Optionally retrieve the specified config version of the IC (otherwise retrieves the latest/only version).
 * @param {boolean} params.showDeleted If true, if the instance configuration has been marked for deletion it is still returned. Otherwise, instance configurations marked for deletion generate a 404.
 * @param {boolean} params.showMaxZones If true, will populate the max_zones field of the instance configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An InstanceConfiguration. See {@link InstanceConfiguration}
 * @see 404 Instance configuration specified by {id} cannot be found (code: 'configuration.instance_configuration_not_found'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getInstanceConfigurationUrl(params: GetInstanceConfigurationUrlParams): string {
  const queryString = buildQueryString({
    config_version: params.configVersion,
    show_deleted: params.showDeleted,
    show_max_zones: params.showMaxZones === undefined ? false : params.showMaxZones,
  })
  const path = encode`/${params.regionId}/platform/configuration/instances/${params.id}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetInstanceConfigurationUrlParams {
  id: string
  configVersion?: number | null
  showDeleted?: boolean | null
  showMaxZones?: boolean | null
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/instances/{id}
 * Operation: `set-instance-configuration`
 * Creates or updates an instance configuration.
 * @param {string} params.id ID of the instance configuration.
 * @param {integer} params.version This is a database-level field, not related to the application-level 'config_version', except as described in the following docs. If specified, checks for conflicts against 'x-cloud-resource-version' from the GET request (where the 'config_version' in the GET should match the the 'configuration_version' in the PUT body if the IC is configuration controlled).
 * @param {boolean} params.configControl If true, then an update that changes immutable fields will create a new version of the IC (leaving the previous ones accessible via 'config_version'). Otherwise such updates will fail.
 * @param {boolean} params.testConfig If true, The IC is added as a test version (set 'instance_config_version': -1 in the deployment request to reference), leaving the existing IC alone. Will error if the IC doesn't already exist.
 * @param {boolean} params.strictMode If true, then allocator_filter and storage_multiplier cannot be changed in an existing IC. Defaults to false unless config_control is true (in which case a new version is created).
 * @param {boolean} params.createOnly If true, will fail if an instance configuration already exists at the given id.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link InstanceConfiguration}
 * @see 200 The instance configuration was updated successfully. See {@link VersionedIdResponse}
 * @see 201 The instance configuration was created successfully. See {@link VersionedIdResponse}
 * @see 400 cluster_type in the InstanceConfiguration model is invalid (code: 'configuration.invalid_cluster_type') or the ZooKeeper operation failed due to bad version, etc. (code: 'configuration.instance_configuration_update_failed') or the id in the InstanceConfiguration model is reserved/prohibited (code: 'configuration.configuration_id_reserved') or the discrete_sizes in the InstanceConfiguration model is invalid (code: 'configuration.invalid_discrete_sizes') or the metadata in the InstanceConfiguration model has empty keys or values (code: 'configuration.bad_meta_data') . See {@link BasicFailedReply}
 * @see 403 system_owned or deleted_on cannot be set externally (code: 'configuration.system_owned') . See {@link BasicFailedReply}
 * @see 404 Instance configuration specified by {id} cannot be found or the operation failed (code: 'configuration.instance_configuration_not_found'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setInstanceConfigurationUrl(params: SetInstanceConfigurationUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
    config_control: params.configControl,
    test_config: params.testConfig,
    strict_mode: params.strictMode,
    create_only: params.createOnly,
  })
  const path = encode`/${params.regionId}/platform/configuration/instances/${params.id}`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetInstanceConfigurationUrlParams {
  id: string
  version?: number | null
  configControl?: boolean | null
  testConfig?: boolean | null
  strictMode?: boolean | null
  createOnly?: boolean | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/instances/{id}
 * Operation: `delete-instance-configuration`
 * Delete the instance configuration with the given id.
 * @param {string} params.id ID of the instance configuration.
 * @param {integer} params.version This is a database-level field, not related to the application-level 'config_version', except as described in the following docs. If specified, checks for conflicts against 'x-cloud-resource-version' from the GET request (the GET's 'config_version' should be left blank if the IC is configuration controlled, ie to get the latest configuration).
 * @param {boolean} params.onlyTestVersion If true, the testing version (version -1) will be permanently deleted but the latest IC version will remain untouched. Defaults to false.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Instance configuration specified by {id} was deleted
 * @see 403 Instance configuration specified by {id} is system owned (code: 'configuration.system_owned'). See {@link BasicFailedReply}
 * @see 404 Instance configuration specified by {id} cannot be found or the operation failed (code: 'configuration.instance_configuration_not_found'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteInstanceConfigurationUrl(
  params: DeleteInstanceConfigurationUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
    only_test_version: params.onlyTestVersion,
  })
  const path = encode`/${params.regionId}/platform/configuration/instances/${params.id}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteInstanceConfigurationUrlParams {
  id: string
  version?: number | null
  onlyTestVersion?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/networking/deployment_domain_name
 * Operation: `get-default-deployment-domain-name`
 * Gets the default Deployment Domain Name configuration value.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Deployment Domain Name was successfully retrieved. See {@link DeploymentDomainName}
 * @see 404 There is no configured Deployment Domain Name but optimistic locking was sent. (code: `networking.cname.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getDefaultDeploymentDomainNameUrl(
  params: GetDefaultDeploymentDomainNameUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/configuration/networking/deployment_domain_name`
  return `${getBasePath()}${path}`
}

export interface GetDefaultDeploymentDomainNameUrlParams {
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/networking/deployment_domain_name
 * Operation: `set-default-deployment-domain-name`
 * Saves the default Deployment Domain Name configuration value.
 * @param {string} params.version If specified, then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request). If not specified, will unconditionally upsert.
 * @param {boolean} params.skipCascadingOperations Whether or not to skip cascading operations, such as re-provisioning the Security Deployment.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link DeploymentDomainName}
 * @see 200 The Deployment Domain Name was successfully saved. See {@link DeploymentDomainName}
 * @see 400 The optimistic locking version format was wrong. (code: `networking.cname.bad_version_format`). See {@link BasicFailedReply}
 * @see 404 There is no configured Deployment Domain Name but optimistic locking was sent. (code: `networking.cname.not_found`). See {@link BasicFailedReply}
 * @see 409 There was an optimistic locking version conflict. (code: `networking.cname.version_conflict`). See {@link BasicFailedReply}
 * @see 412 skip_cascading_operations was false but the Security Deployment already had a pending plan. (code: `security_deployment.cluster_pending_plan_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setDefaultDeploymentDomainNameUrl(
  params: SetDefaultDeploymentDomainNameUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
    skip_cascading_operations:
      params.skipCascadingOperations === undefined ? false : params.skipCascadingOperations,
  })
  const path = encode`/${params.regionId}/platform/configuration/networking/deployment_domain_name`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetDefaultDeploymentDomainNameUrlParams {
  version?: string | null
  skipCascadingOperations?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/networking/deployment_domain_name/{resource_kind}
 * Operation: `get-resource-kind-deployment-domain-name`
 * Gets Deployment Domain Name configuration value for a resource kind.
 * @param {string} params.resourceKind The kind of resource you want to manage a Deployment Domain Name for.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Deployment Domain Name was successfully retrieved. See {@link DeploymentDomainName}
 * @see 404 There is no configured Deployment Domain Name but optimistic locking was sent. (code: `networking.cname.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getResourceKindDeploymentDomainNameUrl(
  params: GetResourceKindDeploymentDomainNameUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/configuration/networking/deployment_domain_name/${params.resourceKind}`
  return `${getBasePath()}${path}`
}

export interface GetResourceKindDeploymentDomainNameUrlParams {
  resourceKind: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/networking/deployment_domain_name/{resource_kind}
 * Operation: `set-resource-kind-deployment-domain-name`
 * Saves the Deployment Domain Name configuration value for a resource kind.
 * @param {string} params.resourceKind The kind of resource you want to manage a Deployment Domain Name for.
 * @param {string} params.version If specified, then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request). If not specified, will unconditionally upsert.
 * @param {boolean} params.skipCascadingOperations Whether or not to skip cascading operations, such as re-provisioning the Security Deployment.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link DeploymentDomainName}
 * @see 200 The Deployment Domain Name was successfully saved. See {@link DeploymentDomainName}
 * @see 400 The optimistic locking version format was wrong. (code: `networking.cname.bad_version_format`). See {@link BasicFailedReply}
 * @see 404 There is no configured Deployment Domain Name but optimistic locking was sent. (code: `networking.cname.not_found`). See {@link BasicFailedReply}
 * @see 409 There was an optimistic locking version conflict. (code: `networking.cname.version_conflict`). See {@link BasicFailedReply}
 * @see 412 skip_cascading_operations was false but the Security Deployment already had a pending plan. (code: `security_deployment.cluster_pending_plan_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setResourceKindDeploymentDomainNameUrl(
  params: SetResourceKindDeploymentDomainNameUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
    skip_cascading_operations:
      params.skipCascadingOperations === undefined ? false : params.skipCascadingOperations,
  })
  const path = encode`/${params.regionId}/platform/configuration/networking/deployment_domain_name/${params.resourceKind}`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetResourceKindDeploymentDomainNameUrlParams {
  resourceKind: string
  version?: string | null
  skipCascadingOperations?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/deployment
 * Operation: `get-security-deployment`
 * Retrieves the current security deployment.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The security deployment was successfully returned. See {@link SecurityDeployment}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_deployment.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSecurityDeploymentUrl(params: GetSecurityDeploymentUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/deployment`
  return `${getBasePath()}${path}`
}

export interface GetSecurityDeploymentUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/deployment
 * Operation: `create-security-deployment`
 * Creates a new security deployment configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SecurityDeploymentCreateRequest}
 * @see 201 The security deployment was successfully created. See {@link IdResponse}
 * @see 409 * There is a version conflict. (code: `security_deployment.version_conflict`)
 * There is a version conflict. (code: `security_deployment.already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createSecurityDeploymentUrl(params: CreateSecurityDeploymentUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/deployment`
  return `${getBasePath()}${path}`
}

export interface CreateSecurityDeploymentUrlParams {
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/security/deployment
 * Operation: `update-security-deployment`
 * Updates an existing security deployment configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SecurityDeploymentUpdateRequest}
 * @see 200 The security deployment was successfully updated. See {@link IdResponse}
 * @see 404 The security deployment was not found. (code: `security_deployment.not_found`). See {@link BasicFailedReply}
 * @see 409 * There is a version conflict. (code: `security_deployment.version_conflict`)
 * There is a version conflict. (code: `security_deployment.already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateSecurityDeploymentUrl(params: UpdateSecurityDeploymentUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/deployment`
  return `${getBasePath()}${path}`
}

export interface UpdateSecurityDeploymentUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/deployment/_publish_idp_metadata
 * Operation: `publish-security-deployment-idp-metadata`
 * Publishes IdP metadata to be picked up by SSO-enabled SPs.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 List of public URLs where the IdP metadata are available. See {@link PublishIdPMetadataResponse}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_deployment.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function publishSecurityDeploymentIdpMetadataUrl(
  params: PublishSecurityDeploymentIdpMetadataUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/configuration/security/deployment/_publish_idp_metadata`
  return `${getBasePath()}${path}`
}

export interface PublishSecurityDeploymentIdpMetadataUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/enrollment-tokens
 * Operation: `get-enrollment-tokens`
 * Retrieves a list of active enrollment tokens.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 A token has been generated that can be used to start new servers with the requested roles. See {@link ListEnrollmentTokenReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getEnrollmentTokensUrl(params: GetEnrollmentTokensUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/enrollment-tokens`
  return `${getBasePath()}${path}`
}

export interface GetEnrollmentTokensUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/enrollment-tokens
 * Operation: `create-enrollment-token`
 * Creates an enrollment token.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link EnrollmentTokenRequest}
 * @see 200 A token has been generated that can be used to start new servers with the requested roles. See {@link RequestEnrollmentTokenReply}
 * @see 400 The token request format was invalid, details in the error (code: 'enrollment_tokens.invalid_token_request'). See {@link BasicFailedReply}
 * @see 403 No signing key is available to generate a token (code: 'enrollment_tokens.signing_key_not_found'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createEnrollmentTokenUrl(params: CreateEnrollmentTokenUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/enrollment-tokens`
  return `${getBasePath()}${path}`
}

export interface CreateEnrollmentTokenUrlParams {
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/enrollment-tokens/{token}
 * Operation: `delete-enrollment-token`
 * Revokes and deletes the enrollment token.
 * @param {string} params.token The token or token identifier to revoke.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The supplied token has been revoked and can no longer be used to start services on new servers
 * @see 404 Token not found (code: 'enrollment_tokens.invalid_token_id'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteEnrollmentTokenUrl(params: DeleteEnrollmentTokenUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/enrollment-tokens/${params.token}`
  return `${getBasePath()}${path}`
}

export interface DeleteEnrollmentTokenUrlParams {
  token: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/extra_certs
 * Operation: `get-extra-certificates`
 * List all of the extra certificates.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The collection of all available extra certificates public chains. See {@link PublicCertChainCollection}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getExtraCertificatesUrl(params: GetExtraCertificatesUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/extra_certs`
  return `${getBasePath()}${path}`
}

export interface GetExtraCertificatesUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/extra_certs/{cert_id}
 * Operation: `get-extra-certificate`
 * Read an extra certificate.
 * @param {string} params.certId The extra certificate Id. For example, `star_my_domain_com`.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Extra certificate public chain if exists. See {@link PublicCertChain}
 * @see 404 The extra certificate cannot be found. (code: `cert.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getExtraCertificateUrl(params: GetExtraCertificateUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/extra_certs/${params.certId}`
  return `${getBasePath()}${path}`
}

export interface GetExtraCertificateUrlParams {
  certId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/security/extra_certs/{cert_id}
 * Operation: `set-extra-certificate`
 * Add or update an extra certificate.
 * @param {string} params.certId The extra certificate Id. For example, `star_my_domain_com`.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 200 Updates extra certificate under given id
 * @see 400 Certificate uses unknown cryptographic algorithm. (code: `cert.unknown_cert_algorithm`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setExtraCertificateUrl(params: SetExtraCertificateUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/extra_certs/${params.certId}`
  return `${getBasePath()}${path}`
}

export interface SetExtraCertificateUrlParams {
  certId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/extra_certs/{cert_id}
 * Operation: `delete-extra-certificate`
 * Delete an extra certificate.
 * @param {string} params.certId The extra certificate Id. For example, `star_my_domain_com`.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Extra certificate under the given id is deleted if exists
 * @see 404 The extra certificate cannot be found. (code: `cert.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteExtraCertificateUrl(params: DeleteExtraCertificateUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/extra_certs/${params.certId}`
  return `${getBasePath()}${path}`
}

export interface DeleteExtraCertificateUrlParams {
  certId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/realms
 * Operation: `get-security-realm-configurations`
 * Retrieves a list of security realm configurations.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The security realm configurations were successfully returned. See {@link SecurityRealmInfoList}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSecurityRealmConfigurationsUrl(
  params: GetSecurityRealmConfigurationsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/configuration/security/realms`
  return `${getBasePath()}${path}`
}

export interface GetSecurityRealmConfigurationsUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/realms/_reorder
 * Operation: `reorder-security-realms`
 * Reorder security realms.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SecurityRealmsReorderRequest}
 * @see 200 The reorder request was successful
 * @see 400 * Invalid ids . (code: `security_realm.invalid_ids`)
 * Missing ids . (code: `security_realm.missing_ids`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function reorderSecurityRealmsUrl(params: ReorderSecurityRealmsUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/realms/_reorder`
  return `${getBasePath()}${path}`
}

export interface ReorderSecurityRealmsUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/realms/active-directory
 * Operation: `create-active-directory-configuration`
 * Creates a new Active Directory configuration. .
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ActiveDirectorySettings}
 * @see 201 The Active Directory configuration was successfully created
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The url format is invalid. (code: `security_realm.invalid_url`)
 * Invalid Active Directory URL. (code: `security_realm.active_directory.invalid_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createActiveDirectoryConfigurationUrl(
  params: CreateActiveDirectoryConfigurationUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/configuration/security/realms/active-directory`
  return `${getBasePath()}${path}`
}

export interface CreateActiveDirectoryConfigurationUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/realms/active-directory/{realm_id}
 * Operation: `get-active-directory-configuration`
 * Retrieves a single Active Directory security realm configuration. .
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Active Directory configuration was successfully retrieved. See {@link ActiveDirectorySettings}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getActiveDirectoryConfigurationUrl(
  params: GetActiveDirectoryConfigurationUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/configuration/security/realms/active-directory/${params.realmId}`
  return `${getBasePath()}${path}`
}

export interface GetActiveDirectoryConfigurationUrlParams {
  realmId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/security/realms/active-directory/{realm_id}
 * Operation: `update-active-directory-configuration`
 * Updates an existing Active Directory configuration. .
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ActiveDirectorySettings}
 * @see 200 The Active Directory configuration was successfully updated
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The url format is invalid. (code: `security_realm.invalid_url`)
 * Invalid Active Directory URL. (code: `security_realm.active_directory.invalid_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateActiveDirectoryConfigurationUrl(
  params: UpdateActiveDirectoryConfigurationUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/security/realms/active-directory/${params.realmId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface UpdateActiveDirectoryConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/realms/active-directory/{realm_id}
 * Operation: `delete-active-directory-configuration`
 * Deletes a single Active Directory configuration. .
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Active Directory configuration was successfully deleted
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteActiveDirectoryConfigurationUrl(
  params: DeleteActiveDirectoryConfigurationUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/security/realms/active-directory/${params.realmId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteActiveDirectoryConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/realms/ldap
 * Operation: `create-ldap-configuration`
 * Creates a new LDAP configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LdapSettings}
 * @see 201 The LDAP configuration was successfully created
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The url format is invalid. (code: `security_realm.invalid_url`)
 * Invalid LDAP URL. (code: `security_realm.ldap.invalid_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createLdapConfigurationUrl(params: CreateLdapConfigurationUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/realms/ldap`
  return `${getBasePath()}${path}`
}

export interface CreateLdapConfigurationUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/realms/ldap/{realm_id}
 * Operation: `get-ldap-configuration`
 * Retrieves a single LDAP security realm configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The LDAP configuration was successfully retrieved. See {@link LdapSettings}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getLdapConfigurationUrl(params: GetLdapConfigurationUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/realms/ldap/${params.realmId}`
  return `${getBasePath()}${path}`
}

export interface GetLdapConfigurationUrlParams {
  realmId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/security/realms/ldap/{realm_id}
 * Operation: `update-ldap-configuration`
 * Updates an existing LDAP configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LdapSettings}
 * @see 200 The LDAP configuration was successfully updated
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The url format is invalid. (code: `security_realm.invalid_url`)
 * Invalid LDAP URL. (code: `security_realm.ldap.invalid_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateLdapConfigurationUrl(params: UpdateLdapConfigurationUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/security/realms/ldap/${params.realmId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface UpdateLdapConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/realms/ldap/{realm_id}
 * Operation: `delete-ldap-configuration`
 * Deletes a single LDAP configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The LDAP configuration was successfully deleted
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteLdapConfigurationUrl(params: DeleteLdapConfigurationUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/security/realms/ldap/${params.realmId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteLdapConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/realms/saml
 * Operation: `create-saml-configuration`
 * Creates a new SAML configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SamlSettings}
 * @see 201 The SAML configuration was successfully created
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The SAML IDP metadata endpoint returned an error response code 200 OK. (code: `security_realm.saml.invalid_idp_metadata_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createSamlConfigurationUrl(params: CreateSamlConfigurationUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/realms/saml`
  return `${getBasePath()}${path}`
}

export interface CreateSamlConfigurationUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/realms/saml/{realm_id}
 * Operation: `get-saml-configuration`
 * Retrieves a single SAML security realm configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The SAML configuration was successfully retrieved. See {@link SamlSettings}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSamlConfigurationUrl(params: GetSamlConfigurationUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/realms/saml/${params.realmId}`
  return `${getBasePath()}${path}`
}

export interface GetSamlConfigurationUrlParams {
  realmId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/security/realms/saml/{realm_id}
 * Operation: `update-saml-configuration`
 * Updates an existing SAML configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SamlSettings}
 * @see 200 The SAML configuration was successfully updated
 * @see 400 * The realm id is already in use. (code: `security_realm.id_conflict`)
 * The selected id is not valid. (code: `security_realm.invalid_id`)
 * Order must be greater than zero. (code: `security_realm.invalid_order`)
 * Invalid Elasticsearch Security realm type. (code: `security_realm.invalid_type`)
 * The realm order is already in use. (code: `security_realm.order_conflict`)
 * Advanced YAML format is invalid. (code: `security_realm.invalid_yaml`)
 * The SAML IDP metadata endpoint returned an error response code 200 OK. (code: `security_realm.saml.invalid_idp_metadata_url`)
 * Invalid certificate bundle URL. (code: `security_realm.invalid_bundle_url`). See {@link BasicFailedReply}
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateSamlConfigurationUrl(params: UpdateSamlConfigurationUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/security/realms/saml/${params.realmId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface UpdateSamlConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/realms/saml/{realm_id}
 * Operation: `delete-saml-configuration`
 * Deletes a single SAML configuration.
 * @param {string} params.realmId The Elasticsearch Security realm identifier.
 * @param {string} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The SAML configuration was successfully deleted
 * @see 404 The realm specified by {realm_id} cannot be found. (code: `security_realm.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `security_realm.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteSamlConfigurationUrl(params: DeleteSamlConfigurationUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/security/realms/saml/${params.realmId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteSamlConfigurationUrlParams {
  realmId: string
  version?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/service-accounts
 * Operation: `list-service-accounts`
 * Retrieves the list of all service accounts.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The list of service accounts was successfully returned. See {@link ServiceAccountList}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listServiceAccountsUrl(params: ListServiceAccountsUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/service-accounts`
  return `${getBasePath()}${path}`
}

export interface ListServiceAccountsUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/service-accounts
 * Operation: `create-service-account`
 * Creates a new service account.
 * @param {boolean} params.acceptUnknownResourceActions When true, accepts resource actions that aren't specified in the domain. Useful to grant resource actions that haven't yet been deployed.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ServiceAccountCreateRequest}
 * @see 201 The service account was successfully created. See {@link ServiceAccount}
 * @see 400 Service account name is not valid. (code: `service_accounts.invalid_id`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createServiceAccountUrl(params: CreateServiceAccountUrlParams): string {
  const queryString = buildQueryString({
    accept_unknown_resource_actions:
      params.acceptUnknownResourceActions === undefined
        ? false
        : params.acceptUnknownResourceActions,
  })
  const path = encode`/${params.regionId}/platform/configuration/security/service-accounts`
  return `${getBasePath()}${path}${queryString}`
}

export interface CreateServiceAccountUrlParams {
  acceptUnknownResourceActions?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}
 * Operation: `get-service-account`
 * Retrieves details for a single service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The service account was successfully returned. See {@link ServiceAccount}
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getServiceAccountUrl(params: GetServiceAccountUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}`
  return `${getBasePath()}${path}`
}

export interface GetServiceAccountUrlParams {
  userId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}
 * Operation: `delete-service-account`
 * Deletes a single service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The service account was successfully deleted
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteServiceAccountUrl(params: DeleteServiceAccountUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}`
  return `${getBasePath()}${path}`
}

export interface DeleteServiceAccountUrlParams {
  userId: string
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}
 * Operation: `patch-service-account`
 * Updates an existing service account using PATCH semantics.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {boolean} params.acceptUnknownResourceActions When true, accepts resource actions that aren't specified in the domain. Useful to grant resource actions that haven't yet been deployed.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ServiceAccountPatchRequest}
 * @see 200 The service account was successfully updated. See {@link ServiceAccount}
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function patchServiceAccountUrl(params: PatchServiceAccountUrlParams): string {
  const queryString = buildQueryString({
    accept_unknown_resource_actions:
      params.acceptUnknownResourceActions === undefined
        ? false
        : params.acceptUnknownResourceActions,
  })
  const path = encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface PatchServiceAccountUrlParams {
  userId: string
  acceptUnknownResourceActions?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}/keys
 * Operation: `list-service-account-api-keys`
 * Retries the list of API keys for a service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The list of service account API keys was successfully returned. See {@link ServiceAccountApiKeyList}
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listServiceAccountApiKeysUrl(params: ListServiceAccountApiKeysUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}/keys`
  return `${getBasePath()}${path}`
}

export interface ListServiceAccountApiKeysUrlParams {
  userId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}/keys
 * Operation: `create-service-account-api-key`
 * Creates a new API key for a service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ServiceAccountApiKeyCreateRequest}
 * @see 201 The service account API key was successfully created. See {@link ServiceAccountApiKey}
 * @see 404 Service account not found. (code: `service_accounts.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createServiceAccountApiKeyUrl(params: CreateServiceAccountApiKeyUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}/keys`
  return `${getBasePath()}${path}`
}

export interface CreateServiceAccountApiKeyUrlParams {
  userId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/security/service-accounts/{user_id}/keys/{api_key_id}
 * Operation: `delete-service-account-api-key`
 * Deletes an API key for a service account.
 * @param {string} params.userId Identifier for the Service Account.
 * @param {string} params.apiKeyId Identifier for the Service Account API Key.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The service account API key was successfully deleted
 * @see 404 * Service account not found. (code: `service_accounts.not_found`)
 * Service account API key not found. (code: `service_accounts.api_key_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteServiceAccountApiKeyUrl(params: DeleteServiceAccountApiKeyUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/service-accounts/${params.userId}/keys/${params.apiKeyId}`
  return `${getBasePath()}${path}`
}

export interface DeleteServiceAccountApiKeyUrlParams {
  userId: string
  apiKeyId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/security/tls/{service_name}
 * Operation: `get-tls-certificate`
 * Retrieves a certificate in the TLS certificate chain.
 * @param {string} params.serviceName The service certificate chain to read.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The TLS certificate chain, with private key omitted, for the given service and whether it was user specified. See {@link TlsPublicCertChain}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTlsCertificateUrl(params: GetTlsCertificateUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/tls/${params.serviceName}`
  return `${getBasePath()}${path}`
}

export interface GetTlsCertificateUrlParams {
  serviceName: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/security/tls/{service_name}
 * Operation: `set-tls-certificate`
 * Creates or updates the TLS certificate chain.
 * @param {string} params.serviceName The service certificate chain to read.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 202 The TLS update has been accepted for the given service and will take effect throughout the system. See {@link UpdatedTlsChain}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setTlsCertificateUrl(params: SetTlsCertificateUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/security/tls/${params.serviceName}`
  return `${getBasePath()}${path}`
}

export interface SetTlsCertificateUrlParams {
  serviceName: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/shared-files/{shared_file}
 * Operation: `get-shared-file`
 * Gets the value of the shared file: {shared_file} should be equal to an existing containerset's path, like '/container_sets/admin-consoles/shared/adminconsole.conf'.
 * @param {string} params.sharedFile The shared file to read.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The contents of the shared file, with ZK version in the header. See {@link SharedFileReadRequest}
 * @see 409 Bad Version for shared file. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSharedFileUrl(params: GetSharedFileUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/shared-files/${params.sharedFile}`
  return `${getBasePath()}${path}`
}

export interface GetSharedFileUrlParams {
  sharedFile: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/shared-files/{shared_file}
 * Operation: `set-shared-file`
 * Sets the value of the shared file from request body: {shared_file} should be equal to an existing containerset's path, like '/container_sets/admin-consoles/shared/adminconsole.conf'.
 * @param {string} params.sharedFile The shared file to write.
 * @param {integer} params.version The shared file version.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 201 EmptyResponse from successful write of the shared file
 * @see 404 Shared file not found (code: 'sharedfiles.no_shared_file_found'). See {@link BasicFailedReply}
 * @see 409 Bad Version for shared file. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setSharedFileUrl(params: SetSharedFileUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/shared-files/${params.sharedFile}`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetSharedFileUrlParams {
  sharedFile: string
  version?: number | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/shared-files/{shared_file}
 * Operation: `delete-shared-file`
 * Deletes the shared file: {shared_file} should be equal to an existing containerset's path, like '/container_sets/admin-consoles/shared/adminconsole.conf'.
 * @param {string} params.sharedFile The shared file to delete.
 * @param {integer} params.version The shared file version.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 EmptyResponse from successful delete of the shared file
 * @see 404 Shared file not found (code: 'sharedfiles.no_shared_file_found'). See {@link BasicFailedReply}
 * @see 409 Bad Version for shared file. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteSharedFileUrl(params: DeleteSharedFileUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/shared-files/${params.sharedFile}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteSharedFileUrlParams {
  sharedFile: string
  version?: number | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/snapshots/repositories
 * Operation: `get-snapshot-repositories`
 * Retrieves a list of available snapshot repository configurations. To access the configuration contents, you must have privileged permissions.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 List of snapshot repository configs. See {@link RepositoryConfigs}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSnapshotRepositoriesUrl(params: GetSnapshotRepositoriesUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/snapshots/repositories`
  return `${getBasePath()}${path}`
}

export interface GetSnapshotRepositoriesUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/snapshots/repositories/{repository_name}
 * Operation: `get-snapshot-repository`
 * Retrieves the snapshot repository configuration. To access the configuration contents, you must have privileged permissions.
 * @param {string} params.repositoryName The name of the snapshot repository configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Retrieve snapshot repository config. See {@link RepositoryConfig}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSnapshotRepositoryUrl(params: GetSnapshotRepositoryUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/snapshots/repositories/${params.repositoryName}`
  return `${getBasePath()}${path}`
}

export interface GetSnapshotRepositoryUrlParams {
  repositoryName: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/snapshots/repositories/{repository_name}
 * Operation: `set-snapshot-repository`
 * Creates or updates the snapshot repository configuration.
 * @param {integer} params.version Checks for conflicts against the metadata version, then returns the value in the `x-cloud-resource-version` header.
 * @param {string} params.repositoryName The name of the snapshot repository configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SnapshotRepositoryConfiguration}
 * @see 200 New snapshot repository config. See {@link RepositoryConfig}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setSnapshotRepositoryUrl(params: SetSnapshotRepositoryUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/snapshots/repositories/${params.repositoryName}`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetSnapshotRepositoryUrlParams {
  version?: number | null
  repositoryName: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/snapshots/repositories/{repository_name}
 * Operation: `delete-snapshot-repository`
 * Deletes the snapshot repository configuration.
 * @param {string} params.repositoryName The name of the snapshot repository configuration.
 * @param {boolean} params.cleanupDeployments Removes references to this snapshot repository configuration and disables snapshots on the clusters that were referencing this configuration. If a request is made to delete a repository configuration that has already been deleted and this parameter is set to true and clusters still reference the configuration, then the request will have the side effects of removing references and disabling snapshots for clusters that reference the previously deleted configuration.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 successful operation
 * @see 202 Delete snapshot repository config
 * @see 500 Failed to delete references and disable snapshots in one or more referencing clusters. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteSnapshotRepositoryUrl(params: DeleteSnapshotRepositoryUrlParams): string {
  const queryString = buildQueryString({
    cleanup_deployments:
      params.cleanupDeployments === undefined ? false : params.cleanupDeployments,
  })
  const path = encode`/${params.regionId}/platform/configuration/snapshots/repositories/${params.repositoryName}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteSnapshotRepositoryUrlParams {
  repositoryName: string
  cleanupDeployments?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/store
 * Operation: `list-config-store-option`
 * List all existing Config Store Option.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 List of existing Config Store Option. See {@link ConfigStoreOptionList}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listConfigStoreOptionUrl(params: ListConfigStoreOptionUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/store`
  return `${getBasePath()}${path}`
}

export interface ListConfigStoreOptionUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/store/{config_option_id}
 * Operation: `get-config-store-option`
 * Find Config Store Option by name.
 * @param {string} params.configOptionId Name of the Config Store Option that you would like to find.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Config Store Option retrieved successfully. See {@link ConfigStoreOption}
 * @see 404 Config Store Option do not exist by that name. (code: `platform.config.store.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getConfigStoreOptionUrl(params: GetConfigStoreOptionUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/store/${params.configOptionId}`
  return `${getBasePath()}${path}`
}

export interface GetConfigStoreOptionUrlParams {
  configOptionId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/store/{config_option_id}
 * Operation: `create-config-store-option`
 * Inserts new Config Store Option.
 * @param {string} params.configOptionId Name of the Config Store Option that you would like to create.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ConfigStoreOptionData}
 * @see 201 The Config Store Option was inserted successfully. See {@link ConfigStoreOption}
 * @see 400 Config Store Option data already exists for the given name. (code: `platform.config.store.already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createConfigStoreOptionUrl(params: CreateConfigStoreOptionUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/store/${params.configOptionId}`
  return `${getBasePath()}${path}`
}

export interface CreateConfigStoreOptionUrlParams {
  configOptionId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/store/{config_option_id}
 * Operation: `put-config-store-option`
 * Update existing Config Store Option.
 * @param {string} params.configOptionId Name of the Config Store Option that you would like to modify.
 * @param {string} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ConfigStoreOptionData}
 * @see 200 Config Store Option retrieved successfully. See {@link ConfigStoreOption}
 * @see 404 There was no existing data for the given Config Store Id. (code: `platform.config.store.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `platform.config.store.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function putConfigStoreOptionUrl(params: PutConfigStoreOptionUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/store/${params.configOptionId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface PutConfigStoreOptionUrlParams {
  configOptionId: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/store/{config_option_id}
 * Operation: `delete-config-store-option`
 * Delete Config Store Option by name.
 * @param {string} params.configOptionId Name of the Config Store Option that you would like to delete.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Config Store Option deleted successfully
 * @see 404 Config Store Option do not exist by that name. (code: `platform.config.store.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteConfigStoreOptionUrl(params: DeleteConfigStoreOptionUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/store/${params.configOptionId}`
  return `${getBasePath()}${path}`
}

export interface DeleteConfigStoreOptionUrlParams {
  configOptionId: string
  regionId: string
}

/**
 * GET /api/v1/platform/configuration/templates/deployments/global
 * Operation: `get-global-deployment-templates`
 * Global deployment template endpoint which fetches the deployment templates across all region services.
 * @see 200 The deployment templates were returned successfully. See {@link Array<GlobalDeploymentTemplateInfo>}
 * @see 204 There are no deployment templates available.
 * @return {string} A fully-interpolated and escaped URL
 */
export function getGlobalDeploymentTemplatesUrl(): string {
  const path = `/platform/configuration/templates/deployments/global`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/trust-relationships
 * Operation: `get-trust-relationships`
 * List trust relationships.
 * @param {boolean} params.includeCertificate Whether to include the public CA certificates in the response.
 * @param {string} params.filter Which trust relationships to return in the response. Defaults to `all`. Default: all
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The list of trust relationships. See {@link TrustRelationshipsListResponse}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTrustRelationshipsUrl(params: GetTrustRelationshipsUrlParams): string {
  const queryString = buildQueryString({
    include_certificate:
      params.includeCertificate === undefined ? false : params.includeCertificate,
    filter: params.filter === undefined ? `all` : params.filter,
  })
  const path = encode`/${params.regionId}/platform/configuration/trust-relationships`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetTrustRelationshipsUrlParams {
  includeCertificate?: boolean | null
  filter?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/configuration/trust-relationships
 * Operation: `create-trust-relationship`
 * Creates a trust relationship.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link TrustRelationshipCreateRequest}
 * @see 201 The request was valid and a new trust relationship was created. See {@link TrustRelationshipCreateResponse}
 * @see 400 The trust relationship request had errors. See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createTrustRelationshipUrl(params: CreateTrustRelationshipUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/trust-relationships`
  return `${getBasePath()}${path}`
}

export interface CreateTrustRelationshipUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/configuration/trust-relationships/{trust_relationship_id}
 * Operation: `get-trust-relationship`
 * Retrieves information about a trust relationship.
 * @param {string} params.trustRelationshipId Identifier for the trust relationship.
 * @param {boolean} params.includeCertificate Whether to include the public CA certificates in the response.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The trust relationship info response. See {@link TrustRelationshipGetResponse}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The trust relationship specified by {trust_relationship_id} cannot be found. (code: `trust_relationships.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTrustRelationshipUrl(params: GetTrustRelationshipUrlParams): string {
  const queryString = buildQueryString({
    include_certificate:
      params.includeCertificate === undefined ? false : params.includeCertificate,
  })
  const path = encode`/${params.regionId}/platform/configuration/trust-relationships/${params.trustRelationshipId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetTrustRelationshipUrlParams {
  trustRelationshipId: string
  includeCertificate?: boolean | null
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/configuration/trust-relationships/{trust_relationship_id}
 * Operation: `update-trust-relationship`
 * Updates a trust relationship.
 * @param {string} params.trustRelationshipId Identifier for the trust relationship.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link TrustRelationshipUpdateRequest}
 * @see 200 The request was valid and the trust relationship was updated. See {@link TrustRelationshipUpdateResponse}
 * @see 400 The trust relationship request had errors. See {@link BasicFailedReply}
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The trust relationship specified by {trust_relationship_id} cannot be found. (code: `trust_relationships.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateTrustRelationshipUrl(params: UpdateTrustRelationshipUrlParams): string {
  const path = encode`/${params.regionId}/platform/configuration/trust-relationships/${params.trustRelationshipId}`
  return `${getBasePath()}${path}`
}

export interface UpdateTrustRelationshipUrlParams {
  trustRelationshipId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/configuration/trust-relationships/{trust_relationship_id}
 * Operation: `delete-trust-relationship`
 * Deletes a trust relationship.
 * @param {string} params.trustRelationshipId Identifier for the trust relationship.
 * @param {integer} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The request was valid and the trust relationship was deleted.
 * @see 401 You are not authorized to perform this action. See {@link BasicFailedReply}
 * @see 404 The trust relationship specified by {trust_relationship_id} cannot be found. (code: `trust_relationships.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteTrustRelationshipUrl(params: DeleteTrustRelationshipUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/configuration/trust-relationships/${params.trustRelationshipId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteTrustRelationshipUrlParams {
  trustRelationshipId: string
  version?: number | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/health
 * Operation: `list-platform-health`
 * Retrieves the platform health.
 * @param {string} params.expectStatus If specified, returns a 417 status code if the health status is not what was sent or better. Should be one of [red, yellow, or green].
 * @param {boolean} params.verbose When true, returns the list of all health check results.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The platform health. See {@link PlatformHealth}
 * @see 417 NOTE: When expect_status is sent, but the health status is _not_ better or equal to what was sent, the status code is 417. See {@link PlatformHealth}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listPlatformHealthUrl(params: ListPlatformHealthUrlParams): string {
  const queryString = buildQueryString({
    expect_status: params.expectStatus,
    verbose: params.verbose,
  })
  const path = encode`/${params.regionId}/platform/health`
  return `${getBasePath()}${path}${queryString}`
}

export interface ListPlatformHealthUrlParams {
  expectStatus?: string | null
  verbose?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/health/{health_check_name}
 * Operation: `get-platform-health`
 * Gets an individual platform health check by name.
 * @param {string} params.healthCheckName The name of the health check.
 * @param {string} params.expectStatus If specified, returns a 417 status code if the health status is not what was sent or better. Should be one of [red, yellow, or green].
 * @param {boolean} params.verbose When true, returns the list of all health check results.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The platform health check. See {@link PlatformHealth}
 * @see 404 The health check specified at {health_check_name} could not be found. (code: `health.health_check_not_found`). See {@link BasicFailedReply}
 * @see 417 NOTE: When expect_status is sent, but the health status is _not_ better or equal to what was sent, the status code is 417. See {@link PlatformHealth}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getPlatformHealthUrl(params: GetPlatformHealthUrlParams): string {
  const queryString = buildQueryString({
    expect_status: params.expectStatus,
    verbose: params.verbose,
  })
  const path = encode`/${params.regionId}/platform/health/${params.healthCheckName}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetPlatformHealthUrlParams {
  healthCheckName: string
  expectStatus?: string | null
  verbose?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/adminconsoles
 * Operation: `get-adminconsoles`
 * Retrieves an overview of all adminconsoles.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An overview of all adminconsoles. See {@link AdminconsolesOverview}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAdminconsolesUrl(params: GetAdminconsolesUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/adminconsoles`
  return `${getBasePath()}${path}`
}

export interface GetAdminconsolesUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/adminconsoles/_reindex
 * Operation: `reindex-adminconsoles`
 * Start reindex of all regional indices.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 Started reindexing of regional indices.
 * @return {string} A fully-interpolated and escaped URL
 */
export function reindexAdminconsolesUrl(params: ReindexAdminconsolesUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/adminconsoles/_reindex`
  return `${getBasePath()}${path}`
}

export interface ReindexAdminconsolesUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/adminconsoles/{adminconsole_id}/logging_settings
 * Operation: `get-adminconsole-logging-settings`
 * Get the logging settings for this adminconsole instance.
 * @param {string} params.adminconsoleId The identifier for the adminconsole instance.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The logging settings for the adminconsole instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this adminconsole were not found. (code: `adminconsoles.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAdminconsoleLoggingSettingsUrl(
  params: GetAdminconsoleLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/adminconsoles/${params.adminconsoleId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface GetAdminconsoleLoggingSettingsUrlParams {
  adminconsoleId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/adminconsoles/{adminconsole_id}/logging_settings
 * Operation: `set-adminconsole-logging-settings`
 * Set the logging settings for this adminconsole instance.
 * @param {string} params.adminconsoleId The identifier for the adminconsole instance.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LoggingSettings}
 * @see 200 The updated logging settings for the adminconsole instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this adminconsole were not found. (code: `adminconsoles.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setAdminconsoleLoggingSettingsUrl(
  params: SetAdminconsoleLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/adminconsoles/${params.adminconsoleId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface SetAdminconsoleLoggingSettingsUrlParams {
  adminconsoleId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/adminconsoles/{adminconsole_id}/logging_settings
 * Operation: `delete-adminconsole-logging-settings`
 * Reset the default logging settings for this adminconsole instance.
 * @param {string} params.adminconsoleId The identifier for the adminconsole instance.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The updated logging settings for the adminconsole instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this adminconsole were not found. (code: `adminconsoles.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteAdminconsoleLoggingSettingsUrl(
  params: DeleteAdminconsoleLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/adminconsoles/${params.adminconsoleId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface DeleteAdminconsoleLoggingSettingsUrlParams {
  adminconsoleId: string
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/platform/infrastructure/adminconsoles/{adminconsole_id}/logging_settings
 * Operation: `update-adminconsole-logging-settings`
 * All changes in the specified object are applied to the logging settings for this adminconsole instance according to the JSON Merge Patch processing rules. Omitting existing fields causes the same values to be reapplied. Specifying a `null` value reverts the field to the default value, or removes the field when no default value exists.
 * @param {string} params.adminconsoleId The identifier for the adminconsole instance.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 200 The updated logging settings for the adminconsole instance. See {@link LoggingSettings}
 * @see 400 The update request is invalid. (code: `patch.request_malformed`). See {@link BasicFailedReply}
 * @see 404 The logging settings for this adminconsole were not found. (code: `adminconsoles.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateAdminconsoleLoggingSettingsUrl(
  params: UpdateAdminconsoleLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/adminconsoles/${params.adminconsoleId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface UpdateAdminconsoleLoggingSettingsUrlParams {
  adminconsoleId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/allocators
 * Operation: `get-allocators`
 * Retrieves the information for all of the allocators in the platform.
 * @param {string} params.q (Optional) The query that filters the allocators. Maps to an Elasticsearch `query_string` query.
 * @param {integer} params.size (Optional) The maximum number of search results to return. Defaults to 100. Default: 100
 * @param {integer} params.from (Optional) The offset from the first result you want to fetch. Defaults to 0.
 * @param {string} params.sort (Optional) A comma-separated array of fields to sort the search results by. Defaults to `allocator_id`.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An overview of all allocators. See {@link AllocatorOverview}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAllocatorsUrl(params: GetAllocatorsUrlParams): string {
  const queryString = buildQueryString({
    q: params.q,
    size: params.size === undefined ? 100 : params.size,
    from: params.from === undefined ? 0 : params.from,
    sort: params.sort,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetAllocatorsUrlParams {
  q?: string | null
  size?: number | null
  from?: number | null
  sort?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/allocators/_resync
 * Operation: `resync-allocators`
 * Asynchronously synchronizes all allocator information in the back-end search index.
 * @param {boolean} params.skipMatchingVersion When true, skips the document indexing when the version matches the in-memory copy. Default: true
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 The ids of documents, organized by model version, that will be synchronized. See {@link ModelVersionIndexSynchronizationResults}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resyncAllocatorsUrl(params: ResyncAllocatorsUrlParams): string {
  const queryString = buildQueryString({
    skip_matching_version:
      params.skipMatchingVersion === undefined ? true : params.skipMatchingVersion,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/_resync`
  return `${getBasePath()}${path}${queryString}`
}

export interface ResyncAllocatorsUrlParams {
  skipMatchingVersion?: boolean | null
  regionId: string
}

/**
 * POST /api/v1(/regions/{region_id})?/platform/infrastructure/allocators/_search
 * Operation: `search-allocators`
 * Retrieves the information for all the allocators in the platform that match a specified query.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts an optional request payload of type {@link SearchRequest}
 * @see 200 An overview of allocators that matched the given search query. See {@link AllocatorOverview}
 * @return {string} A fully-interpolated and escaped URL
 */
export function searchAllocatorsUrl(params: SearchAllocatorsUrlParams = emptyParams): string {
  const path =
    (params.regionId == null ? '' : '/' + encodeURIComponent(params.regionId)) +
    `/platform/infrastructure/allocators/_search`
  return `${params.regionId == null ? getRawBasePath() : getBasePath()}${path}`
}

export interface SearchAllocatorsUrlParams {
  regionId?: string | null
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}
 * Operation: `get-allocator`
 * Retrieves the allocator by the ID.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The information for the allocator specified by {allocator_id}. See {@link AllocatorInfo}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAllocatorUrl(params: GetAllocatorUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}`
  return `${getBasePath()}${path}`
}

export interface GetAllocatorUrlParams {
  allocatorId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}
 * Operation: `delete-allocator`
 * Deletes the allocator.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {boolean} params.removeInstances Removes the instances from the deleted allocator.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The allocator specified by {allocator_id} was successfully deleted
 * @see 400 * The allocator specified by {allocator_id} could not be deleted. (code: `allocators.delete_connected_allocator_attempt`)
 * The allocator specified by {allocator_id} could not be deleted. (code: `allocators.delete_allocator_with_instances_attempt`). See {@link BasicFailedReply}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteAllocatorUrl(params: DeleteAllocatorUrlParams): string {
  const queryString = buildQueryString({
    remove_instances: params.removeInstances === undefined ? false : params.removeInstances,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteAllocatorUrlParams {
  allocatorId: string
  removeInstances?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/_resync
 * Operation: `resync-allocator`
 * Immediately synchronizes the allocator information in the back-end search index and caches.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The allocator resync operation executed successfully
 * @see 500 The allocator resync operation failed for allocator {allocator_id}. (code: `allocators.resync_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resyncAllocatorUrl(params: ResyncAllocatorUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/_resync`
  return `${getBasePath()}${path}`
}

export interface ResyncAllocatorUrlParams {
  allocatorId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/clusters/_move
 * Operation: `move-clusters`
 * Moves the clusters to a different allocator.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {boolean} params.forceUpdate When `true`, cancels and overwrites the pending plans, or treats the instance as an error.
 * @param {boolean} params.moveOnly When `true`, moves the specified instances and ignores the changes for the cluster state. Default: true
 * @param {boolean} params.forceMove When `true`, execute a primitive vacate by moving data at file-system level, and recreating instances on the target allocator(s).
 * @param {boolean} params.allocatorDown When `true`, considers all instances on the allocator as permanently shut down when deciding how to migrate data to new nodes.When left blank, the system automatically decides. NOTE: The default treats the allocator as up.
 * @param {boolean} params.validateOnly When `true`, validates the plan overrides, then returns the plan without performing the move.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts an optional request payload of type {@link MoveClustersRequest}
 * @see 202 The move command was issued successfully, use the "GET" command on each /{cluster_id} resource to monitor progress. See {@link MoveClustersCommandResponse}
 * @see 400 * The cluster definition contained errors. (code: `clusters.cluster_invalid_plan`)
 * The cluster definition contained errors. (code: `clusters.plan_feature_not_implemented`). See {@link BasicFailedReply}
 * @see 403 The move command was prohibited for the given cluster. (code: `clusters.command_prohibited`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function moveClustersUrl(params: MoveClustersUrlParams): string {
  const queryString = buildQueryString({
    force_update: params.forceUpdate === undefined ? false : params.forceUpdate,
    move_only: params.moveOnly === undefined ? true : params.moveOnly,
    force_move: params.forceMove === undefined ? false : params.forceMove,
    allocator_down: params.allocatorDown,
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/clusters/_move`
  return `${getBasePath()}${path}${queryString}`
}

export interface MoveClustersUrlParams {
  allocatorId: string
  forceUpdate?: boolean | null
  moveOnly?: boolean | null
  forceMove?: boolean | null
  allocatorDown?: boolean | null
  validateOnly?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/clusters/{cluster_type}/_move
 * Operation: `move-clusters-by-type`
 * Moves the clusters, by type, to a different allocator.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.clusterType The cluster types to move off of the allocator. NOTE: When unspecified, all clusters are moved.
 * @param {boolean} params.forceUpdate When true, cancels and overwrites pending plans, or treats instance as an error.
 * @param {boolean} params.moveOnly When true, bypasses the cluster state changes, but continues to move the specified instances. Default: true
 * @param {boolean} params.forceMove When `true`, execute a primitive vacate by moving data at file-system level, and recreating instances on the target allocator(s).
 * @param {boolean} params.allocatorDown When `true`, considers all instances on the allocator as permanently shut down when deciding how to migrate data to new nodes.When left blank, the system automatically decides. NOTE: The default treats the allocator as up.
 * @param {boolean} params.validateOnly When `true`, validates the plan overrides, then returns the plan without performing the move.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts an optional request payload of type {@link MoveClustersRequest}
 * @see 202 The move command was issued successfully, use the "GET" command on each /{cluster_id} resource to monitor progress. See {@link MoveClustersCommandResponse}
 * @see 400 * The cluster definition contained errors. (code: `clusters.cluster_invalid_plan`)
 * The cluster definition contained errors. (code: `clusters.plan_feature_not_implemented`). See {@link BasicFailedReply}
 * @see 403 The move command was prohibited for the given cluster. (code: `clusters.command_prohibited`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function moveClustersByTypeUrl(params: MoveClustersByTypeUrlParams): string {
  const queryString = buildQueryString({
    force_update: params.forceUpdate === undefined ? false : params.forceUpdate,
    move_only: params.moveOnly === undefined ? true : params.moveOnly,
    force_move: params.forceMove === undefined ? false : params.forceMove,
    allocator_down: params.allocatorDown,
    validate_only: params.validateOnly === undefined ? false : params.validateOnly,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/clusters/${params.clusterType}/_move`
  return `${getBasePath()}${path}${queryString}`
}

export interface MoveClustersByTypeUrlParams {
  allocatorId: string
  clusterType: string
  forceUpdate?: boolean | null
  moveOnly?: boolean | null
  forceMove?: boolean | null
  allocatorDown?: boolean | null
  validateOnly?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/logging_settings
 * Operation: `get-allocator-logging-settings`
 * Get the logging settings for this allocator instance.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The logging settings for the allocator instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this allocator were not found. (code: `allocators.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAllocatorLoggingSettingsUrl(
  params: GetAllocatorLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface GetAllocatorLoggingSettingsUrlParams {
  allocatorId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/logging_settings
 * Operation: `set-allocator-logging-settings`
 * Set the logging settings for this allocator instance.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LoggingSettings}
 * @see 200 The updated logging settings for the allocator instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this allocator were not found. (code: `allocators.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setAllocatorLoggingSettingsUrl(
  params: SetAllocatorLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface SetAllocatorLoggingSettingsUrlParams {
  allocatorId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/logging_settings
 * Operation: `delete-allocator-logging-settings`
 * Reset the default logging settings for this allocator instance.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The updated logging settings for the allocator instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this allocator were not found. (code: `allocators.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteAllocatorLoggingSettingsUrl(
  params: DeleteAllocatorLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface DeleteAllocatorLoggingSettingsUrlParams {
  allocatorId: string
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/logging_settings
 * Operation: `update-allocator-logging-settings`
 * All changes in the specified object are applied to the logging settings for this allocator instance according to the JSON Merge Patch processing rules. Omitting existing fields causes the same values to be reapplied. Specifying a `null` value reverts the field to the default value, or removes the field when no default value exists.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 200 The updated logging settings for the allocator instance. See {@link LoggingSettings}
 * @see 400 The update request is invalid. (code: `patch.request_malformed`). See {@link BasicFailedReply}
 * @see 404 The logging settings for this allocator were not found. (code: `allocators.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateAllocatorLoggingSettingsUrl(
  params: UpdateAllocatorLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface UpdateAllocatorLoggingSettingsUrlParams {
  allocatorId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/maintenance-mode/_start
 * Operation: `start-allocator-maintenance-mode`
 * Starts maintenance mode on the allocator.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.reason Provides a reason for changing the maintenance mode which will appear as a message on the allocator.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 The start maintenance mode command was issued successfully
 * @see 403 The start maintenance mode command was prohibited for the given allocator. (code: `root.unauthorized.rbac`). See {@link BasicFailedReply}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function startAllocatorMaintenanceModeUrl(
  params: StartAllocatorMaintenanceModeUrlParams,
): string {
  const queryString = buildQueryString({
    reason: params.reason,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/maintenance-mode/_start`
  return `${getBasePath()}${path}${queryString}`
}

export interface StartAllocatorMaintenanceModeUrlParams {
  allocatorId: string
  reason?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/maintenance-mode/_stop
 * Operation: `stop-allocator-maintenance-mode`
 * Stops maintenance mode on the allocator.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.reason Provides a reason for changing the maintenance mode which will appear as a message on the allocator.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 The stop maintenance mode command was issued successfully
 * @see 403 The stop maintenance mode command was prohibited for the given allocator. (code: `root.unauthorized.rbac`). See {@link BasicFailedReply}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function stopAllocatorMaintenanceModeUrl(
  params: StopAllocatorMaintenanceModeUrlParams,
): string {
  const queryString = buildQueryString({
    reason: params.reason,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/maintenance-mode/_stop`
  return `${getBasePath()}${path}${queryString}`
}

export interface StopAllocatorMaintenanceModeUrlParams {
  allocatorId: string
  reason?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/metadata
 * Operation: `get-allocator-metadata`
 * Retrieves the allocator metadata.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The allocator metadata was successfully returned. See {@link Array<MetadataItem>}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAllocatorMetadataUrl(params: GetAllocatorMetadataUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/metadata`
  return `${getBasePath()}${path}`
}

export interface GetAllocatorMetadataUrlParams {
  allocatorId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/metadata
 * Operation: `set-allocator-metadata`
 * Sets the allocator metadata.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.version Checks for conflicts against the metadata version, then returns the value in the `x-cloud-resource-version` header.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link MetadataItems}
 * @see 200 The allocator metadata was successfully changed (the updated JSON is returned). See {@link Array<MetadataItem>}
 * @see 400 The value specified is empty for at least one of the metadata tags. (code: `allocators.invalid_empty_metadata_items`). See {@link BasicFailedReply}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setAllocatorMetadataUrl(params: SetAllocatorMetadataUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/metadata`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetAllocatorMetadataUrlParams {
  allocatorId: string
  version?: string | null
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/metadata/{key}
 * Operation: `set-allocator-metadata-item`
 * Adds or updates a single item in the allocator metadata.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.key The metadata item key.
 * @param {string} params.version Checks for conflicts against the metadata version, then returns the value in the `x-cloud-resource-version` header.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link MetadataItemValue}
 * @see 200 The allocator metadata was successfully changed (the updated JSON is returned). See {@link Array<MetadataItem>}
 * @see 400 The value specified for the metadata tag is empty. (code: `allocators.invalid_empty_metadata_item`). See {@link BasicFailedReply}
 * @see 404 * The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`)
 * The metadata item specified by {key} cannot be found. (code: `allocators.metadata_item_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setAllocatorMetadataItemUrl(params: SetAllocatorMetadataItemUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/metadata/${params.key}`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetAllocatorMetadataItemUrlParams {
  allocatorId: string
  key: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/metadata/{key}
 * Operation: `delete-allocator-metadata-item`
 * Removes a single item from the allocator metadata.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.key The metadata item key.
 * @param {string} params.version Checks for conflicts against the metadata version, then returns the value in the `x-cloud-resource-version` header.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The allocator metadata was successfully changed (the updated JSON is returned). See {@link Array<MetadataItem>}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteAllocatorMetadataItemUrl(
  params: DeleteAllocatorMetadataItemUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/metadata/${params.key}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteAllocatorMetadataItemUrlParams {
  allocatorId: string
  key: string
  version?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/settings
 * Operation: `get-allocator-settings`
 * Retrieves the allocator override settings.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Returns the settings for the specified Allocator. See {@link AllocatorSettings}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAllocatorSettingsUrl(params: GetAllocatorSettingsUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/settings`
  return `${getBasePath()}${path}`
}

export interface GetAllocatorSettingsUrlParams {
  allocatorId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/settings
 * Operation: `set-allocator-settings`
 * Overwrites the allocator settings with the specified settings. WARNING: Any unspecified fields are deleted.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.version Checks for conflicts against the metadata version, then returns the value in the `x-cloud-resource-version` header.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link AllocatorSettings}
 * @see 200 Returns the updated settings for the specified allocator. See {@link AllocatorSettings}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setAllocatorSettingsUrl(params: SetAllocatorSettingsUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/settings`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetAllocatorSettingsUrlParams {
  allocatorId: string
  version?: string | null
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/platform/infrastructure/allocators/{allocator_id}/settings
 * Operation: `update-allocator-settings`
 * Applies the settings as a patch. Only the fields that are referenced in the update are altered.
 * @param {string} params.allocatorId The allocator identifier.
 * @param {string} params.version Checks for conflicts against the metadata version, then returns the value in the `x-cloud-resource-version` header.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 200 Returns the updated settings for the specified allocator. See {@link AllocatorSettings}
 * @see 404 The allocator specified by {allocator_id} cannot be found. (code: `allocators.allocator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateAllocatorSettingsUrl(params: UpdateAllocatorSettingsUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/allocators/${params.allocatorId}/settings`
  return `${getBasePath()}${path}${queryString}`
}

export interface UpdateAllocatorSettingsUrlParams {
  allocatorId: string
  version?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/blueprinter/roles
 * Operation: `list-blueprinter-roles`
 * Retrieve all persisted roles.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The role aggregates. See {@link RoleAggregates}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listBlueprinterRolesUrl(params: ListBlueprinterRolesUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/blueprinter/roles`
  return `${getBasePath()}${path}`
}

export interface ListBlueprinterRolesUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/blueprinter/roles
 * Operation: `create-blueprinter-role`
 * Create a Blueprinter role.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link RoleAggregateCreateData}
 * @see 201 The role aggregate that was just created. See {@link RoleAggregate}
 * @see 409 The role ID you specified is already in use. (code: `roles.already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createBlueprinterRoleUrl(params: CreateBlueprinterRoleUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/blueprinter/roles`
  return `${getBasePath()}${path}`
}

export interface CreateBlueprinterRoleUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/blueprinter/roles/{blueprinter_role_id}
 * Operation: `get-blueprinter-role`
 * Retrieve a role.
 * @param {string} params.blueprinterRoleId User-specified Blueprinter role ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The role aggregate definition. See {@link RoleAggregate}
 * @see 404 The role can't be found. (code: `roles.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getBlueprinterRoleUrl(params: GetBlueprinterRoleUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/blueprinter/roles/${params.blueprinterRoleId}`
  return `${getBasePath()}${path}`
}

export interface GetBlueprinterRoleUrlParams {
  blueprinterRoleId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/blueprinter/roles/{blueprinter_role_id}
 * Operation: `update-blueprinter-role`
 * Update a role.
 * @param {string} params.blueprinterRoleId User-specified Blueprinter role ID.
 * @param {integer} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link Role}
 * @see 200 The role was successfully updated. See {@link RoleAggregate}
 * @see 400 The role is currently running container sets. (code: `roles.in_use`). See {@link BasicFailedReply}
 * @see 404 The role can't be found. (code: `roles.not_found`). See {@link BasicFailedReply}
 * @see 409 Your request failed because the specified version does not match the persisted version. (code: `roles.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateBlueprinterRoleUrl(params: UpdateBlueprinterRoleUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/blueprinter/roles/${params.blueprinterRoleId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface UpdateBlueprinterRoleUrlParams {
  blueprinterRoleId: string
  version?: number | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/blueprinter/roles/{blueprinter_role_id}
 * Operation: `delete-blueprinter-role`
 * Delete a role.
 * @param {string} params.blueprinterRoleId User-specified Blueprinter role ID.
 * @param {boolean} params.skipValidations When sent as true, ignores validation errors.
 * @param {integer} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The role was successfully deleted.
 * @see 400 The role is currently running container sets. (code: `roles.in_use`). See {@link BasicFailedReply}
 * @see 404 The role can't be found. (code: `roles.not_found`). See {@link BasicFailedReply}
 * @see 409 Your request failed because the specified version does not match the persisted version. (code: `roles.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteBlueprinterRoleUrl(params: DeleteBlueprinterRoleUrlParams): string {
  const queryString = buildQueryString({
    skip_validations: params.skipValidations === undefined ? false : params.skipValidations,
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/blueprinter/roles/${params.blueprinterRoleId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteBlueprinterRoleUrlParams {
  blueprinterRoleId: string
  skipValidations?: boolean | null
  version?: number | null
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/blueprinter/roles/{blueprinter_role_id}/blessings
 * Operation: `set-blueprinter-blessings`
 * Set blessings for a role.
 * @param {string} params.blueprinterRoleId User-specified Blueprinter role ID.
 * @param {integer} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link Blessings}
 * @see 200 The blessings were successfully set. See {@link RoleAggregate}
 * @see 404 The role can't be found. (code: `roles.not_found`). See {@link BasicFailedReply}
 * @see 409 Your request failed because the specified version does not match the persisted version. (code: `roles.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setBlueprinterBlessingsUrl(params: SetBlueprinterBlessingsUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/blueprinter/roles/${params.blueprinterRoleId}/blessings`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetBlueprinterBlessingsUrlParams {
  blueprinterRoleId: string
  version?: number | null
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/blueprinter/roles/{blueprinter_role_id}/blessings/{runner_id}
 * Operation: `add-blueprinter-blessing`
 * Add a Blessing for a runner to a role.
 * @param {string} params.blueprinterRoleId User-specified Blueprinter role ID.
 * @param {string} params.runnerId Runner ID for a blessing associated with a role.
 * @param {integer} params.version When specified, checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link Blessing}
 * @see 200 Blessing added successfully. See {@link RoleAggregate}
 * @see 404 The role can't be found. (code: `roles.not_found`). See {@link BasicFailedReply}
 * @see 409 Your request failed because the specified version does not match the persisted version. (code: `roles.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function addBlueprinterBlessingUrl(params: AddBlueprinterBlessingUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/blueprinter/roles/${params.blueprinterRoleId}/blessings/${params.runnerId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface AddBlueprinterBlessingUrlParams {
  blueprinterRoleId: string
  runnerId: string
  version?: number | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/constructors
 * Operation: `get-constructors`
 * Retrieves an overview of all constructors in an ECE installation.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An overview of all constructors. See {@link ConstructorOverview}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getConstructorsUrl(params: GetConstructorsUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/constructors`
  return `${getBasePath()}${path}`
}

export interface GetConstructorsUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/constructors/_resync
 * Operation: `resync-constructors`
 * Asynchronously synchronizes all constructor information in the back-end search index.
 * @param {boolean} params.skipMatchingVersion When true, skips the document indexing when the version matches the in-memory copy. Default: true
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 The ids of documents, organized by model version, that will be synchronized. See {@link ModelVersionIndexSynchronizationResults}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resyncConstructorsUrl(params: ResyncConstructorsUrlParams): string {
  const queryString = buildQueryString({
    skip_matching_version:
      params.skipMatchingVersion === undefined ? true : params.skipMatchingVersion,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/constructors/_resync`
  return `${getBasePath()}${path}${queryString}`
}

export interface ResyncConstructorsUrlParams {
  skipMatchingVersion?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/constructors/{constructor_id}
 * Operation: `get-constructor`
 * Retrieves a constructor by id.
 * @param {string} params.constructorId Identifier for the constructor.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The information for the constructor specified by {constructor_id}. See {@link ConstructorInfo}
 * @see 404 The constructor specified by {constructor_id} cannot be found. (code: `constructors.constructor_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getConstructorUrl(params: GetConstructorUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/constructors/${params.constructorId}`
  return `${getBasePath()}${path}`
}

export interface GetConstructorUrlParams {
  constructorId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/constructors/{constructor_id}/_resync
 * Operation: `resync-constructor`
 * Immediately resynchronizes the search index and cache for the selected constructor.
 * @param {string} params.constructorId Identifier for the constructor.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The constructor resync operation executed successfully
 * @see 500 The constructor resync operation failed for allocator {constructor_id}. (code: `constructors.resync_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resyncConstructorUrl(params: ResyncConstructorUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/constructors/${params.constructorId}/_resync`
  return `${getBasePath()}${path}`
}

export interface ResyncConstructorUrlParams {
  constructorId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/constructors/{constructor_id}/logging_settings
 * Operation: `get-constructor-logging-settings`
 * Get the logging settings for this constructor instance.
 * @param {string} params.constructorId Identifier for the constructor.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The logging settings for the constructor instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this constructor were not found. (code: `constructors.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getConstructorLoggingSettingsUrl(
  params: GetConstructorLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/constructors/${params.constructorId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface GetConstructorLoggingSettingsUrlParams {
  constructorId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/constructors/{constructor_id}/logging_settings
 * Operation: `set-constructor-logging-settings`
 * Set the logging settings for this constructor instance.
 * @param {string} params.constructorId Identifier for the constructor.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LoggingSettings}
 * @see 200 The updated logging settings for the constructor instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this constructor were not found. (code: `constructors.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setConstructorLoggingSettingsUrl(
  params: SetConstructorLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/constructors/${params.constructorId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface SetConstructorLoggingSettingsUrlParams {
  constructorId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/constructors/{constructor_id}/logging_settings
 * Operation: `delete-constructor-logging-settings`
 * Reset the default logging settings for this constructor instance.
 * @param {string} params.constructorId Identifier for the constructor.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The updated logging settings for the constructor instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this constructor were not found. (code: `constructors.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteConstructorLoggingSettingsUrl(
  params: DeleteConstructorLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/constructors/${params.constructorId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface DeleteConstructorLoggingSettingsUrlParams {
  constructorId: string
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/platform/infrastructure/constructors/{constructor_id}/logging_settings
 * Operation: `update-constructor-logging-settings`
 * All changes in the specified object are applied to the logging settings for this constructor instance according to the JSON Merge Patch processing rules. Omitting existing fields causes the same values to be reapplied. Specifying a `null` value reverts the field to the default value, or removes the field when no default value exists.
 * @param {string} params.constructorId Identifier for the constructor.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 200 The updated logging settings for the constructor instance. See {@link LoggingSettings}
 * @see 400 The update request is invalid. (code: `patch.request_malformed`). See {@link BasicFailedReply}
 * @see 404 The logging settings for this constructor were not found. (code: `constructors.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateConstructorLoggingSettingsUrl(
  params: UpdateConstructorLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/constructors/${params.constructorId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface UpdateConstructorLoggingSettingsUrlParams {
  constructorId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/constructors/{constructor_id}/maintenance-mode/_start
 * Operation: `start-constructor-maintenance-mode`
 * Start maintenance mode of a constructor. It will stop constructing new plans while continuing with on-going ones.
 * @param {string} params.constructorId Identifier for the constructor.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 The start maintenance mode command was issued successfully
 * @see 403 The start maintenance mode command was prohibited for the given constructor. (code: `constructors.command_prohibited`). See {@link BasicFailedReply}
 * @see 404 The constructor specified by {constructor_id} cannot be found. (code: `constructors.constructor_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function startConstructorMaintenanceModeUrl(
  params: StartConstructorMaintenanceModeUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/constructors/${params.constructorId}/maintenance-mode/_start`
  return `${getBasePath()}${path}`
}

export interface StartConstructorMaintenanceModeUrlParams {
  constructorId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/constructors/{constructor_id}/maintenance-mode/_stop
 * Operation: `stop-constructor-maintenance-mode`
 * Stop maintenance mode of a constructor. It will resume the construction of submitted plans.
 * @param {string} params.constructorId Identifier for the constructor.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 The stop maintenance mode command was issued successfully
 * @see 403 The stop maintenance mode command was prohibited for the given constructor. (code: `constructors.command_prohibited`). See {@link BasicFailedReply}
 * @see 404 The constructor specified by {constructor_id} cannot be found. (code: `constructors.constructor_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function stopConstructorMaintenanceModeUrl(
  params: StopConstructorMaintenanceModeUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/constructors/${params.constructorId}/maintenance-mode/_stop`
  return `${getBasePath()}${path}`
}

export interface StopConstructorMaintenanceModeUrlParams {
  constructorId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/container-sets
 * Operation: `get-container-sets`
 * Retrieves an overview of all container sets.
 * @param {boolean} params.hidden Include hidden container sets in output.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An overview of all container sets. See {@link ContainerSetsOverview}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getContainerSetsUrl(params: GetContainerSetsUrlParams): string {
  const queryString = buildQueryString({
    hidden: params.hidden === undefined ? false : params.hidden,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/container-sets`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetContainerSetsUrlParams {
  hidden?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/container-sets
 * Operation: `create-container-set`
 * Creates a new container set, with optional id.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts an optional request payload of type {@link ContainerSet}
 * @see 200 The container set was created. See {@link ContainerSet}
 * @see 400 The container set create failed. (code: `container_sets.create_failed`). See {@link BasicFailedReply}
 * @see 409 Container Set name already in use. (code: `container_sets.name_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createContainerSetUrl(params: CreateContainerSetUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/container-sets`
  return `${getBasePath()}${path}`
}

export interface CreateContainerSetUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/container-sets/{containerset_id}
 * Operation: `get-container-set`
 * Retrieves a container set by id.
 * @param {string} params.containersetId Identifier for the container set.
 * @param {string} params.include Comma delimited list of properties to include in the output.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The information for the container set specified by {containerset_id}. See {@link ContainerSetDetails}
 * @see 404 The container set specified by {containerset_id} cannot be found. (code: `container_sets.container_set_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getContainerSetUrl(params: GetContainerSetUrlParams): string {
  const queryString = buildQueryString({
    include: params.include,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/container-sets/${params.containersetId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetContainerSetUrlParams {
  containersetId: string
  include?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/container-sets/{containerset_id}
 * Operation: `delete-container-set`
 * Deletes a container set.
 * @param {string} params.containersetId Identifier for the container set.
 * @param {string} params.version The container set version.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The container set was deleted
 * @see 400 The container set delete failed. (code: `container_sets.delete_failed`). See {@link BasicFailedReply}
 * @see 404 The container set specified by {containerset_id} cannot be found. (code: `container_sets.container_set_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteContainerSetUrl(params: DeleteContainerSetUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/container-sets/${params.containersetId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteContainerSetUrlParams {
  containersetId: string
  version?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/container-sets/{containerset_id}/containers
 * Operation: `get-containers`
 * Lists containers by container-set id.
 * @param {string} params.containersetId Identifier for the container set.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The overview of containers belonging to the container-set specified by {container_set_id}. See {@link ContainersOverview}
 * @see 404 The container set cannot be found. (code: `container_sets.container_set_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getContainersUrl(params: GetContainersUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/container-sets/${params.containersetId}/containers`
  return `${getBasePath()}${path}`
}

export interface GetContainersUrlParams {
  containersetId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/container-sets/{containerset_id}/containers
 * Operation: `create-container`
 * Creates a new container.
 * @param {string} params.containersetId Identifier for the container set.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link Container}
 * @see 200 The container was created. See {@link Container}
 * @see 400 The container create failed. (code: `container_sets.container_create_failed`). See {@link BasicFailedReply}
 * @see 404 The container set cannot be found. (code: `container_sets.container_set_not_found`). See {@link BasicFailedReply}
 * @see 409 Container name already in use. (code: `container_sets.container_name_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createContainerUrl(params: CreateContainerUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/container-sets/${params.containersetId}/containers`
  return `${getBasePath()}${path}`
}

export interface CreateContainerUrlParams {
  containersetId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/container-sets/{containerset_id}/containers/{container_id}
 * Operation: `get-container`
 * Retrieves a container by id.
 * @param {string} params.containersetId Identifier for the container set.
 * @param {string} params.containerId Identifier for the container.
 * @param {string} params.include Comma delimited list of properties to include in the output. Currently, only 'inspect' is supported for containers.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The information for the container specified by {container_id}. See {@link Container}
 * @see 404 * The container set cannot be found. (code: `container_sets.container_set_not_found`)
 * The container cannot be found. (code: `container_sets.container_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getContainerUrl(params: GetContainerUrlParams): string {
  const queryString = buildQueryString({
    include: params.include,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/container-sets/${params.containersetId}/containers/${params.containerId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetContainerUrlParams {
  containersetId: string
  containerId: string
  include?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/container-sets/{containerset_id}/containers/{container_id}
 * Operation: `delete-container`
 * Deletes a container.
 * @param {string} params.containersetId Identifier for the container set.
 * @param {string} params.containerId Identifier for the container.
 * @param {string} params.version The container version.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The container was deleted
 * @see 400 The container delete failed. (code: `container_sets.container_delete_failed`). See {@link BasicFailedReply}
 * @see 404 * The container set cannot be found. (code: `container_sets.container_set_not_found`)
 * The container cannot be found. (code: `container_sets.container_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteContainerUrl(params: DeleteContainerUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/container-sets/${params.containersetId}/containers/${params.containerId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteContainerUrlParams {
  containersetId: string
  containerId: string
  version?: string | null
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/platform/infrastructure/container-sets/{containerset_id}/containers/{container_id}
 * Operation: `patch-container`
 * All changes in the specified object are applied to an existing container according to the JSON Merge Patch processing rules. Omitting existing fields causes the same values to be reapplied. Specifying a `null` value reverts the field to the default value, or removes the field when no default value exists.
 * @param {string} params.containersetId Identifier for the container set.
 * @param {string} params.containerId Identifier for the container.
 * @param {string} params.version The container version.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 200 The container was updated. See {@link Container}
 * @see 400 The container update failed. (code: `container_sets.container_update_failed`). See {@link BasicFailedReply}
 * @see 404 * The container set cannot be found. (code: `container_sets.container_set_not_found`)
 * The container cannot be found. (code: `container_sets.container_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function patchContainerUrl(params: PatchContainerUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/container-sets/${params.containersetId}/containers/${params.containerId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface PatchContainerUrlParams {
  containersetId: string
  containerId: string
  version?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/coordinators
 * Operation: `get-coordinators`
 * Retrieves an overview of all coordinators.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An overview of all coordinators. See {@link CoordinatorsSummary}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCoordinatorsUrl(params: GetCoordinatorsUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/coordinators`
  return `${getBasePath()}${path}`
}

export interface GetCoordinatorsUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/coordinators/candidates
 * Operation: `get-coordinator-candidates`
 * Retrieve a list of all coordinator candidates.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 A list of all coordinator candidates. See {@link CoordinatorCandidatesSummary}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCoordinatorCandidatesUrl(params: GetCoordinatorCandidatesUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/coordinators/candidates`
  return `${getBasePath()}${path}`
}

export interface GetCoordinatorCandidatesUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/coordinators/candidates/{coordinator_candidate_id}
 * Operation: `get-coordinator-candidate`
 * Retrieve a coordinator candidate.
 * @param {string} params.coordinatorCandidateId The identifier for the coordinator candidate.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Info about a coordinator candidate. See {@link CoordinatorCandidateInfo}
 * @see 404 Unable to find coordinator candidate {coordinator_candidate_id}. Edit your request, then try again. (code: `coordinators.candidate_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCoordinatorCandidateUrl(params: GetCoordinatorCandidateUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/coordinators/candidates/${params.coordinatorCandidateId}`
  return `${getBasePath()}${path}`
}

export interface GetCoordinatorCandidateUrlParams {
  coordinatorCandidateId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/coordinators/candidates/{coordinator_candidate_id}
 * Operation: `delete-coordinator-candidate`
 * Delete a coordinator candidate by id.
 * @param {string} params.coordinatorCandidateId The identifier for the coordinator candidate.
 * @param {string} params.version cloud resource version.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The coordinator candidate specified by {coordinator_candidate_id} was deleted.
 * @see 400 The coordinator specified by {coordinator_candidate_id} could not be deleted. (code: `coordinators.candidate_deletion_failed`). See {@link BasicFailedReply}
 * @see 404 The coordinator candidate specified by {coordinator_candidate_id} cannot be found. (code: `coordinators.candidate_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteCoordinatorCandidateUrl(params: DeleteCoordinatorCandidateUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/coordinators/candidates/${params.coordinatorCandidateId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteCoordinatorCandidateUrlParams {
  coordinatorCandidateId: string
  version?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/coordinators/candidates/{coordinator_candidate_id}/_promote
 * Operation: `promote-coordinator-candidate`
 * Promotes a coordinator candidate.
 * @param {string} params.coordinatorCandidateId The identifier for the coordinator candidate.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 Accepted promote of coordinator candidate. See {@link CoordinatorCandidateInfo}
 * @see 404 Unable to find coordinator candidate {coordinator_id}. Edit your request, then try again. (code: `coordinators.candidate_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function promoteCoordinatorCandidateUrl(
  params: PromoteCoordinatorCandidateUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/coordinators/candidates/${params.coordinatorCandidateId}/_promote`
  return `${getBasePath()}${path}`
}

export interface PromoteCoordinatorCandidateUrlParams {
  coordinatorCandidateId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/coordinators/{coordinator_id}
 * Operation: `get-coordinator`
 * Retrieve overview of a single coordinator.
 * @param {string} params.coordinatorId The identifier for the coordinator.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An overview of a coordinator instance. See {@link CoordinatorSummary}
 * @see 404 Unable to find coordinator {coordinator_id}. Edit your request, then try again. (code: `coordinators.coordinator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCoordinatorUrl(params: GetCoordinatorUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/coordinators/${params.coordinatorId}`
  return `${getBasePath()}${path}`
}

export interface GetCoordinatorUrlParams {
  coordinatorId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/coordinators/{coordinator_id}/_demote
 * Operation: `demote-coordinator`
 * Demotes a coordinator.
 * @param {string} params.coordinatorId The identifier for the coordinator.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Accepted demote of coordinator.
 * @see 400 The coordinator specified by {coordinator_id} could not be demoted. (code: `coordinators.coordinator_demote_failed`). See {@link BasicFailedReply}
 * @see 404 Unable to find the coordinator {coordinator_id}. Edit your request, then try again. (code: `coordinators.coordinator_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function demoteCoordinatorUrl(params: DemoteCoordinatorUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/coordinators/${params.coordinatorId}/_demote`
  return `${getBasePath()}${path}`
}

export interface DemoteCoordinatorUrlParams {
  coordinatorId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/proxies
 * Operation: `get-proxies`
 * Retrieves an overview of all proxies.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An overview of all proxies. See {@link ProxyOverview}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getProxiesUrl(params: GetProxiesUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/proxies`
  return `${getBasePath()}${path}`
}

export interface GetProxiesUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/proxies/filtered-groups
 * Operation: `create-proxies-filtered-group`
 * Create the settings for a filtered group of proxies.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ProxiesFilteredGroup}
 * @see 200 Returns the created or updated filtered group of proxies. See {@link ProxiesFilteredGroup}
 * @see 400 * The filtered group of proxies has empty id. (code: `proxies.proxies_filtered_group_empty_id`)
 * A filtered group of proxies with the same identifier already exists. (code: `proxies.proxies_filtered_group_already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createProxiesFilteredGroupUrl(params: CreateProxiesFilteredGroupUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/filtered-groups`
  return `${getBasePath()}${path}`
}

export interface CreateProxiesFilteredGroupUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/proxies/filtered-groups/{proxies_filtered_group_id}
 * Operation: `get-proxies-filtered-group`
 * Get data for a filtered group of proxies.
 * @param {string} params.proxiesFilteredGroupId "The identifier for the filtered group of proxies.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Data for the filtered group of proxies identified by {proxies_filtered_group_id}. See {@link ProxiesFilteredGroup}
 * @see 404 Unable to find the {proxies_filtered_group_id} specified filtered group of proxies. Edit your request, then try again. (code: `proxies.proxies_filtered_group_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getProxiesFilteredGroupUrl(params: GetProxiesFilteredGroupUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/filtered-groups/${params.proxiesFilteredGroupId}`
  return `${getBasePath()}${path}`
}

export interface GetProxiesFilteredGroupUrlParams {
  proxiesFilteredGroupId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/proxies/filtered-groups/{proxies_filtered_group_id}
 * Operation: `update-proxies-filtered-group`
 * Update the settings for a filtered group of proxies.
 * @param {string} params.proxiesFilteredGroupId "The identifier for the filtered group of proxies.
 * @param {string} params.version Checks for conflicts against the metadata version, then returns the value in the `x-cloud-resource-version` header.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ProxiesFilteredGroup}
 * @see 200 Returns the created or updated filtered group of proxies. See {@link ProxiesFilteredGroup}
 * @see 400 The provided identifier doesn't match the identifier in the object. (code: `proxies.proxies_filtered_group_id_conflict`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `proxies.proxies_filtered_group_version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateProxiesFilteredGroupUrl(params: UpdateProxiesFilteredGroupUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/filtered-groups/${params.proxiesFilteredGroupId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface UpdateProxiesFilteredGroupUrlParams {
  proxiesFilteredGroupId: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/proxies/filtered-groups/{proxies_filtered_group_id}
 * Operation: `delete-proxies-filtered-group`
 * Deletes a filtered group of proxies by ID.
 * @param {string} params.proxiesFilteredGroupId "The identifier for the filtered group of proxies.
 * @param {string} params.version Checks for conflicts against the metadata version, then returns the value in the `x-cloud-resource-version` header.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The filtered group of proxies was successfully deleted
 * @see 404 Unable to find the {proxies_filtered_group_id} specified filtered group of proxies. Edit your request, then try again. (code: `proxies.proxies_filtered_group_not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `proxies.proxies_filtered_group_version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteProxiesFilteredGroupUrl(params: DeleteProxiesFilteredGroupUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/filtered-groups/${params.proxiesFilteredGroupId}`
  return `${getBasePath()}${path}${queryString}`
}

export interface DeleteProxiesFilteredGroupUrlParams {
  proxiesFilteredGroupId: string
  version?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/proxies/filtered-groups/{proxies_filtered_group_id}/health
 * Operation: `get-proxies-filtered-group-health`
 * Get the health status of a filtered group of proxies.
 * @param {string} params.proxiesFilteredGroupId "The identifier for the filtered group of proxies.
 * @param {string} params.expectStatus The expected status.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Returns health information on a filtered group of proxies. See {@link ProxiesFilteredGroupHealth}
 * @see 417 The health status is worse than the expected one. See {@link ProxiesFilteredGroupHealth}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getProxiesFilteredGroupHealthUrl(
  params: GetProxiesFilteredGroupHealthUrlParams,
): string {
  const queryString = buildQueryString({
    expect_status: params.expectStatus,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/filtered-groups/${params.proxiesFilteredGroupId}/health`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetProxiesFilteredGroupHealthUrlParams {
  proxiesFilteredGroupId: string
  expectStatus?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/proxies/health
 * Operation: `get-proxies-health`
 * Get the health status of all proxies.
 * @param {string} params.expectStatus The expected status.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Returns health information on all the proxies. See {@link ProxiesHealth}
 * @see 417 The health status is worse than the expected one. See {@link ProxiesHealth}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getProxiesHealthUrl(params: GetProxiesHealthUrlParams): string {
  const queryString = buildQueryString({
    expect_status: params.expectStatus,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/health`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetProxiesHealthUrlParams {
  expectStatus?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/proxies/settings
 * Operation: `get-proxies-settings`
 * > WARNING > This endpoint is deprecated and scheduled to be removed in the next major version.  Retrieves the settings for all proxies.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Settings for all proxies. See {@link ProxiesSettings}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getProxiesSettingsUrl(params: GetProxiesSettingsUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/settings`
  return `${getBasePath()}${path}`
}

export interface GetProxiesSettingsUrlParams {
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/proxies/settings
 * Operation: `set-proxies-settings`
 * > WARNING > This endpoint is deprecated and scheduled to be removed in the next major version.  Overwrites the proxy settings. All unspecified fields are deleted.
 * @param {string} params.version If specified, checks for conflicts against the version of the settings (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link ProxiesSettings}
 * @see 200 Returns the updated settings. See {@link ProxiesSettings}
 * @see 409 There is a version conflict. (code: `proxies.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setProxiesSettingsUrl(params: SetProxiesSettingsUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/settings`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetProxiesSettingsUrlParams {
  version?: string | null
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/platform/infrastructure/proxies/settings
 * Operation: `update-proxies-settings`
 * > WARNING > This endpoint is deprecated and scheduled to be removed in the next major version.  Applies the settings as a patch. Only the fields that are referenced in the update are changed.
 * @param {string} params.version If specified, checks for conflicts against the version of the repository configuration (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 200 Returns the updated settings. See {@link ProxiesSettings}
 * @see 409 There is a version conflict. (code: `proxies.version_conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateProxiesSettingsUrl(params: UpdateProxiesSettingsUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/settings`
  return `${getBasePath()}${path}${queryString}`
}

export interface UpdateProxiesSettingsUrlParams {
  version?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/proxies/{proxy_id}
 * Operation: `get-proxy`
 * Retrieves a single proxy by ID.
 * @param {string} params.proxyId The identifier for the proxy.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The information for the proxy specified by {proxy_id}. See {@link ProxyInfo}
 * @see 404 Unable to find the {proxy_id} specified proxy. Edit your request, then try again. (code: `proxies.proxy_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getProxyUrl(params: GetProxyUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/proxies/${params.proxyId}`
  return `${getBasePath()}${path}`
}

export interface GetProxyUrlParams {
  proxyId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/runners
 * Operation: `get-runners`
 * Retrieves an overview of all runners.
 * @param {string} params.q An optional query to filter runners by. Maps to an Elasticsearch query_string query.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 An overview of all runners. See {@link RunnerOverview}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getRunnersUrl(params: GetRunnersUrlParams): string {
  const queryString = buildQueryString({
    q: params.q,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/runners`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetRunnersUrlParams {
  q?: string | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/runners/_resync
 * Operation: `resync-runners`
 * Asynchronously synchronizes all runner information in the search index.
 * @param {boolean} params.skipMatchingVersion When true, skips the document indexing when the version matches the in-memory copy. Default: true
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 202 The ids of documents, organized by model version, that will be synchronized. See {@link ModelVersionIndexSynchronizationResults}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resyncRunnersUrl(params: ResyncRunnersUrlParams): string {
  const queryString = buildQueryString({
    skip_matching_version:
      params.skipMatchingVersion === undefined ? true : params.skipMatchingVersion,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/runners/_resync`
  return `${getBasePath()}${path}${queryString}`
}

export interface ResyncRunnersUrlParams {
  skipMatchingVersion?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/runners/_search
 * Operation: `search-runners`
 * Retrieves runners that match a given query.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts an optional request payload of type {@link SearchRequest}
 * @see 200 An overview of runners that matched the given search query. See {@link RunnerOverview}
 * @see 400 The search request failed. (code: `runners.search_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function searchRunnersUrl(params: SearchRunnersUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/runners/_search`
  return `${getBasePath()}${path}`
}

export interface SearchRunnersUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/runners/{runner_id}
 * Operation: `get-runner`
 * Retrieves a runner by id.
 * @param {string} params.runnerId The identifier for the runner.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The information for the runner specified by {runner_id}. See {@link RunnerInfo}
 * @see 404 Unable to find the {runner_id} specified runner. Edit your request, then try again. (code: `runners.runner_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getRunnerUrl(params: GetRunnerUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/runners/${params.runnerId}`
  return `${getBasePath()}${path}`
}

export interface GetRunnerUrlParams {
  runnerId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/runners/{runner_id}
 * Operation: `delete-runner`
 * Delete a runner by id.
 * @param {string} params.runnerId The identifier for the runner.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The runner specified by {runner_id} was deleted.
 * @see 400 The runner specified by {runner_id} could not be deleted. (code: `runners.runner_deletion_failed`). See {@link BasicFailedReply}
 * @see 404 The runner specified by {runner_id} cannot be found. (code: `runners.runner_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteRunnerUrl(params: DeleteRunnerUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/runners/${params.runnerId}`
  return `${getBasePath()}${path}`
}

export interface DeleteRunnerUrlParams {
  runnerId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/infrastructure/runners/{runner_id}/_resync
 * Operation: `resync-runner`
 * Immediately resynchronizes the search index and cache for the selected runner.
 * @param {string} params.runnerId The identifier for the runner.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The runner resync operation executed successfully
 * @see 500 The runner resync operation failed for runner {runner_id}. (code: `runners.resync_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resyncRunnerUrl(params: ResyncRunnerUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/runners/${params.runnerId}/_resync`
  return `${getBasePath()}${path}`
}

export interface ResyncRunnerUrlParams {
  runnerId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/runners/{runner_id}/logging_settings
 * Operation: `get-runner-logging-settings`
 * Get the logging settings for this runner instance.
 * @param {string} params.runnerId The identifier for the runner.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The logging settings for the runner instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this runner were not found. (code: `runners.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getRunnerLoggingSettingsUrl(params: GetRunnerLoggingSettingsUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/runners/${params.runnerId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface GetRunnerLoggingSettingsUrlParams {
  runnerId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/runners/{runner_id}/logging_settings
 * Operation: `set-runner-logging-settings`
 * Set the logging settings for this runner instance.
 * @param {string} params.runnerId The identifier for the runner.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LoggingSettings}
 * @see 200 The updated logging settings for the runner instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this runner were not found. (code: `runners.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setRunnerLoggingSettingsUrl(params: SetRunnerLoggingSettingsUrlParams): string {
  const path = encode`/${params.regionId}/platform/infrastructure/runners/${params.runnerId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface SetRunnerLoggingSettingsUrlParams {
  runnerId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/infrastructure/runners/{runner_id}/logging_settings
 * Operation: `delete-runner-logging-settings`
 * Reset the default logging settings for this runner instance.
 * @param {string} params.runnerId The identifier for the runner.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The updated logging settings for the runner instance. See {@link LoggingSettings}
 * @see 404 The logging settings for this runner were not found. (code: `runners.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteRunnerLoggingSettingsUrl(
  params: DeleteRunnerLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/runners/${params.runnerId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface DeleteRunnerLoggingSettingsUrlParams {
  runnerId: string
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/platform/infrastructure/runners/{runner_id}/logging_settings
 * Operation: `update-runner-logging-settings`
 * All changes in the specified object are applied to the logging settings for this runner instance according to the JSON Merge Patch processing rules. Omitting existing fields causes the same values to be reapplied. Specifying a `null` value reverts the field to the default value, or removes the field when no default value exists.
 * @param {string} params.runnerId The identifier for the runner.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see 200 The updated logging settings for the runner instance. See {@link LoggingSettings}
 * @see 400 The update request is invalid. (code: `patch.request_malformed`). See {@link BasicFailedReply}
 * @see 404 The logging settings for this runner were not found. (code: `runners.logging_settings.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateRunnerLoggingSettingsUrl(
  params: UpdateRunnerLoggingSettingsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/infrastructure/runners/${params.runnerId}/logging_settings`
  return `${getBasePath()}${path}`
}

export interface UpdateRunnerLoggingSettingsUrlParams {
  runnerId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/infrastructure/runners/{runner_id}/roles
 * Operation: `set-runner-roles`
 * Set runner roles.
 * @param {string} params.runnerId The identifier for the runner.
 * @param {boolean} params.bless Assigns the runner to the roles.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link RunnerRolesInfo}
 * @see 200 The roles for the {runner_id} specified runner are assigned. See {@link RunnerRolesInfo}
 * @see 400 There was an error while assigning the roles for the {runner_id} specified runner. Try your request again. (code: `runners.runner_set_roles_failed`). See {@link BasicFailedReply}
 * @see 404 Unable to find the {runner_id} specified runner. Edit your request, then try again. (code: `runners.runner_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setRunnerRolesUrl(params: SetRunnerRolesUrlParams): string {
  const queryString = buildQueryString({
    bless: params.bless,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/runners/${params.runnerId}/roles`
  return `${getBasePath()}${path}${queryString}`
}

export interface SetRunnerRolesUrlParams {
  runnerId: string
  bless?: boolean | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/services/accounter/health
 * Operation: `get-accounter-health`
 * Checks the health of the Accounter.
 * @param {string} params.expectStatus If specified, returns a 417 status code if the status is not what was sent or better. Should be one of [Red, Yellow, or Green].
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Accounter's health status. See {@link AccounterHealth}
 * @see 412 The Accounter's health status; 417 is sent when expect_status is sent but the status is _not_ better or equal to what was sent. See {@link AccounterHealth}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAccounterHealthUrl(params: GetAccounterHealthUrlParams): string {
  const queryString = buildQueryString({
    expect_status: params.expectStatus,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/services/accounter/health`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetAccounterHealthUrlParams {
  expectStatus?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/services/mailer/health
 * Operation: `get-mailer-health`
 * Checks the health of the Mailer.
 * @param {string} params.expectStatus If specified, returns 417 if status is worse. Should be one of [Red, Yellow, or Green].
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Returns Mailer health. See {@link EventProcessorHealth}
 * @see 417 When expect_status is sent and status is worse than that, the response code is 417. See {@link EventProcessorHealth}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getMailerHealthUrl(params: GetMailerHealthUrlParams): string {
  const queryString = buildQueryString({
    expect_status: params.expectStatus,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/services/mailer/health`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetMailerHealthUrlParams {
  expectStatus?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/infrastructure/services/scaler/health
 * Operation: `get-scaler-health`
 * Checks the health of the Scaler.
 * @param {string} params.expectStatus If specified, returns 417 if status is worse. Should be one of [Red, Yellow, or Green].
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Returns Scaler health. See {@link EventProcessorHealth}
 * @see 417 When expect_status is sent and status is worse than that, the response code is 417. See {@link EventProcessorHealth}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getScalerHealthUrl(params: GetScalerHealthUrlParams): string {
  const queryString = buildQueryString({
    expect_status: params.expectStatus,
  })
  const path = encode`/${params.regionId}/platform/infrastructure/services/scaler/health`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetScalerHealthUrlParams {
  expectStatus?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/license
 * Operation: `get-license`
 * Retrieves the license.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The information for the license. See {@link LicenseObject}
 * @see 404 The license cannot be found. (code: `license.license_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getLicenseUrl(params: GetLicenseUrlParams): string {
  const path = encode`/${params.regionId}/platform/license`
  return `${getBasePath()}${path}`
}

export interface GetLicenseUrlParams {
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/license
 * Operation: `set-license`
 * Adds a new or updates an existing license.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LicenseObject}
 * @see 200 The license was updated.
 * @see 400 The license could not be updated. (code: `license.invalid_license`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setLicenseUrl(params: SetLicenseUrlParams): string {
  const path = encode`/${params.regionId}/platform/license`
  return `${getBasePath()}${path}`
}

export interface SetLicenseUrlParams {
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/license
 * Operation: `delete-license`
 * Deletes the license.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The license was deleted.
 * @see 404 The license cannot be found. (code: `license.license_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteLicenseUrl(params: DeleteLicenseUrlParams): string {
  const path = encode`/${params.regionId}/platform/license`
  return `${getBasePath()}${path}`
}

export interface DeleteLicenseUrlParams {
  regionId: string
}

/**
 * GET /api/v1/platform/license/eula
 * Operation: `eula-status`
 * Get status of EULA.
 * @see 200 The status of the EULA. See {@link EulaState}
 * @return {string} A fully-interpolated and escaped URL
 */
export function eulaStatusUrl(): string {
  const path = `/platform/license/eula`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/platform/license/eula/_accept
 * Operation: `accept-eula`
 * Accept end-user license agreement.
 * @see 200 EULA was accepted.
 * @see 500 Something went wrong during EULA accept. (code: `license.eula_not_accepted`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function acceptEulaUrl(): string {
  const path = `/platform/license/eula/_accept`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/regions/{region_id}/platform/resources/aws/s3/accounts
 * Operation: `list-aws-s3-account-credentials`
 * List all existing Account Credentials.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 List of existing Account Credentials. See {@link AccountCredentialsList}
 * @return {string} A fully-interpolated and escaped URL
 */
export function listAwsS3AccountCredentialsUrl(
  params: ListAwsS3AccountCredentialsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/resources/aws/s3/accounts`
  return `${getBasePath()}${path}`
}

export interface ListAwsS3AccountCredentialsUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/resources/aws/s3/accounts/{account_name}
 * Operation: `get-aws-s3-account-credentials`
 * Find Account Credentials by name.
 * @param {string} params.accountName Name of the Account Credentials that you would like to find.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Account Credentials retrieved successfully. See {@link AccountCredentials}
 * @see 404 Account Credentials do not exist by that name. (code: `saas.resources.aws.s3.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAwsS3AccountCredentialsUrl(params: GetAwsS3AccountCredentialsUrlParams): string {
  const path = encode`/${params.regionId}/platform/resources/aws/s3/accounts/${params.accountName}`
  return `${getBasePath()}${path}`
}

export interface GetAwsS3AccountCredentialsUrlParams {
  accountName: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/platform/resources/aws/s3/accounts/{account_name}
 * Operation: `create-aws-s3-account-credentials`
 * Inserts new AWS S3 Account Credentials.
 * @param {string} params.accountName Name of the Account Credentials that you would like to create.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link AccountCredentialsData}
 * @see 201 The Account Credentials were inserted successfully. See {@link AccountCredentials}
 * @see 400 Account Credentials data already exists for the given name. (code: `saas.resources.aws.s3.already_exists`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createAwsS3AccountCredentialsUrl(
  params: CreateAwsS3AccountCredentialsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/resources/aws/s3/accounts/${params.accountName}`
  return `${getBasePath()}${path}`
}

export interface CreateAwsS3AccountCredentialsUrlParams {
  accountName: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/platform/resources/aws/s3/accounts/{account_name}
 * Operation: `put-aws-s3-account-credentials`
 * Update existing Account Credentials.
 * @param {string} params.accountName Name of the Account Credentials that you would like to modify.
 * @param {string} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link AccountCredentialsData}
 * @see 200 Account Credentials retrieved successfully. See {@link AccountCredentials}
 * @see 404 There was no existing data for the given account name. (code: `saas.resources.aws.s3.not_found`). See {@link BasicFailedReply}
 * @see 409 There is a version conflict. (code: `saas.resources.aws.s3.bad_version`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function putAwsS3AccountCredentialsUrl(params: PutAwsS3AccountCredentialsUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/platform/resources/aws/s3/accounts/${params.accountName}`
  return `${getBasePath()}${path}${queryString}`
}

export interface PutAwsS3AccountCredentialsUrlParams {
  accountName: string
  version?: string | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/platform/resources/aws/s3/accounts/{account_name}
 * Operation: `delete-aws-s3-account-credentials`
 * Delete Account Credentials by name.
 * @param {string} params.accountName Name of the Account Credentials that you would like to delete.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 Account Credentials deleted successfully
 * @see 404 Account Credentials do not exist by that name. (code: `saas.resources.aws.s3.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteAwsS3AccountCredentialsUrl(
  params: DeleteAwsS3AccountCredentialsUrlParams,
): string {
  const path = encode`/${params.regionId}/platform/resources/aws/s3/accounts/${params.accountName}`
  return `${getBasePath()}${path}`
}

export interface DeleteAwsS3AccountCredentialsUrlParams {
  accountName: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/platform/resources/{cloud_service}/{storage_service}/health
 * Operation: `get-resources-health`
 * Get health for the resources of the given storage provider.
 * @param {string} params.cloudService The cloud service.
 * @param {string} params.storageService The storage service.
 * @param {string} params.expectStatus If specified, returns a 417 status code if the health status is not what was sent or better. Should be one of [red, yellow, or green].
 * @param {boolean} params.verbose When true, returns the list of health check results.
 * @param {integer} params.threshold When provided will base the healthiness on the given threshold. Expressed in .
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The resources health check. See {@link ResourcesHealth}
 * @see 417 NOTE: When expect_status is sent, but the health status is _not_ better or equal to what was sent, the status code is 417. See {@link ResourcesHealth}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getResourcesHealthUrl(params: GetResourcesHealthUrlParams): string {
  const queryString = buildQueryString({
    expect_status: params.expectStatus,
    verbose: params.verbose,
    threshold: params.threshold,
  })
  const path = encode`/${params.regionId}/platform/resources/${params.cloudService}/${params.storageService}/health`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetResourcesHealthUrlParams {
  cloudService: string
  storageService: string
  expectStatus?: string | null
  verbose?: boolean | null
  threshold?: number | null
  regionId: string
}

/**
 * POST /api/v1/saas/auth/_login
 * Operation: `login-saas-current-user`
 * Primary authentication for an Elastic Cloud user via email address and password.
 * @see Accepts a request payload of type {@link SaasAuthRequest}
 * @see 200 Login was successful. See {@link SaasAuthResponse}
 * @see 401 Invalid user credentials. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function loginSaasCurrentUserUrl(): string {
  const path = `/saas/auth/_login`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/auth/_logout
 * Operation: `logout-saas-current-user`
 * Ends an authenticated session for an Elastic Cloud user.
 * @see 200 Logout was successful
 * @return {string} A fully-interpolated and escaped URL
 */
export function logoutSaasCurrentUserUrl(): string {
  const path = `/saas/auth/_logout`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/auth/_refresh
 * Operation: `refresh-saas-current-user`
 * Refreshes an authenticated session for an Elastic Cloud user with updated authorization.
 * @see 200 Refresh was successful
 * @return {string} A fully-interpolated and escaped URL
 */
export function refreshSaasCurrentUserUrl(): string {
  const path = `/saas/auth/_refresh`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/regions/{region_id}/saas/auth/heroku/_callback
 * Operation: `saas-heroku-callback`
 * Finishes a Heroku authentication by validating and authorizing a token.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link SaasHerokuCallbackRequest}
 * @see 200 The token validation was successful. See {@link SaasAuthResponse}
 * @see 400 The token validation request was invalid. (code: `auth.expired_credentials`). See {@link BasicFailedReply}
 * @see 401 The token validation failed. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function saasHerokuCallbackUrl(params: SaasHerokuCallbackUrlParams): string {
  const path = encode`/${params.regionId}/saas/auth/heroku/_callback`
  return `${getBasePath()}${path}`
}

export interface SaasHerokuCallbackUrlParams {
  regionId: string
}

/**
 * GET /api/v1/saas/auth/mfa
 * Operation: `get-saas-current-user-mfa-enabled`
 * Returns the MFA enabled status for the current user.
 * @see 200 The MFA enabled/disabled state response. See {@link SaasAuthMfaEnabledResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasCurrentUserMfaEnabledUrl(): string {
  const path = `/saas/auth/mfa`
  return `${getRawBasePath()}${path}`
}

/**
 * PUT /api/v1/saas/auth/mfa
 * Operation: `set-saas-current-user-mfa-enabled`
 * Enables or disables MFA for the current user.
 * @see Accepts a request payload of type {@link SaasAuthMfaEnabledRequest}
 * @see 200 The MFA device is enabled
 * @see 400 MFA cannot be enabled unless there are available devices. (code: `auth.no_available_mfa_devices`). See {@link BasicFailedReply}
 * @see 429 Authenticated session is too old for this critical operation, please retry after re-authenticating. (code: `auth.must_reauthenticate_for_critical_operation`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setSaasCurrentUserMfaEnabledUrl(): string {
  const path = `/saas/auth/mfa`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/saas/auth/mfa/devices
 * Operation: `get-saas-current-user-mfa-devices`
 * Returns all available MFA devices for the current user.
 * @see 200 The MFA devices response. See {@link SaasAuthMfaDevicesResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasCurrentUserMfaDevicesUrl(): string {
  const path = `/saas/auth/mfa/devices`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/auth/mfa/devices
 * Operation: `enroll-saas-current-user-mfa-device`
 * Enrolls a new MFA device for the current user.
 * @see Accepts a request payload of type {@link SaasAuthMfaEnrollDeviceRequest}
 * @see 200 The MFA device enrollment response. See {@link SaasAuthMfaDeviceResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function enrollSaasCurrentUserMfaDeviceUrl(): string {
  const path = `/saas/auth/mfa/devices`
  return `${getRawBasePath()}${path}`
}

/**
 * PUT /api/v1/saas/auth/mfa/devices/{device_id}
 * Operation: `activate-saas-current-user-mfa-device`
 * Activates an enrolled MFA device for the current user.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see Accepts a request payload of type {@link SaasAuthMfaActivateDeviceRequest}
 * @see 200 The MFA device activation response. See {@link SaasAuthMfaDeviceResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function activateSaasCurrentUserMfaDeviceUrl(
  params: ActivateSaasCurrentUserMfaDeviceUrlParams,
): string {
  const path = encode`/saas/auth/mfa/devices/${params.deviceId}`
  return `${getRawBasePath()}${path}`
}

export interface ActivateSaasCurrentUserMfaDeviceUrlParams {
  deviceId: string
}

/**
 * DELETE /api/v1/saas/auth/mfa/devices/{device_id}
 * Operation: `delete-saas-current-user-mfa-device`
 * Deletes an enrolled MFA device for the current user.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see 200 The MFA device was successfully deleted
 * @see 400 Cannot delete the last active MFA device while MFA is enabled. (code: `auth.no_available_mfa_devices`). See {@link BasicFailedReply}
 * @see 429 Authenticated session is too old for this critical operation, please retry after re-authenticating. (code: `auth.must_reauthenticate_for_critical_operation`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteSaasCurrentUserMfaDeviceUrl(
  params: DeleteSaasCurrentUserMfaDeviceUrlParams,
): string {
  const path = encode`/saas/auth/mfa/devices/${params.deviceId}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteSaasCurrentUserMfaDeviceUrlParams {
  deviceId: string
}

/**
 * POST /api/v1/saas/auth/mfa/devices/{device_id}/_challenge
 * Operation: `challenge-saas-current-user-mfa-device`
 * For some MFA device types (such as SMS), an SMS message or push notification is sent to the device. This endpoint requires the state_id input field to be set.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see Accepts a request payload of type {@link SaasAuthMfaChallengeRequest}
 * @see 200 The challenge was successfully delivered to the MFA device. See {@link SaasAuthMfaChallengeResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function challengeSaasCurrentUserMfaDeviceUrl(
  params: ChallengeSaasCurrentUserMfaDeviceUrlParams,
): string {
  const path = encode`/saas/auth/mfa/devices/${params.deviceId}/_challenge`
  return `${getRawBasePath()}${path}`
}

export interface ChallengeSaasCurrentUserMfaDeviceUrlParams {
  deviceId: string
}

/**
 * POST /api/v1/saas/auth/mfa/devices/{device_id}/_challenge_factor
 * Operation: `challenge-saas-current-user-mfa-factor`
 * For some MFA device types (such as SMS), an SMS message or push notification is sent to the device. This endpoint requires authentication.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see Accepts a request payload of type {@link SaasAuthMfaFactorChallengeRequest}
 * @see 200 The challenge was successfully delivered to the MFA device. See {@link SaasAuthMfaChallengeResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function challengeSaasCurrentUserMfaFactorUrl(
  params: ChallengeSaasCurrentUserMfaFactorUrlParams,
): string {
  const path = encode`/saas/auth/mfa/devices/${params.deviceId}/_challenge_factor`
  return `${getRawBasePath()}${path}`
}

export interface ChallengeSaasCurrentUserMfaFactorUrlParams {
  deviceId: string
}

/**
 * POST /api/v1/saas/auth/mfa/devices/{device_id}/_verify_challenge
 * Operation: `verify-saas-current-user-mfa-challenge`
 * Called in response to a primary (password) authentication request demanding an additional factor. This endpoint requires the state_id input field to be set.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see Accepts a request payload of type {@link SaasAuthMfaVerifyChallengeRequest}
 * @see 200 The MFA device activation response. See {@link SaasAuthResponse}
 * @see 401 The MFA challenge verification failed. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function verifySaasCurrentUserMfaChallengeUrl(
  params: VerifySaasCurrentUserMfaChallengeUrlParams,
): string {
  const path = encode`/saas/auth/mfa/devices/${params.deviceId}/_verify_challenge`
  return `${getRawBasePath()}${path}`
}

export interface VerifySaasCurrentUserMfaChallengeUrlParams {
  deviceId: string
}

/**
 * POST /api/v1/saas/auth/mfa/devices/{device_id}/_verify_factor
 * Operation: `verify-saas-current-user-mfa-factor`
 * Called to return a fresh authenticated session using an MFA factor. This endpoint requires authentication.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see Accepts a request payload of type {@link SaasAuthMfaFactorVerifyChallengeRequest}
 * @see 200 The MFA factor verification response. See {@link SaasAuthResponse}
 * @see 400 The MFA challenge verification failed. (code: `auth.mfa_factor_failed`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function verifySaasCurrentUserMfaFactorUrl(
  params: VerifySaasCurrentUserMfaFactorUrlParams,
): string {
  const path = encode`/saas/auth/mfa/devices/${params.deviceId}/_verify_factor`
  return `${getRawBasePath()}${path}`
}

export interface VerifySaasCurrentUserMfaFactorUrlParams {
  deviceId: string
}

/**
 * POST /api/v1/saas/auth/oauth/_callback
 * Operation: `saas-openid-callback`
 * Finishes an OAuth2 authentication by validating and authorizing an ID token.
 * @see Accepts a request payload of type {@link SaasOpenIdCodeCallbackRequest}
 * @see 200 The token validation was successful. See {@link SaasAuthResponse}
 * @see 400 The token validation request was invalid. (code: `auth.expired_credentials`). See {@link BasicFailedReply}
 * @see 401 The token validation failed. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function saasOpenidCallbackUrl(): string {
  const path = `/saas/auth/oauth/_callback`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/auth/sso/_callback
 * Operation: `organization-sso-callback`
 * Finishes SSO authentication.
 * @see Accepts a request payload of type {@link SsoCallbackRequest}
 * @see 200 Login was successful. See {@link SaasAuthResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function organizationSsoCallbackUrl(): string {
  const path = `/saas/auth/sso/_callback`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/auth/sso/_find_idp
 * Operation: `organization-sso-find-idp`
 * Finds organization identity providers that can authenticate a given user's email address.
 * @see Accepts a request payload of type {@link FindIdpRequest}
 * @see 200 IdP results that match the request. See {@link FindIdpResults}
 * @return {string} A fully-interpolated and escaped URL
 */
export function organizationSsoFindIdpUrl(): string {
  const path = `/saas/auth/sso/_find_idp`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/saas/auth/sso/{login_identifier}
 * Operation: `organization-sso-init`
 * Starts SSO authentication with the configured organization IdP with the given login identifier.
 * @param {string} params.loginIdentifier The ID that identifies which organization to SSO with.
 * @param {string} params.redirectUri The URI to redirect to after authentication.
 * @param {string} params.state A value sent by the client that will be returned in the token.
 * @see 302 Redirection to the organization IdP for authentication
 * @see 404 Invalid login identifier. (code: `auth.no_org_for_login_id`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function organizationSsoInitUrl(params: OrganizationSsoInitUrlParams): string {
  const queryString = buildQueryString({
    redirect_uri: params.redirectUri,
    state: params.state,
  })
  const path = encode`/saas/auth/sso/${params.loginIdentifier}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface OrganizationSsoInitUrlParams {
  loginIdentifier: string
  redirectUri: string
  state: string
}

/**
 * PUT /api/v1/saas/billing/lineitems/activate
 * Operation: `activate-line-items`
 * Activate the line items assigned to a user.
 * @see Accepts a request payload of type {@link ActivateLineItemsRequest}
 * @see 200 Line items successfully activated.
 * @see 400 Invalid parameters were supplied. (code: `billing_service.invalid_request`). See {@link BasicFailedReply}
 * @see 500 Internal server error. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function activateLineItemsUrl(): string {
  const path = `/saas/billing/lineitems/activate`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/saas/billing/prices
 * Operation: `get-prices`
 * Retrieves the list of Elastic Cloud products' prices by regions.
 * @param {string} params.region The region for which to fetch prices.
 * @param {string} params.from A datetime for the beginning of the desired range for which to fetch prices.
 * @param {string} params.to A datetime for the end of the desired range for which to fetch prices.
 * @param {string} params.timestamp A timestamp to retrieve prices effective at a specific point in time. Default: the current instant using the system UTC clock.
 * @param {boolean} params.isMarketplace Whether or not to include marketplace uplifts in the prices.
 * @param {boolean} params.includeDeprecatedSkus Whether or not to include deprecated SKUs.
 * @see 200 Prices for all the products in a region. See {@link Prices}
 * @see 404 Region not found. (code: `billing_service.region_not_found`). See {@link BasicFailedReply}
 * @see 500 Error fetching the list of Elastic Cloud products' prices. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getPricesUrl(params: GetPricesUrlParams): string {
  const queryString = buildQueryString({
    region: params.region,
    from: params.from,
    to: params.to,
    timestamp: params.timestamp,
    is_marketplace: params.isMarketplace === undefined ? false : params.isMarketplace,
    include_deprecated_skus:
      params.includeDeprecatedSkus === undefined ? false : params.includeDeprecatedSkus,
  })
  const path = `/saas/billing/prices`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetPricesUrlParams {
  region: string
  from?: string | null
  to?: string | null
  timestamp?: string | null
  isMarketplace?: boolean | null
  includeDeprecatedSkus?: boolean | null
}

/**
 * GET /api/v1/saas/billing/{organization_id}
 * Operation: `get-organization-billing-details`
 * Retrieves billing details of an organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @see 200 The details of an organization. See {@link OrganizationBillingDetails}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 The organization was not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 An error occurred when fetching prepaid users for the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getOrganizationBillingDetailsUrl(
  params: GetOrganizationBillingDetailsUrlParams,
): string {
  const path = encode`/saas/billing/${params.organizationId}`
  return `${getRawBasePath()}${path}`
}

export interface GetOrganizationBillingDetailsUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/saas/billing/{organization_id}/_credit
 * Operation: `credit-organization`
 * Add an ad hoc credit for an organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @see Accepts a request payload of type {@link CreditRequest}
 * @see 200 Credit was accepted.
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 Invalid parameters were supplied. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function creditOrganizationUrl(params: CreditOrganizationUrlParams): string {
  const path = encode`/saas/billing/${params.organizationId}/_credit`
  return `${getRawBasePath()}${path}`
}

export interface CreditOrganizationUrlParams {
  organizationId: string
}

/**
 * PUT /api/v1/saas/billing/{organization_id}/_deactivate
 * Operation: `deactivate-organization`
 * Deactivate an organization so it is not billed and can no longer use ESS resources.
 * @param {string} params.organizationId Identifier for the organization.
 * @see Accepts a request payload of type {@link DeactivateOrgRequest}
 * @see 200 Organization successfully deactivated.
 * @see 400 Invalid parameters were supplied. (code: `billing_service.invalid_request`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 Internal server error. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deactivateOrganizationUrl(params: DeactivateOrganizationUrlParams): string {
  const path = encode`/saas/billing/${params.organizationId}/_deactivate`
  return `${getRawBasePath()}${path}`
}

export interface DeactivateOrganizationUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/saas/billing/{organization_id}/_retry_past_due_invoices
 * Operation: `retry-past-due-invoices`
 * Retry past due invoices.
 * @param {string} params.organizationId Identifier for the organization.
 * @see 200 The retry attempt was successful
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function retryPastDueInvoicesUrl(params: RetryPastDueInvoicesUrlParams): string {
  const path = encode`/saas/billing/${params.organizationId}/_retry_past_due_invoices`
  return `${getRawBasePath()}${path}`
}

export interface RetryPastDueInvoicesUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/saas/billing/{organization_id}/_set_to_direct
 * Operation: `set-to-direct`
 * Set an organization from reseller to direct.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.asOf Optional datetime for the transition to occur. Default is the start of the next month.
 * @see 200 Organization was set to direct.
 * @see 400 Invalid parameters were supplied. (code: `billing_service.invalid_request`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 An error occurred during this request. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setToDirectUrl(params: SetToDirectUrlParams): string {
  const queryString = buildQueryString({
    as_of: params.asOf,
  })
  const path = encode`/saas/billing/${params.organizationId}/_set_to_direct`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface SetToDirectUrlParams {
  organizationId: string
  asOf?: string | null
}

/**
 * PUT /api/v1/saas/billing/{organization_id}/activate
 * Operation: `activate-organization`
 * Activate an organization to be billed and to use ESS resources.
 * @param {string} params.organizationId Identifier for the organization.
 * @see Accepts a request payload of type {@link ActivateOrgRequest}
 * @see 200 Organization successfully activated.
 * @see 400 Invalid parameters were supplied. (code: `billing_service.invalid_request`). See {@link BasicFailedReply}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 Internal server error. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function activateOrganizationUrl(params: ActivateOrganizationUrlParams): string {
  const path = encode`/saas/billing/${params.organizationId}/activate`
  return `${getRawBasePath()}${path}`
}

export interface ActivateOrganizationUrlParams {
  organizationId: string
}

/**
 * GET /api/v1/saas/billing/{organization_id}/features
 * Operation: `get-features-usage`
 * Retrieves the features in use by an organization above its subscription level in the last hour.
 * @param {string} params.organizationId Identifier for the organization.
 * @see 200 The Features Usage has been returned. See {@link FeaturesUsage}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 Failed to retrieve valid features usage due to an internal server error. (code: `features_usage.invalid_stored_data`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getFeaturesUsageUrl(params: GetFeaturesUsageUrlParams): string {
  const path = encode`/saas/billing/${params.organizationId}/features`
  return `${getRawBasePath()}${path}`
}

export interface GetFeaturesUsageUrlParams {
  organizationId: string
}

/**
 * GET /api/v1/saas/billing/{organization_id}/history
 * Operation: `get-billing-history`
 * Retrieves the billing history for the organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @see 200 The billing history associated to an organization. See {@link BillingHistory}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 The organization was not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 An error occurred fetching the billing history associated with the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getBillingHistoryUrl(params: GetBillingHistoryUrlParams): string {
  const path = encode`/saas/billing/${params.organizationId}/history`
  return `${getRawBasePath()}${path}`
}

export interface GetBillingHistoryUrlParams {
  organizationId: string
}

/**
 * GET /api/v1/saas/billing/{organization_id}/history/{statement_number}
 * Operation: `get-billing-history-by-statement`
 * Retrieves the billing history by statement for the organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.statementNumber The Statement number.
 * @see 200 The billing history by statement associated to an organization. See {@link BillingHistory}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 The organization was not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 An error occurred when fetching the billing history by statement associated with the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getBillingHistoryByStatementUrl(
  params: GetBillingHistoryByStatementUrlParams,
): string {
  const path = encode`/saas/billing/${params.organizationId}/history/${params.statementNumber}`
  return `${getRawBasePath()}${path}`
}

export interface GetBillingHistoryByStatementUrlParams {
  organizationId: string
  statementNumber: string
}

/**
 * GET /api/v1/saas/billing/{organization_id}/lineitems
 * Operation: `get-line-items`
 * Retrieves the line items of for an organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.at Only returns line items active at the given time, if specified. Defaults to all line items.
 * @see 200 The line items associated to an organization. See {@link LineItems}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 Error fetching the line items associated to the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getLineItemsUrl(params: GetLineItemsUrlParams): string {
  const queryString = buildQueryString({
    at: params.at,
  })
  const path = encode`/saas/billing/${params.organizationId}/lineitems`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetLineItemsUrlParams {
  organizationId: string
  at?: string | null
}

/**
 * GET /api/v1/saas/billing/{organization_id}/marketplaces/azure/subscription
 * Operation: `get-marketplace-billing-details`
 * Retrieves Azure marketplace subscription billing details for a given organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @see 200 The marketplace subscription billing details of an organization. See {@link AzureMarketplaceSubscription}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 The organization was not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 An error occurred for this request. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getMarketplaceBillingDetailsUrl(
  params: GetMarketplaceBillingDetailsUrlParams,
): string {
  const path = encode`/saas/billing/${params.organizationId}/marketplaces/azure/subscription`
  return `${getRawBasePath()}${path}`
}

export interface GetMarketplaceBillingDetailsUrlParams {
  organizationId: string
}

/**
 * GET /api/v1/saas/billing/{organization_id}/prepaids
 * Operation: `get-prepaids`
 * Retrieves subscriptions and premium products for an organization.
 * @param {string} params.organizationId Identifier for the organization.
 * @param {string} params.start If specified, returns active or future prepaids as of this date. Defaults to all prepaids.
 * @see 200 The prepaids associated with an organization. See {@link Prepaids}
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 The organization was not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 An error occurred when fetching prepaid users for the organization. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getPrepaidsUrl(params: GetPrepaidsUrlParams): string {
  const queryString = buildQueryString({
    start: params.start,
  })
  const path = encode`/saas/billing/${params.organizationId}/prepaids`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetPrepaidsUrlParams {
  organizationId: string
  start?: string | null
}

/**
 * PUT /api/v1/saas/billing/{organization_id}/set_payment_method
 * Operation: `set-payment-method`
 * Set organization payment method to credit card or invoice.
 * @param {string} params.organizationId Identifier for the organization.
 * @see Accepts a request payload of type {@link SetPaymentRequest}
 * @see 200 Payment method was accepted.
 * @see 403 The current user does not have access to the requested organization. (code: `organization.invalid_access`). See {@link BasicFailedReply}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @see 500 Invalid parameters were supplied. (code: `billing_service.failed_request`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setPaymentMethodUrl(params: SetPaymentMethodUrlParams): string {
  const path = encode`/saas/billing/${params.organizationId}/set_payment_method`
  return `${getRawBasePath()}${path}`
}

export interface SetPaymentMethodUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/saas/deployments/instant
 * Operation: `post-instant-deployment`
 * Claims an instant deployment if available.
 * @see Accepts a request payload of type {@link DeploymentClaimRequest}
 * @see 200 Claim response. See {@link DeploymentClaimResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function postInstantDeploymentUrl(): string {
  const path = `/saas/deployments/instant`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/regions/{region_id}/saas/inappchat/token
 * Operation: `get-token-inappchat`
 * Get an InAppChat identity token to unambiguously and securely identify a user.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The In App Chat token identifying the user in the current session. See {@link GetInAppChatUserIdJWTResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTokenInappchatUrl(params: GetTokenInappchatUrlParams): string {
  const path = encode`/${params.regionId}/saas/inappchat/token`
  return `${getBasePath()}${path}`
}

export interface GetTokenInappchatUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/saas/launchdarkly/{context_key}/evaluate_feature_flag
 * Operation: `post-evaluate-feature-flag`
 * Evaluate a feature flag and retrieve its detailed evaluation result.
 * @param {string} params.contextKey Identifier for the launchdarkly context key.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link EvaluateFeatureFlagDetailRequest}
 * @see 200 The detailed result for evaluating a feature flag. See {@link EvaluateFeatureFlagDetailResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function postEvaluateFeatureFlagUrl(params: PostEvaluateFeatureFlagUrlParams): string {
  const path = encode`/${params.regionId}/saas/launchdarkly/${params.contextKey}/evaluate_feature_flag`
  return `${getBasePath()}${path}`
}

export interface PostEvaluateFeatureFlagUrlParams {
  contextKey: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/saas/launchdarkly/{context_key}/track_event
 * Operation: `post-track-event`
 * Track a custom event and send it to LaunchDarkly.
 * @param {string} params.contextKey Identifier for the launchdarkly context key.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link TrackEventRequest}
 * @see 200 Tracked event successfully.
 * @return {string} A fully-interpolated and escaped URL
 */
export function postTrackEventUrl(params: PostTrackEventUrlParams): string {
  const path = encode`/${params.regionId}/saas/launchdarkly/${params.contextKey}/track_event`
  return `${getBasePath()}${path}`
}

export interface PostTrackEventUrlParams {
  contextKey: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/saas/legacy/usage-events/data-tier
 * Operation: `create-data-tier-at-limit-event`
 * Create data tier at limit event.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link UsageEventRequest}
 * @see 201 Usage event was successfully created
 * @see 401 Credentials were invalid. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @see 403 Permission is required. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createDataTierAtLimitEventUrl(params: CreateDataTierAtLimitEventUrlParams): string {
  const path = encode`/${params.regionId}/saas/legacy/usage-events/data-tier`
  return `${getBasePath()}${path}`
}

export interface CreateDataTierAtLimitEventUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/saas/legacy/usage-events/disk
 * Operation: `create-disk-usage-event`
 * Create disk usage event.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link UsageEventRequest}
 * @see 201 Usage event was successfully created
 * @see 401 Credentials were invalid. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @see 403 Permission is required. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createDiskUsageEventUrl(params: CreateDiskUsageEventUrlParams): string {
  const path = encode`/${params.regionId}/saas/legacy/usage-events/disk`
  return `${getBasePath()}${path}`
}

export interface CreateDiskUsageEventUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/saas/legacy/usage-events/failed-autoscale
 * Operation: `create-failed-autoscale-event`
 * Create failed autoscale event.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link UsageEventRequest}
 * @see 201 Usage event was successfully created
 * @see 401 Authentication is required. (code: `root.unauthenticated`). See {@link BasicFailedReply}
 * @see 403 Permission is required. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createFailedAutoscaleEventUrl(params: CreateFailedAutoscaleEventUrlParams): string {
  const path = encode`/${params.regionId}/saas/legacy/usage-events/failed-autoscale`
  return `${getBasePath()}${path}`
}

export interface CreateFailedAutoscaleEventUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/saas/legacy/usage-events/machine-learning
 * Operation: `create-ml-tier-at-limit-event`
 * Create ML tier at limit event.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link UsageEventRequest}
 * @see 201 Usage event was successfully created
 * @see 401 Credentials were invalid. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @see 403 Permission is required. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createMlTierAtLimitEventUrl(params: CreateMlTierAtLimitEventUrlParams): string {
  const path = encode`/${params.regionId}/saas/legacy/usage-events/machine-learning`
  return `${getBasePath()}${path}`
}

export interface CreateMlTierAtLimitEventUrlParams {
  regionId: string
}

/**
 * POST /api/v1/saas/marketplace/_activate
 * Operation: `post-activate-marketplace`
 * Activate a pending subscription for an authenticated user. When available, firmographics will update the organization's info.
 * @see Accepts a request payload of type {@link MarketplaceActivateRequest}
 * @see 202 The organization successfully activated the Marketplace subscription. See {@link MarketplaceActivationResponse}
 * @see 400 * The partner token expired. (code: `user.marketplace.expired_token`)
 * The partner token is invalid. (code: `user.marketplace.invalid_token`)
 * Unable to resolve partner token. (code: `user.marketplace.token_resolve_error`)
 * The user is already subscribed. (code: `user.marketplace.already_subscribed`)
 * The user already has a pending subscription. (code: `user.marketplace.pending_subscription`)
 * The user is running a trial on a different provider. (code: `user.marketplace.incompatible_deployments`). See {@link BasicFailedReply}
 * @see 404 The partner marketplace doesn't exist. (code: `user.marketplace.invalid_marketplace`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function postActivateMarketplaceUrl(): string {
  const path = `/saas/marketplace/_activate`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/regions/{region_id}/saas/marketplace/_deactivate
 * Operation: `post-deactivate-marketplace`
 * Deactivate an organization from a Marketplace after unsubscribed.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link MarketplaceDeactivateRequest}
 * @see 200 The organization was successfully deactivated from the Marketplace.
 * @see 400 * Unable to put the deployment parameter. (code: `user.marketplace.put_deployment_parameter.error`)
 * Failed to update the organization's metadata. (code: `user.update_organization_metadata.error`). See {@link BasicFailedReply}
 * @see 404 * The partner marketplace doesn't exist. (code: `user.marketplace.invalid_marketplace`)
 * Unable to find the organization. (code: `user.marketplace.organization_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function postDeactivateMarketplaceUrl(params: PostDeactivateMarketplaceUrlParams): string {
  const path = encode`/${params.regionId}/saas/marketplace/_deactivate`
  return `${getBasePath()}${path}`
}

export interface PostDeactivateMarketplaceUrlParams {
  regionId: string
}

/**
 * POST /api/v1/saas/marketplace/_introspect
 * Operation: `get-marketplace-subscription-availability`
 * Introspect the availability of user ids to be converted to marketplace subscription.
 * @see Accepts a request payload of type {@link MarketplaceIntrospectionRequest}
 * @see 200 The introspection request completed successfully. See {@link MarketplaceIntrospectionResponse}
 * @see 400 * The partner token expired. (code: `user.marketplace.expired_token`)
 * The partner token is invalid. (code: `user.marketplace.invalid_token`)
 * Unable to resolve partner token. (code: `user.marketplace.token_resolve_error`)
 * The introspection request is not allowed for the client. (code: `user.marketplace.introspection_denied`). See {@link BasicFailedReply}
 * @see 404 The partner marketplace doesn't exist. (code: `user.marketplace.invalid_marketplace`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getMarketplaceSubscriptionAvailabilityUrl(): string {
  const path = `/saas/marketplace/_introspect`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/marketplace/_subscribe
 * Operation: `post-subscribe-marketplace`
 * Subscribe an authenticated user to an Elastic Marketplace partner, putting the user in a waiting state until an activation is performed.
 * @see Accepts a request payload of type {@link MarketplaceSubscribeRequest}
 * @see 201 The user was successfully subscribed to the Marketplace.
 * @see 400 * The partner token expired. (code: `user.marketplace.expired_token`)
 * The partner token is invalid. (code: `user.marketplace.invalid_token`)
 * The user is already subscribed. (code: `user.marketplace.already_subscribed`)
 * The user already has a pending subscription. (code: `user.marketplace.pending_subscription`)
 * The user is running a trial on a different provider. (code: `user.marketplace.incompatible_deployments`). See {@link BasicFailedReply}
 * @see 404 The partner marketplace doesn't exist. (code: `user.marketplace.invalid_marketplace`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function postSubscribeMarketplaceUrl(): string {
  const path = `/saas/marketplace/_subscribe`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/saas/organizations/{organization_id}
 * Operation: `get-saas-organization`
 * Retrieves an Elastic Cloud organization.
 * @param {string} params.organizationId Identifier for the Organization.
 * @param {string} params.include Comma delimited list of optional properties to include in the output.
 * @see 200 The Elastic Cloud organization. See {@link SaasOrganizationResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasOrganizationUrl(params: GetSaasOrganizationUrlParams): string {
  const queryString = buildQueryString({
    include: params.include,
  })
  const path = encode`/saas/organizations/${params.organizationId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetSaasOrganizationUrlParams {
  organizationId: string
  include?: string | null
}

/**
 * POST /api/v1/saas/organizations/{organization_id}/_add_to_serverless_waitlist
 * Operation: `request-organization-serverless-waitlist`
 * Add an organization to the serverless waitlist, and send a confirmation email.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link ServerlessWaitlistRequest}
 * @see 200 requests serverless waitlist successfully
 * @return {string} A fully-interpolated and escaped URL
 */
export function requestOrganizationServerlessWaitlistUrl(
  params: RequestOrganizationServerlessWaitlistUrlParams,
): string {
  const path = encode`/saas/organizations/${params.organizationId}/_add_to_serverless_waitlist`
  return `${getRawBasePath()}${path}`
}

export interface RequestOrganizationServerlessWaitlistUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/saas/organizations/{organization_id}/_convert_from_trial
 * Operation: `convert-saas-organization-from-trial`
 * Convert an Elastic Cloud organization from trial to paying customer.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 The organization was successfully converted
 * @return {string} A fully-interpolated and escaped URL
 */
export function convertSaasOrganizationFromTrialUrl(
  params: ConvertSaasOrganizationFromTrialUrlParams,
): string {
  const path = encode`/saas/organizations/${params.organizationId}/_convert_from_trial`
  return `${getRawBasePath()}${path}`
}

export interface ConvertSaasOrganizationFromTrialUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/saas/organizations/{organization_id}/_extend_trial
 * Operation: `extend-saas-organization-trial`
 * Extend the trial end date for an Elastic Cloud organization by the specified duration.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see Accepts a request payload of type {@link ExtendTrialRequest}
 * @see 200 The trial was successfully extended
 * @return {string} A fully-interpolated and escaped URL
 */
export function extendSaasOrganizationTrialUrl(
  params: ExtendSaasOrganizationTrialUrlParams,
): string {
  const path = encode`/saas/organizations/${params.organizationId}/_extend_trial`
  return `${getRawBasePath()}${path}`
}

export interface ExtendSaasOrganizationTrialUrlParams {
  organizationId: string
}

/**
 * POST /api/v1/saas/organizations/{organization_id}/_regenerate_invoices
 * Operation: `regenerate-invoices`
 * Regenerates invoices for an Elastic Cloud organization.
 * @param {string} params.organizationId Identifier for the Organization.
 * @see 200 The invoices were successfully regenerated
 * @return {string} A fully-interpolated and escaped URL
 */
export function regenerateInvoicesUrl(params: RegenerateInvoicesUrlParams): string {
  const path = encode`/saas/organizations/${params.organizationId}/_regenerate_invoices`
  return `${getRawBasePath()}${path}`
}

export interface RegenerateInvoicesUrlParams {
  organizationId: string
}

/**
 * PUT /api/v1/regions/{region_id}/saas/organizations/{organization_id}/_update_subscription_level
 * Operation: `update-organization-subscription-level`
 * Update subscription level for deployments that are owned by this organization.
 * @param {string} params.organizationId Identifier for the Organization.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link UpdateSubscriptionLevelRequest}
 * @see 200 Updated the subscription level successfully
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateOrganizationSubscriptionLevelUrl(
  params: UpdateOrganizationSubscriptionLevelUrlParams,
): string {
  const path = encode`/${params.regionId}/saas/organizations/${params.organizationId}/_update_subscription_level`
  return `${getBasePath()}${path}`
}

export interface UpdateOrganizationSubscriptionLevelUrlParams {
  organizationId: string
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/saas/portal/checkout/_callback
 * Operation: `portal-checkout-saml-callback`
 * Handles a SAML response from the Identity Provider and extracts user's metadata to provision a new Portal Checkout user.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts an optional request payload of type {@link string}
 * @see Accepts a request payload of type {@link string}
 * @see 302 Redirect to either 'RelayState' or configured Checkout URL. See {@link void}
 * @see 403 Forbidden access to this resource. See {@link void}
 * @return {string} A fully-interpolated and escaped URL
 */
export function portalCheckoutSamlCallbackUrl(params: PortalCheckoutSamlCallbackUrlParams): string {
  const path = encode`/${params.regionId}/saas/portal/checkout/_callback`
  return `${getBasePath()}${path}`
}

export interface PortalCheckoutSamlCallbackUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/saas/portal/checkout/_init
 * Operation: `portal-checkout-saml-init`
 * Initiates a SAML Assertion Request to be processed by an Identity Provider.
 * @param {string} params.relayState The relay-state to hold during the SAML assertion.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 302 Redirect to Identity provider with assertion. See {@link void}
 * @see 403 Forbidden access to this resource. See {@link void}
 * @return {string} A fully-interpolated and escaped URL
 */
export function portalCheckoutSamlInitUrl(params: PortalCheckoutSamlInitUrlParams): string {
  const queryString = buildQueryString({
    RelayState: params.relayState,
  })
  const path = encode`/${params.regionId}/saas/portal/checkout/_init`
  return `${getBasePath()}${path}${queryString}`
}

export interface PortalCheckoutSamlInitUrlParams {
  relayState?: string | null
  regionId: string
}

/**
 * GET /api/v1/saas/providers
 * Operation: `get-saas-providers`
 * Retrieves the list of Elastic Cloud providers with their regions.
 * @param {boolean} params.productionOnlyRegions When false, the response will include non-production ready regions. Default: true
 * @see 200 The Elastic Cloud providers have been returned. See {@link Array<Provider>}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasProvidersUrl(params: GetSaasProvidersUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    production_only_regions:
      params.productionOnlyRegions === undefined ? true : params.productionOnlyRegions,
  })
  const path = `/saas/providers`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetSaasProvidersUrlParams {
  productionOnlyRegions?: boolean | null
}

/**
 * GET /api/v1/saas/status
 * Operation: `get-saas-status`
 * Retrieves the current Elastic Cloud Status summary.
 * @see 200 The Elastic Cloud Status has been returned. See {@link Summary}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasStatusUrl(): string {
  const path = `/saas/status`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/saas/tiles/blog/{category}
 * Operation: `get-saas-tiles-blog`
 * Retrieves posts from the Elastic Blog.
 * @param {string} params.category The name of the category.
 * @param {integer} params.count How many blog posts should be returned. Default: 3
 * @see 200 The Elastic Blog posts have been returned. See {@link Array<Post>}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasTilesBlogUrl(params: GetSaasTilesBlogUrlParams): string {
  const queryString = buildQueryString({
    count: params.count === undefined ? 3 : params.count,
  })
  const path = encode`/saas/tiles/blog/${params.category}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetSaasTilesBlogUrlParams {
  category: string
  count?: number | null
}

/**
 * GET /api/v1/saas/tiles/event
 * Operation: `get-saas-tiles-event`
 * Retrieves Elastic events.
 * @param {integer} params.count How many events should be returned. Default: 2
 * @see 200 The Elastic events have been returned. See {@link Array<Event>}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasTilesEventUrl(params: GetSaasTilesEventUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    count: params.count === undefined ? 2 : params.count,
  })
  const path = `/saas/tiles/event`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetSaasTilesEventUrlParams {
  count?: number | null
}

/**
 * GET /api/v1/saas/user
 * Operation: `get-saas-current-user`
 * Returns the current user.
 * @param {boolean} params.showHiddenRoleAssignments If true, hidden role assignments are returned.
 * @param {boolean} params.showRoleAssignments If true, role assignments are returned.
 * @see 200 User successfully fetched. See {@link SaasUserResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasCurrentUserUrl(params: GetSaasCurrentUserUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    show_hidden_role_assignments:
      params.showHiddenRoleAssignments === undefined ? false : params.showHiddenRoleAssignments,
    show_role_assignments:
      params.showRoleAssignments === undefined ? false : params.showRoleAssignments,
  })
  const path = `/saas/user`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetSaasCurrentUserUrlParams {
  showHiddenRoleAssignments?: boolean | null
  showRoleAssignments?: boolean | null
}

/**
 * POST /api/v1/saas/user
 * Operation: `create-saas-user`
 * Create SaaS user.
 * @param {boolean} params.activate Activate the new user if true*, only Stage if false.
 * @see Accepts a request payload of type {@link CreateSaasUserRequest}
 * @see 201 User successfully created. See {@link SaasUserResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 409 User already exists. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createSaasUserUrl(params: CreateSaasUserUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    activate: params.activate,
  })
  const path = `/saas/user`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface CreateSaasUserUrlParams {
  activate?: boolean | null
}

/**
 * POST /api/v1/saas/user/_email
 * Operation: `set-saas-current-user-email`
 * Updates the user's email address.
 * @see Accepts a request payload of type {@link SaasUpdateEmailRequestData}
 * @see 200 User successfully updated. See {@link SaasUserResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 403 Access is denied. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setSaasCurrentUserEmailUrl(): string {
  const path = `/saas/user/_email`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/user/_password
 * Operation: `set-saas-current-user-password`
 * Updates the user's password.
 * @see Accepts a request payload of type {@link SaasUpdatePasswordRequestData}
 * @see 200 User successfully updated. See {@link SaasUserResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 403 Access is denied. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setSaasCurrentUserPasswordUrl(): string {
  const path = `/saas/user/_password`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/user/_password_hash
 * Operation: `set-saas-current-user-password-hash`
 * Updates the user's password via password reset hash.
 * @see Accepts a request payload of type {@link SaasUpdatePasswordWithHashRequestData}
 * @see 200 User successfully updated. See {@link SaasUserResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 403 Access is denied. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setSaasCurrentUserPasswordHashUrl(): string {
  const path = `/saas/user/_password_hash`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/user/_resend_verification
 * Operation: `resend-saas-verification-email`
 * Resends the email for the user to verify their email address.
 * @see Accepts a request payload of type {@link SaasResendVerificationRequest}
 * @see 200 Verification email resent
 * @see 400 Cannot fulfil request. See {@link BasicFailedReply}
 * @see 429 Rate limit exceeded. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resendSaasVerificationEmailUrl(): string {
  const path = `/saas/user/_resend_verification`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/user/_reset-password
 * Operation: `reset-saas-user-password`
 * Requests a password reset. A confirmation email is sent to existing users.
 * @see Accepts a request payload of type {@link SaasResetPasswordRequest}
 * @see 200 Password reset procedure initiated
 * @return {string} A fully-interpolated and escaped URL
 */
export function resetSaasUserPasswordUrl(): string {
  const path = `/saas/user/_reset-password`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/user/_subscription_level
 * Operation: `subscription-level`
 * Update subscription level request.
 * @see Accepts a request payload of type {@link SaasUpdateSubscriptionLevelRequestData}
 * @see 200 Update subscription level request was submitted successfully
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 402 The request execution failed (code: 'user.update_user_level_denied'). See {@link BasicFailedReply}
 * @see 403 Access is denied. See {@link BasicFailedReply}
 * @see 500 The request execution failed (code: 'event.event_not_found'). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function subscriptionLevelUrl(): string {
  const path = `/saas/user/_subscription_level`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/user/_verify
 * Operation: `verify-saas-user-email`
 * Verifies the user's email address and activates those users with pending activation.
 * @see Accepts a request payload of type {@link SaasVerifyUserEmailRequest}
 * @see 200 User's email was verified. See {@link SaasUserResponse}
 * @see 401 Invalid credentials hash. See {@link BasicFailedReply}
 * @see 403 Expired or previously used credentials token. See {@link BasicFailedReply}
 * @see 404 User not found. See {@link BasicFailedReply}
 * @see 409 User has already verified their email. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function verifySaasUserEmailUrl(): string {
  const path = `/saas/user/_verify`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/saas/user/applications
 * Operation: `get-saas-current-user-applications`
 * Retrieves the list of Applications the user can access.
 * @see 200 The user applications have been returned. See {@link SaasUserApplicationsResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasCurrentUserApplicationsUrl(): string {
  const path = `/saas/user/applications`
  return `${getRawBasePath()}${path}`
}

/**
 * PUT /api/v1/saas/user/discovery_questions
 * Operation: `put-saas-current-user-discovery-questions`
 * Updates discovery questions answers for the current user.
 * @see Accepts a request payload of type {@link SaasUserUpdateDiscoveryQuestions}
 * @see 200 User successfully updated. See {@link SaasUserResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function putSaasCurrentUserDiscoveryQuestionsUrl(): string {
  const path = `/saas/user/discovery_questions`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/user/feedback
 * Operation: `submit-feedback`
 * Submit feedback.
 * @see Accepts a request payload of type {@link SaasUserFeedback}
 * @see 200 Feedback was submitted successfully
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 403 Access is denied. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function submitFeedbackUrl(): string {
  const path = `/saas/user/feedback`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/user/mfa/_reset
 * Operation: `reset-saas-current-user-mfa`
 * Resets MFA for the current user.
 * @see 200 The MFA was successfully reseted
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 403 Access is denied. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resetSaasCurrentUserMfaUrl(): string {
  const path = `/saas/user/mfa/_reset`
  return `${getRawBasePath()}${path}`
}

/**
 * PUT /api/v1/saas/user/profile
 * Operation: `put-saas-current-user`
 * Updates profile information for the current user.
 * @see Accepts a request payload of type {@link SaasUpdateProfileRequestData}
 * @see 200 User successfully updated. See {@link SaasUserResponse}
 * @see 400 Invalid Request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function putSaasCurrentUserUrl(): string {
  const path = `/saas/user/profile`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/user/trials/_extend
 * Operation: `extend-trial`
 * Request a one-time trial extension.
 * @see Accepts a request payload of type {@link SaasExtendTrialRequestData}
 * @see 200 Trial was extended successfully
 * @see 400 Trial extension request was not valid. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 403 Access is denied. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function extendTrialUrl(): string {
  const path = `/saas/user/trials/_extend`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/users
 * Operation: `create-saas-user-admin`
 * Create SaaS user.
 * @see Accepts a request payload of type {@link CreateSaasUserAdminRequest}
 * @see 201 User successfully created. See {@link SaasUserCreationResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 409 User already exists. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createSaasUserAdminUrl(): string {
  const path = `/saas/users`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/users/_invite
 * Operation: `invite-saas-user`
 * Invites a new user with a given email and will apply a specific profile upon acceptance.
 * @see Accepts a request payload of type {@link SaasInviteUserEmailRequest}
 * @see 200 The user invitation was created. See {@link SaasInvitedUserResponse}
 * @see 400 The invited email is an invalid email address. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 409 The invited user already exists. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function inviteSaasUserUrl(): string {
  const path = `/saas/users/_invite`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/users/_resend_verification
 * Operation: `resend-saas-verification-email-admin`
 * Resends the email for the user to verify their email address.
 * @see Accepts a request payload of type {@link SaasResendVerificationRequest}
 * @see 200 Verification email resent
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 404 User not found. See {@link BasicFailedReply}
 * @see 409 User has already verified their email. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resendSaasVerificationEmailAdminUrl(): string {
  const path = `/saas/users/_resend_verification`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/users/_search
 * Operation: `search-saas-users`
 * Search for users by email. Returns the top 100 results.
 * @see Accepts a request payload of type {@link SaasUsersSearchRequestData}
 * @see 200 Search successful. See {@link Array<SaasUsersSearchResponseData>}
 * @return {string} A fully-interpolated and escaped URL
 */
export function searchSaasUsersUrl(): string {
  const path = `/saas/users/_search`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/saas/users/external/events/verify_email
 * Operation: `add-saas-external-user-event`
 * Post external user verify-email event.
 * @see Accepts a request payload of type {@link SaasExternalChangeMailEvent}
 * @see 201 External User event successfully created. See {@link SaasExternalUserResponse}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function addSaasExternalUserEventUrl(): string {
  const path = `/saas/users/external/events/verify_email`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/saas/users/external/{email}
 * Operation: `get-saas-external-user`
 * Fetch external user info.
 * @param {string} params.email Email for the user.
 * @see 200 External User successfully fetched. See {@link SaasExternalUserResponse}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 404 External User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasExternalUserUrl(params: GetSaasExternalUserUrlParams): string {
  const path = encode`/saas/users/external/${params.email}`
  return `${getRawBasePath()}${path}`
}

export interface GetSaasExternalUserUrlParams {
  email: string
}

/**
 * POST /api/v1/regions/{region_id}/saas/users/{user_ids}/authorization/_check
 * Operation: `check-authorization-on-behalf-of`
 * Check authorisation on behalf of a number users. Note that since this endpoint allows targeting multiple users, rather than fail on any non-existence user ids, the results for such users will be 'not-authorised'.
 * @param {string} params.userIds Identifiers for users in CSV form.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link AuthzCheckRequest}
 * @see 200 Authorization check results. See {@link OnBehalfOfUserAuthzCheckResponses}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user credentials. See {@link BasicFailedReply}
 * @see 403 Not authorised to run this endpoint. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function checkAuthorizationOnBehalfOfUrl(
  params: CheckAuthorizationOnBehalfOfUrlParams,
): string {
  const path = encode`/${params.regionId}/saas/users/${params.userIds}/authorization/_check`
  return `${getBasePath()}${path}`
}

export interface CheckAuthorizationOnBehalfOfUrlParams {
  userIds: string
  regionId: string
}

/**
 * GET /api/v1/saas/users/{user_id}
 * Operation: `get-saas-user`
 * Fetch user info.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.include Comma delimited list of optional properties to include in the output.
 * @see 200 User successfully fetched. See {@link SaasUserResponse}
 * @see 400 Invalid user id. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserUrl(params: GetSaasUserUrlParams): string {
  const queryString = buildQueryString({
    include: params.include,
  })
  const path = encode`/saas/users/${params.userId}`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetSaasUserUrlParams {
  userId: string
  include?: string | null
}

/**
 * GET /api/v1/saas/users/{user_id}/_password_params
 * Operation: `get-saas-reset-password-params`
 * Fetches reset password params for the user.
 * @param {string} params.userId Identifier for the user.
 * @see 200 reset password params successfully fetched. See {@link SaasResetPasswordLinkParamsResponse}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasResetPasswordParamsUrl(params: GetSaasResetPasswordParamsUrlParams): string {
  const path = encode`/saas/users/${params.userId}/_password_params`
  return `${getRawBasePath()}${path}`
}

export interface GetSaasResetPasswordParamsUrlParams {
  userId: string
}

/**
 * POST /api/v1/saas/users/{user_id}/_request_deletion
 * Operation: `request-saas-user-deletion`
 * Requests that a user be deleted.
 * @param {string} params.userId Identifier for the user.
 * @see 200 The request was processed successfully.
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function requestSaasUserDeletionUrl(params: RequestSaasUserDeletionUrlParams): string {
  const path = encode`/saas/users/${params.userId}/_request_deletion`
  return `${getRawBasePath()}${path}`
}

export interface RequestSaasUserDeletionUrlParams {
  userId: string
}

/**
 * POST /api/v1/saas/users/{user_id}/_scrub
 * Operation: `scrub-saas-user`
 * Scrubs personal identifiable information for a user.
 * @param {string} params.userId Identifier for the user.
 * @see 200 The user information was successfully scrubbed. See {@link SaasUserResponse}
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function scrubSaasUserUrl(params: ScrubSaasUserUrlParams): string {
  const path = encode`/saas/users/${params.userId}/_scrub`
  return `${getRawBasePath()}${path}`
}

export interface ScrubSaasUserUrlParams {
  userId: string
}

/**
 * GET /api/v1/saas/users/{user_id}/auth/mfa
 * Operation: `get-saas-user-mfa-enabled`
 * Fetch a user's MFA enabled status.
 * @param {string} params.userId Identifier for the user.
 * @see 200 The MFA enabled/disabled state response. See {@link SaasAuthMfaEnabledResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserMfaEnabledUrl(params: GetSaasUserMfaEnabledUrlParams): string {
  const path = encode`/saas/users/${params.userId}/auth/mfa`
  return `${getRawBasePath()}${path}`
}

export interface GetSaasUserMfaEnabledUrlParams {
  userId: string
}

/**
 * PUT /api/v1/saas/users/{user_id}/auth/mfa
 * Operation: `set-saas-user-mfa-enabled`
 * Enables or disables MFA for a user.
 * @param {string} params.userId Identifier for the user.
 * @see Accepts a request payload of type {@link SaasAuthMfaEnabledRequest}
 * @see 200 The MFA enabled/disabled state response. See {@link SaasAuthMfaEnabledResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function setSaasUserMfaEnabledUrl(params: SetSaasUserMfaEnabledUrlParams): string {
  const path = encode`/saas/users/${params.userId}/auth/mfa`
  return `${getRawBasePath()}${path}`
}

export interface SetSaasUserMfaEnabledUrlParams {
  userId: string
}

/**
 * GET /api/v1/saas/users/{user_id}/auth/mfa/devices
 * Operation: `get-saas-user-mfa-devices`
 * Returns all available MFA devices for a user.
 * @param {string} params.userId Identifier for the user.
 * @see 200 The MFA devices response. See {@link SaasAuthMfaDevicesResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserMfaDevicesUrl(params: GetSaasUserMfaDevicesUrlParams): string {
  const path = encode`/saas/users/${params.userId}/auth/mfa/devices`
  return `${getRawBasePath()}${path}`
}

export interface GetSaasUserMfaDevicesUrlParams {
  userId: string
}

/**
 * POST /api/v1/saas/users/{user_id}/auth/mfa/devices
 * Operation: `enroll-saas-user-mfa-device`
 * Enrolls a new MFA device for a user.
 * @param {string} params.userId Identifier for the user.
 * @see Accepts a request payload of type {@link SaasAuthMfaEnrollDeviceRequest}
 * @see 200 The MFA device enrollment response. See {@link SaasAuthMfaDeviceResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function enrollSaasUserMfaDeviceUrl(params: EnrollSaasUserMfaDeviceUrlParams): string {
  const path = encode`/saas/users/${params.userId}/auth/mfa/devices`
  return `${getRawBasePath()}${path}`
}

export interface EnrollSaasUserMfaDeviceUrlParams {
  userId: string
}

/**
 * PUT /api/v1/saas/users/{user_id}/auth/mfa/devices/{device_id}
 * Operation: `activate-saas-user-mfa-device`
 * Activates an enrolled MFA device for a user.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see Accepts a request payload of type {@link SaasAuthMfaActivateDeviceRequest}
 * @see 200 The MFA device activation response. See {@link SaasAuthMfaDeviceResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function activateSaasUserMfaDeviceUrl(params: ActivateSaasUserMfaDeviceUrlParams): string {
  const path = encode`/saas/users/${params.userId}/auth/mfa/devices/${params.deviceId}`
  return `${getRawBasePath()}${path}`
}

export interface ActivateSaasUserMfaDeviceUrlParams {
  userId: string
  deviceId: string
}

/**
 * DELETE /api/v1/saas/users/{user_id}/auth/mfa/devices/{device_id}
 * Operation: `delete-saas-user-mfa-device`
 * Deletes an enrolled MFA device for a user.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.deviceId Identifier for the MFA device.
 * @see 200 The MFA device deleted response. See {@link SaasAuthMfaDeviceDeletedResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteSaasUserMfaDeviceUrl(params: DeleteSaasUserMfaDeviceUrlParams): string {
  const path = encode`/saas/users/${params.userId}/auth/mfa/devices/${params.deviceId}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteSaasUserMfaDeviceUrlParams {
  userId: string
  deviceId: string
}

/**
 * GET /api/v1/saas/users/{user_id}/auth/okta/groups
 * Operation: `get-saas-user-okta-groups`
 * Get a list of a user okta groups.
 * @param {string} params.userId Identifier for the user.
 * @see 200 List of user's okta groups. See {@link SaasUserOktaGroupsResponse}
 * @see 401 Invalid user credentials. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserOktaGroupsUrl(params: GetSaasUserOktaGroupsUrlParams): string {
  const path = encode`/saas/users/${params.userId}/auth/okta/groups`
  return `${getRawBasePath()}${path}`
}

export interface GetSaasUserOktaGroupsUrlParams {
  userId: string
}

/**
 * POST /api/v1/saas/users/{user_id}/auth/okta/groups/{group_name}
 * Operation: `enroll-saas-user-in-okta-group`
 * Enroll the user in an okta group.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.groupName Name of the okta group to enroll the user into.
 * @see 200 List of use's okta groups. See {@link SaasAuthResponse}
 * @see 401 Invalid user credentials. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function enrollSaasUserInOktaGroupUrl(params: EnrollSaasUserInOktaGroupUrlParams): string {
  const path = encode`/saas/users/${params.userId}/auth/okta/groups/${params.groupName}`
  return `${getRawBasePath()}${path}`
}

export interface EnrollSaasUserInOktaGroupUrlParams {
  userId: string
  groupName: string
}

/**
 * GET /api/v1/saas/users/{user_id}/email/{email_domain}/suppressions
 * Operation: `get-saas-user-email-suppressions`
 * Get list of email suppressions for given user and domain.
 * @param {string} params.email Override email to retrieve suppressions for.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.emailDomain Email domain for the suppression.
 * @see 200 Suppressions successfully retrieved. See {@link SaasUserEmailSuppressions}
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getSaasUserEmailSuppressionsUrl(
  params: GetSaasUserEmailSuppressionsUrlParams,
): string {
  const queryString = buildQueryString({
    email: params.email,
  })
  const path = encode`/saas/users/${params.userId}/email/${params.emailDomain}/suppressions`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetSaasUserEmailSuppressionsUrlParams {
  email?: string | null
  userId: string
  emailDomain: string
}

/**
 * DELETE /api/v1/saas/users/{user_id}/email/{email_domain}/suppressions
 * Operation: `delete-saas-user-email-suppressions`
 * Remove email suppressions for given user and domain.
 * @param {string} params.email Override email to remove suppressions for.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.emailDomain Email domain for the suppression.
 * @see 200 Suppressions successfully deleted
 * @see 400 Invalid request. See {@link BasicFailedReply}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteSaasUserEmailSuppressionsUrl(
  params: DeleteSaasUserEmailSuppressionsUrlParams,
): string {
  const queryString = buildQueryString({
    email: params.email,
  })
  const path = encode`/saas/users/${params.userId}/email/${params.emailDomain}/suppressions`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface DeleteSaasUserEmailSuppressionsUrlParams {
  email?: string | null
  userId: string
  emailDomain: string
}

/**
 * POST /api/v1/saas/users/{user_id}/mfa/_reset
 * Operation: `reset-saas-user-mfa`
 * Resets MFA for a user.
 * @param {string} params.userId Identifier for the user.
 * @param {string} params.deviceId Optional device ID to reset. If not provided this as a query parameter, all devices will be reset.
 * @see 200 The MFA was successfully reseted
 * @see 404 User not found. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function resetSaasUserMfaUrl(params: ResetSaasUserMfaUrlParams): string {
  const queryString = buildQueryString({
    device_id: params.deviceId,
  })
  const path = encode`/saas/users/${params.userId}/mfa/_reset`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface ResetSaasUserMfaUrlParams {
  userId: string
  deviceId?: string | null
}

/**
 * PUT /api/v1/saas/users/{user_id}/profile
 * Operation: `put-saas-user`
 * Update a user's profile as an admin.
 * @param {string} params.userId Identifier for the user.
 * @see Accepts a request payload of type {@link SaasUpdateProfileRequestData}
 * @see 200 User successfully updated. See {@link SaasUserResponse}
 * @see 401 Invalid user authentication. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function putSaasUserUrl(params: PutSaasUserUrlParams): string {
  const path = encode`/saas/users/${params.userId}/profile`
  return `${getRawBasePath()}${path}`
}

export interface PutSaasUserUrlParams {
  userId: string
}

/**
 * GET /api/v1/regions/{region_id}/stack/instance-types
 * Operation: `get-instance-types`
 * Retrieves a list of all instance types.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 List of node types. See {@link Array<InstanceTypeResource>}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getInstanceTypesUrl(params: GetInstanceTypesUrlParams): string {
  const path = encode`/${params.regionId}/stack/instance-types`
  return `${getBasePath()}${path}`
}

export interface GetInstanceTypesUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/stack/versions
 * Operation: `get-version-stacks`
 * By default, retrieves only the available Elastic Stack versions. To retrieve all of the Elastic Stack versions, use the `show_deleted parameter`.
 * @param {boolean} params.showDeleted Whether to show deleted stack versions or not.
 * @param {boolean} params.showUnusable Whether to show versions that are unusable by the authenticated user.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The list of all available Elastic Stack versions are retrieved, including the template version and structure. See {@link StackVersionConfigs}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getVersionStacksUrl(params: GetVersionStacksUrlParams): string {
  const queryString = buildQueryString({
    show_deleted: params.showDeleted === undefined ? false : params.showDeleted,
    show_unusable: params.showUnusable === undefined ? false : params.showUnusable,
  })
  const path = encode`/${params.regionId}/stack/versions`
  return `${getBasePath()}${path}${queryString}`
}

export interface GetVersionStacksUrlParams {
  showDeleted?: boolean | null
  showUnusable?: boolean | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/stack/versions
 * Operation: `update-stack-packs`
 * Creates or updates an Elastic Stack pack and template. The endpoint supports `multipart/form-data` requests, as well as `application/zip` and `application/octet-stream` requests with a binary body. The maximum size of the payload is 1Mb. When the archive contains an Elastic Stack configuration that is available through the API, the configuration and template are overwritten.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link File}
 * @see 200 The Elastic Stack pack successfully processed. See {@link StackVersionArchiveProcessingResult}
 * @see 400 * There is an error in the file. Choose a different file, then try again. (code: `stackpack.bad_archive`)
 * Some unexpected error occurred. (code: `stackpack.unknown_internal_error`). See {@link StackVersionArchiveProcessingResult}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateStackPacksUrl(params: UpdateStackPacksUrlParams): string {
  const path = encode`/${params.regionId}/stack/versions`
  return `${getBasePath()}${path}`
}

export interface UpdateStackPacksUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/stack/versions/access
 * Operation: `get-version-access-management`
 * Lists existing AccessManagement for stack versions.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The AccessManagement data was successfully retrieved. See {@link AccessManagementResponse}
 * @see 500 Bad AccessManagement data was already on the server. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getVersionAccessManagementUrl(params: GetVersionAccessManagementUrlParams): string {
  const path = encode`/${params.regionId}/stack/versions/access`
  return `${getBasePath()}${path}`
}

export interface GetVersionAccessManagementUrlParams {
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/stack/versions/access
 * Operation: `create-version-access-management`
 * Inserts a new AccessManagement for stack versions. Overwrites existing data.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link CreateAccessManagementRequest}
 * @see 201 The AccessManagement data was inserted successfully. See {@link AccessManagementResponse}
 * @see 400 Bad AccessManagement data was sent. See {@link BasicFailedReply}
 * @see 500 Bad AccessManagement data was already on the server. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createVersionAccessManagementUrl(
  params: CreateVersionAccessManagementUrlParams,
): string {
  const path = encode`/${params.regionId}/stack/versions/access`
  return `${getBasePath()}${path}`
}

export interface CreateVersionAccessManagementUrlParams {
  regionId: string
}

/**
 * PATCH /api/v1/regions/{region_id}/stack/versions/access
 * Operation: `patch-version-access-management`
 * Updates new AccessManagement for stack versions. Overwrites existing data.
 * @param {integer} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link UpdateAccessManagementRequest}
 * @see 200 The AccessManagement data was updated successfully. See {@link AccessManagementResponse}
 * @see 400 Bad AccessManagement data was sent. See {@link BasicFailedReply}
 * @see 500 Bad AccessManagement data was already on the server. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function patchVersionAccessManagementUrl(
  params: PatchVersionAccessManagementUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/stack/versions/access`
  return `${getBasePath()}${path}${queryString}`
}

export interface PatchVersionAccessManagementUrlParams {
  version?: number | null
  regionId: string
}

/**
 * POST /api/v1/regions/{region_id}/stack/versions/access/blacklisted-upgrades
 * Operation: `add-blacklisted-upgrade`
 * Adds a blacklisted upgrade version-from matcher to multiple version-to matchers. If the provided version-from matcher already has existing version-to matchers, this is appended. If it does not, a new list is started.
 * @param {integer} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link BlacklistedUpgrade}
 * @see 200 The AccessManagement data was updated successfully. See {@link AccessManagementResponse}
 * @see 400 Bad AccessManagement data was sent. See {@link BasicFailedReply}
 * @see 500 Bad AccessManagement data was already on the server. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function addBlacklistedUpgradeUrl(params: AddBlacklistedUpgradeUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/stack/versions/access/blacklisted-upgrades`
  return `${getBasePath()}${path}${queryString}`
}

export interface AddBlacklistedUpgradeUrlParams {
  version?: number | null
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/stack/versions/access/blacklisted-upgrades/{from_version_matcher}
 * Operation: `remove-blacklisted-upgrade`
 * Removes a blacklisted upgrade version matcher by version-from matcher.
 * @param {integer} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.fromVersionMatcher A matcher expression for upgrade-from versions to de-blacklist.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The AccessManagement data was updated successfully. See {@link AccessManagementResponse}
 * @see 400 Bad AccessManagement data was sent. See {@link BasicFailedReply}
 * @see 500 Bad AccessManagement data was already on the server. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function removeBlacklistedUpgradeUrl(params: RemoveBlacklistedUpgradeUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/stack/versions/access/blacklisted-upgrades/${params.fromVersionMatcher}`
  return `${getBasePath()}${path}${queryString}`
}

export interface RemoveBlacklistedUpgradeUrlParams {
  version?: number | null
  fromVersionMatcher: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/stack/versions/access/blacklisted-upgrades/{from_version_matcher}/{to_version_matcher}
 * Operation: `add-blacklisted-upgrade-pair`
 * Adds a blacklisted upgrade version matcher pair. If the provided version-from matcher already has existing version-to matchers, this is appeneded. If it does not, a new list is started.
 * @param {integer} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.fromVersionMatcher A matcher expression for upgrade-from versions to blacklist.
 * @param {string} params.toVersionMatcher A matcher expression for upgrade-to versions to blacklist.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The AccessManagement data was updated successfully. See {@link AccessManagementResponse}
 * @see 400 Bad AccessManagement data was sent. See {@link BasicFailedReply}
 * @see 500 Bad AccessManagement data was already on the server. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function addBlacklistedUpgradePairUrl(params: AddBlacklistedUpgradePairUrlParams): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/stack/versions/access/blacklisted-upgrades/${params.fromVersionMatcher}/${params.toVersionMatcher}`
  return `${getBasePath()}${path}${queryString}`
}

export interface AddBlacklistedUpgradePairUrlParams {
  version?: number | null
  fromVersionMatcher: string
  toVersionMatcher: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/stack/versions/access/blacklisted-upgrades/{from_version_matcher}/{to_version_matcher}
 * Operation: `remove-blacklisted-upgrade-pair`
 * Removes a blacklisted upgrade-to version matcher from the given upgrade-from version matcher. If upgrade-from matcher no longer has any upgrade-to version matchers, it too is removed. .
 * @param {integer} params.version If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.fromVersionMatcher A matcher expression for upgrade-from versions to un-blacklist.
 * @param {string} params.toVersionMatcher A matcher expression for upgrade-to versions to un-blacklist.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The AccessManagement data was updated successfully. See {@link AccessManagementResponse}
 * @see 400 Bad AccessManagement data was sent. See {@link BasicFailedReply}
 * @see 500 Bad AccessManagement data was already on the server. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function removeBlacklistedUpgradePairUrl(
  params: RemoveBlacklistedUpgradePairUrlParams,
): string {
  const queryString = buildQueryString({
    version: params.version,
  })
  const path = encode`/${params.regionId}/stack/versions/access/blacklisted-upgrades/${params.fromVersionMatcher}/${params.toVersionMatcher}`
  return `${getBasePath()}${path}${queryString}`
}

export interface RemoveBlacklistedUpgradePairUrlParams {
  version?: number | null
  fromVersionMatcher: string
  toVersionMatcher: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/stack/versions/access/organizations/{organization_id}
 * Operation: `get-version-organization-access-management`
 * Find a AccessManagement for stack versions for an organization.
 * @param {string} params.organizationId Organization id whom you would like to retrieve AccessManagement data for.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Organization-specific AccessManagement data was retrieved successfully. See {@link OrganizationSpecificAccessManagementResponse}
 * @see 400 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getVersionOrganizationAccessManagementUrl(
  params: GetVersionOrganizationAccessManagementUrlParams,
): string {
  const path = encode`/${params.regionId}/stack/versions/access/organizations/${params.organizationId}`
  return `${getBasePath()}${path}`
}

export interface GetVersionOrganizationAccessManagementUrlParams {
  organizationId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/stack/versions/access/organizations/{organization_id}
 * Operation: `upsert-version-organization-access-management`
 * Inserts or updates a new AccessManagement for stack versions for an organization.
 * @param {string} params.organizationId Organization id whom you would like to retrieve AccessManagement data for.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link OrganizationSpecificAccessManagementData}
 * @see 200 The Organization-specific AccessManagement data was inserted successfully. See {@link OrganizationSpecificAccessManagementResponse}
 * @see 400 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function upsertVersionOrganizationAccessManagementUrl(
  params: UpsertVersionOrganizationAccessManagementUrlParams,
): string {
  const path = encode`/${params.regionId}/stack/versions/access/organizations/${params.organizationId}`
  return `${getBasePath()}${path}`
}

export interface UpsertVersionOrganizationAccessManagementUrlParams {
  organizationId: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/stack/versions/access/{version}
 * Operation: `add-whitelisted-version`
 * Adds a whitelisted version to Access Management.
 * @param {integer} params.versionQuery If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.version An Elastic Stack version.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The AccessManagement data was updated successfully. See {@link AccessManagementResponse}
 * @see 400 Bad AccessManagement data was sent. See {@link BasicFailedReply}
 * @see 500 Bad AccessManagement data was already on the server. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function addWhitelistedVersionUrl(params: AddWhitelistedVersionUrlParams): string {
  const queryString = buildQueryString({
    version: params.versionQuery,
  })
  const path = encode`/${params.regionId}/stack/versions/access/${params.version}`
  return `${getBasePath()}${path}${queryString}`
}

export interface AddWhitelistedVersionUrlParams {
  versionQuery?: number | null
  version: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/stack/versions/access/{version}
 * Operation: `remove-whitelisted-version`
 * Removes a whitelisted version from Access Management.
 * @param {integer} params.versionQuery If specified then checks for conflicts against the version stored in the persistent store (returned in 'x-cloud-resource-version' of the GET request).
 * @param {string} params.version An Elastic Stack version.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The AccessManagement data was updated successfully. See {@link AccessManagementResponse}
 * @see 400 Bad AccessManagement data was sent. See {@link BasicFailedReply}
 * @see 500 Bad AccessManagement data was already on the server. See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function removeWhitelistedVersionUrl(params: RemoveWhitelistedVersionUrlParams): string {
  const queryString = buildQueryString({
    version: params.versionQuery,
  })
  const path = encode`/${params.regionId}/stack/versions/access/${params.version}`
  return `${getBasePath()}${path}${queryString}`
}

export interface RemoveWhitelistedVersionUrlParams {
  versionQuery?: number | null
  version: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/stack/versions/{version}
 * Operation: `get-version-stack`
 * Retrieves the Elastic Stack version and template.
 * @param {string} params.version The Elastic Stack version. For example, `5.3.1` or `5.0.0-RC4`.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The Elastic Stack version is found. See {@link StackVersionConfig}
 * @see 404 The Elastic Stack version can't be found. (code: `stackpack.version_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getVersionStackUrl(params: GetVersionStackUrlParams): string {
  const path = encode`/${params.regionId}/stack/versions/${params.version}`
  return `${getBasePath()}${path}`
}

export interface GetVersionStackUrlParams {
  version: string
  regionId: string
}

/**
 * PUT /api/v1/regions/{region_id}/stack/versions/{version}
 * Operation: `update-version-stack`
 * Updates the Elastic Stack version configuration.
 * @param {string} params.version The Elastic Stack version. For example, `5.3.1` or `5.0.0-RC4`.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link StackVersionConfigPost}
 * @see 200 The Elastic Stack version successfully updated. See {@link StackVersionConfig}
 * @see 404 The Elastic Stack version can't be found. (code: `stackpack.missing_platform_version`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateVersionStackUrl(params: UpdateVersionStackUrlParams): string {
  const path = encode`/${params.regionId}/stack/versions/${params.version}`
  return `${getBasePath()}${path}`
}

export interface UpdateVersionStackUrlParams {
  version: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/stack/versions/{version}
 * Operation: `delete-version-stack`
 * Uses the `deleted` flag, which removes only that version of the Elastic Stack from the list of available versions. To restore the version, send an update request. For more information, see the PUT request.
 * @param {string} params.version The Elastic Stack version. For example, `5.3.1` or `5.0.0-RC4`.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The `deleted` flag is applied to the specified Elastic Stack version.
 * @see 404 The Elastic Stack version can't be found. (code: `stackpack.version_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteVersionStackUrl(params: DeleteVersionStackUrlParams): string {
  const path = encode`/${params.regionId}/stack/versions/${params.version}`
  return `${getBasePath()}${path}`
}

export interface DeleteVersionStackUrlParams {
  version: string
  regionId: string
}

/**
 * GET /api/v1/trusted-environments
 * Operation: `get-trusted-envs`
 * Returns the list of trusted environments for the organization. If no organization is passed in, the organization attached to the user is assumed.
 * @param {string} params.organizationId (Optional) Organization Id for which to retrieve the trusted environments.
 * @see 200 Trusted environments for this organization. See {@link ElasticsearchClusterTrustSettings}
 * @see 404 Organization not found. (code: `organization.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getTrustedEnvsUrl(params: GetTrustedEnvsUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    organization_id: params.organizationId,
  })
  const path = `/trusted-environments`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetTrustedEnvsUrlParams {
  organizationId?: string | null
}

/**
 * GET /api/v1/user
 * Operation: `get-current-user`
 * Fetch current user information.
 * @see 200 User successfully fetched. See {@link User}
 * @see 404 User not found. (code: `user.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getCurrentUserUrl(): string {
  const path = `/user`
  return `${getRawBasePath()}${path}`
}

/**
 * PATCH /api/v1/user
 * Operation: `update-current-user`
 * Updates the current user.
 * @see Accepts a request payload of type {@link string}
 * @see 200 User successfully updated. See {@link User}
 * @see 400 * Some of the provided roles are invalid. (code: `user.roles.invalid`)
 * Some of the provided roles are forbidden. (code: `user.roles.forbidden`)
 * Trying to set a restricted field. (code: `user.restricted_field`)
 * External users cannot be modified. (code: `user.cannot_modify_external`)
 * Built-in users cannot be modified. (code: `user.cannot_modify`). See {@link BasicFailedReply}
 * @see 404 User not found. (code: `user.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateCurrentUserUrl(): string {
  const path = `/user`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/users
 * Operation: `get-users`
 * Fetches all available users.
 * @param {boolean} params.includeDisabled True if disabled users should be included in the response.
 * @see 200 Users successfully fetched. See {@link UserList}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getUsersUrl(params: GetUsersUrlParams = emptyParams): string {
  const queryString = buildQueryString({
    include_disabled: params.includeDisabled === undefined ? false : params.includeDisabled,
  })
  const path = `/users`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface GetUsersUrlParams {
  includeDisabled?: boolean | null
}

/**
 * POST /api/v1/users
 * Operation: `create-user`
 * Creates a new user.
 * @see Accepts a request payload of type {@link User}
 * @see 200 User successfully created. See {@link User}
 * @see 400 * The provided user name is invalid. Check that it is not empty and it does not contain special characters. (code: `user.user_name.invalid`)
 * Some of the provided roles are invalid. (code: `user.roles.invalid`)
 * Some of the provided roles are forbidden. (code: `user.roles.forbidden`)
 * Trying to set a restricted field. (code: `user.restricted_field`). See {@link BasicFailedReply}
 * @see 409 The username is already in use. (code: `user.user_name.conflict`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createUserUrl(): string {
  const path = `/users`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/users/auth
 * Operation: `get-authentication-info`
 * Provides authentication information about a user, including elevated permission status and TOTP device availability.
 * @see 200 User authentication information response. See {@link AuthenticationInfo}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getAuthenticationInfoUrl(): string {
  const path = `/users/auth`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/regions/{region_id}/users/auth/_login
 * Operation: `login`
 * Authenticates against available users.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link LoginRequest}
 * @see 200 Login successful, returns the token in the body (if 'login_state.path' not specified). See {@link TokenResponse}
 * @see 302 Redirects to '/sso/token#BEARER_TOKEN?state=LOGIN_STATE' with the fragment containing a bearer token  (if 'login_state.path' is specified)
 * @see 401 The supplied authentication is invalid. (code: `root.unauthenticated`). See {@link BasicFailedReply}
 * @see 501 The administrator needs to configure the authentication cluster. (code: `authc.no_authentication_cluster`). See {@link BasicFailedReply}
 * @see 502 The authentication cluster failed to process the request. The response body contains details about the error. (code: `authc.authentication_cluster_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function loginUrl(params: LoginUrlParams): string {
  const path = encode`/${params.regionId}/users/auth/_login`
  return `${getBasePath()}${path}`
}

export interface LoginUrlParams {
  regionId: string
}

/**
 * POST /api/v1/users/auth/_logout
 * Operation: `logout`
 * Destroys the current session.
 * @see 200 The current session was successfully destroyed.
 * @see 501 The administrator needs to configure the authentication cluster. (code: `authc.no_authentication_cluster`). See {@link BasicFailedReply}
 * @see 502 The authentication cluster failed to process the request. The response body contains details about the error. (code: `authc.authentication_cluster_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function logoutUrl(): string {
  const path = `/users/auth/_logout`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/users/auth/_refresh
 * Operation: `refresh-token`
 * Issues a new authentication token.
 * @see 200 The token refreshed successfully and was returned in the body of the response. See {@link TokenResponse}
 * @see 401 The authentication token is invalid or expired. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @see 501 The administrator needs to configure the authentication cluster. (code: `authc.no_authentication_cluster`). See {@link BasicFailedReply}
 * @see 502 The authentication cluster failed to process the request. The response body contains details about the error. (code: `authc.authentication_cluster_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function refreshTokenUrl(): string {
  const path = `/users/auth/_refresh`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/users/auth/keys
 * Operation: `get-api-keys`
 * Retrieves the metadata for all of the API keys that the user generated.
 * @see 200 The metadata for the API keys is retrieved. See {@link ApiKeysResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getApiKeysUrl(): string {
  const path = `/users/auth/keys`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/users/auth/keys
 * Operation: `create-api-key`
 * Creates a new API key.
 * @see Accepts a request payload of type {@link CreateApiKeyRequest}
 * @see 201 The API key is created and returned in the body of the response. See {@link ApiKeyResponse}
 * @see 400 The request is invalid. Specify a different request, then try again. (code: `api_keys.invalid_input`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function createApiKeyUrl(): string {
  const path = `/users/auth/keys`
  return `${getRawBasePath()}${path}`
}

/**
 * DELETE /api/v1/users/auth/keys
 * Operation: `delete-api-keys`
 * Delete or invalidate API keys.
 * @see Accepts a request payload of type {@link DeleteApiKeysRequest}
 * @see 200 The API keys are deleted.
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteApiKeysUrl(): string {
  const path = `/users/auth/keys`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/users/auth/keys/_all
 * Operation: `get-users-api-keys`
 * Retrieves the metadata for all of the API keys for all users.
 * @see 200 The metadata for the API keys is retrieved. See {@link ApiKeysResponse}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getUsersApiKeysUrl(): string {
  const path = `/users/auth/keys/_all`
  return `${getRawBasePath()}${path}`
}

/**
 * DELETE /api/v1/users/auth/keys/_all
 * Operation: `delete-users-api-keys`
 * Delete or invalidate the API keys for multiple users.
 * @see Accepts a request payload of type {@link DeleteUsersApiKeysRequest}
 * @see 200 The API keys are deleted.
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteUsersApiKeysUrl(): string {
  const path = `/users/auth/keys/_all`
  return `${getRawBasePath()}${path}`
}

/**
 * GET /api/v1/regions/{region_id}/users/auth/keys/{api_key_id}
 * Operation: `get-api-key`
 * Retrieves the metadata for an API key.
 * @param {string} params.apiKeyId The API Key ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The API key metadata is retrieved. See {@link ApiKeyResponse}
 * @see 404 The {api_key_id} can't be found. (code: `api_keys.key_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getApiKeyUrl(params: GetApiKeyUrlParams): string {
  const path = encode`/${params.regionId}/users/auth/keys/${params.apiKeyId}`
  return `${getBasePath()}${path}`
}

export interface GetApiKeyUrlParams {
  apiKeyId: string
  regionId: string
}

/**
 * DELETE /api/v1/users/auth/keys/{api_key_id}
 * Operation: `delete-api-key`
 * Delete or invalidate the API key.
 * @param {string} params.apiKeyId The API Key ID.
 * @see 200 The API key is deleted.
 * @see 404 The {api_key_id} can't be found. (code: `api_keys.key_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteApiKeyUrl(params: DeleteApiKeyUrlParams): string {
  const path = encode`/users/auth/keys/${params.apiKeyId}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteApiKeyUrlParams {
  apiKeyId: string
}

/**
 * GET /api/v1/users/auth/methods
 * Operation: `methods`
 * Provides information about available authentication methods.
 * @see 200 Available authentication methods response. See {@link AvailableAuthenticationMethods}
 * @return {string} A fully-interpolated and escaped URL
 */
export function methodsUrl(): string {
  const path = `/users/auth/methods`
  return `${getRawBasePath()}${path}`
}

/**
 * POST /api/v1/regions/{region_id}/users/auth/saml/_callback
 * Operation: `saml-callback`
 * Accepts a callback request from an identity provider and authenticates the user.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see Accepts a request payload of type {@link string}
 * @see Accepts an optional request payload of type {@link string}
 * @see 302 Redirects to the UI endpoint with an authorization token in the fragment and the relay state, if it was specified during the initialization.
 * @see 401 The supplied SAML response is invalid. (code: `root.unauthenticated`). See {@link BasicFailedReply}
 * @see 501 The administrator needs to configure the authentication cluster. (code: `authc.no_authentication_cluster`). See {@link BasicFailedReply}
 * @see 502 The authentication cluster failed to process the request. The response body contains details about the error. (code: `authc.authentication_cluster_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function samlCallbackUrl(params: SamlCallbackUrlParams): string {
  const path = encode`/${params.regionId}/users/auth/saml/_callback`
  return `${getBasePath()}${path}`
}

export interface SamlCallbackUrlParams {
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/users/auth/saml/_init
 * Operation: `saml-init`
 * Calls the authentication cluster to initiate SAML Single Sign-on (Web Browser SSO profile) protocol and redirects the user to the identity provider for authentication. The authentication cluster must be configured prior to initiation.
 * @param {string} params.state An optional relay state that is sent back to the client after the user is authenticated.
 * @param {string} params.realm An optional SAML realm to use for authentication.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 302 Redirects the client to the identity provider with a SAML authentication request
 * @see 501 The administrator needs to configure the authentication cluster. (code: `authc.no_authentication_cluster`). See {@link BasicFailedReply}
 * @see 502 The authentication cluster failed to process the request. The response body contains details about the error. (code: `authc.authentication_cluster_error`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function samlInitUrl(params: SamlInitUrlParams): string {
  const queryString = buildQueryString({
    state: params.state,
    realm: params.realm,
  })
  const path = encode`/${params.regionId}/users/auth/saml/_init`
  return `${getBasePath()}${path}${queryString}`
}

export interface SamlInitUrlParams {
  state?: string | null
  realm?: string | null
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/users/{user_id}/auth/keys
 * Operation: `get-user-api-keys`
 * Retrieves metadata for all API keys created by the given user.
 * @param {string} params.userId The user ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The API key metadata is retrieved. See {@link ApiKeysResponse}
 * @see 404 The {user_id} can't be found. (code: `api_keys.user_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getUserApiKeysUrl(params: GetUserApiKeysUrlParams): string {
  const path = encode`/${params.regionId}/users/${params.userId}/auth/keys`
  return `${getBasePath()}${path}`
}

export interface GetUserApiKeysUrlParams {
  userId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/users/{user_id}/auth/keys
 * Operation: `delete-user-api-keys`
 * Delete or invalidate all of the API keys for a user.
 * @param {string} params.userId The user ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The API key is deleted.
 * @see 404 The {user_id} can't be found. (code: `api_keys.user_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteUserApiKeysUrl(params: DeleteUserApiKeysUrlParams): string {
  const path = encode`/${params.regionId}/users/${params.userId}/auth/keys`
  return `${getBasePath()}${path}`
}

export interface DeleteUserApiKeysUrlParams {
  userId: string
  regionId: string
}

/**
 * GET /api/v1/regions/{region_id}/users/{user_id}/auth/keys/{api_key_id}
 * Operation: `get-user-api-key`
 * Retrieves the API key metadata for a user.
 * @param {string} params.userId The user ID.
 * @param {string} params.apiKeyId The API Key ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The API key metadata is retrieved. See {@link ApiKeyResponse}
 * @see 404 The {api_key_id} can't be found. (code: `api_keys.key_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getUserApiKeyUrl(params: GetUserApiKeyUrlParams): string {
  const path = encode`/${params.regionId}/users/${params.userId}/auth/keys/${params.apiKeyId}`
  return `${getBasePath()}${path}`
}

export interface GetUserApiKeyUrlParams {
  userId: string
  apiKeyId: string
  regionId: string
}

/**
 * DELETE /api/v1/regions/{region_id}/users/{user_id}/auth/keys/{api_key_id}
 * Operation: `delete-user-api-key`
 * Delete or invalidate an API key for a user.
 * @param {string} params.userId The user ID.
 * @param {string} params.apiKeyId The API Key ID.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 The API key is deleted.
 * @see 404 The {api_key_id} can't be found. (code: `api_keys.key_not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteUserApiKeyUrl(params: DeleteUserApiKeyUrlParams): string {
  const path = encode`/${params.regionId}/users/${params.userId}/auth/keys/${params.apiKeyId}`
  return `${getBasePath()}${path}`
}

export interface DeleteUserApiKeyUrlParams {
  userId: string
  apiKeyId: string
  regionId: string
}

/**
 * POST /api/v1/users/{user_id}/role_assignments
 * Operation: `add-role-assignments`
 * Adds a set of RoleAssignments to the specified User. Currently unavailable in self-hosted ECE.
 * @param {string} params.userId Identifier for the user; include realm name and id if required.
 * @see Accepts a request payload of type {@link RoleAssignments}
 * @see 200 Role Assignments were successfully added to the target User
 * @see 400 The target user Id is invalid. (code: `role_assignments.invalid_target_user_id`). See {@link BasicFailedReply}
 * @see 401 Credentials were invalid. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @see 403 You are not authorised to add the specified RoleAssignments. (code: `role_assignments.unauthorized_role_assignments`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function addRoleAssignmentsUrl(params: AddRoleAssignmentsUrlParams): string {
  const path = encode`/users/${params.userId}/role_assignments`
  return `${getRawBasePath()}${path}`
}

export interface AddRoleAssignmentsUrlParams {
  userId: string
}

/**
 * DELETE /api/v1/users/{user_id}/role_assignments
 * Operation: `remove-role-assignments`
 * Removes a set of RoleAssignments from the specified User. Currently unavailable in self-hosted ECE.
 * @param {string} params.userId Identifier for the user; include realm name and id if required.
 * @param {boolean} params.force Whether or not to force the removal of role assignments (effective only for Platform Admins).
 * @see Accepts a request payload of type {@link RoleAssignments}
 * @see 200 Role Assignments were successfully removed from the target User
 * @see 400 The target user Id is invalid. (code: `role_assignments.invalid_target_user_id`). See {@link BasicFailedReply}
 * @see 401 Credentials were invalid. (code: `root.unauthorized`). See {@link BasicFailedReply}
 * @see 403 You are not authorised to remove the specified RoleAssignments. (code: `role_assignments.unauthorized_role_assignments`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function removeRoleAssignmentsUrl(params: RemoveRoleAssignmentsUrlParams): string {
  const queryString = buildQueryString({
    force: params.force === undefined ? false : params.force,
  })
  const path = encode`/users/${params.userId}/role_assignments`
  return `${getRawBasePath()}${path}${queryString}`
}

export interface RemoveRoleAssignmentsUrlParams {
  userId: string
  force?: boolean | null
}

/**
 * GET /api/v1/regions/{region_id}/users/{user_name}
 * Operation: `get-user`
 * Fetches a single user.
 * @param {string} params.userName Identifier for the user.
 * @param {string} params.regionId Injected by `yarn generate-swagger-contracts`.
 * @see 200 User successfully fetched. See {@link User}
 * @see 404 User not found. (code: `user.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function getUserUrl(params: GetUserUrlParams): string {
  const path = encode`/${params.regionId}/users/${params.userName}`
  return `${getBasePath()}${path}`
}

export interface GetUserUrlParams {
  userName: string
  regionId: string
}

/**
 * DELETE /api/v1/users/{user_name}
 * Operation: `delete-user`
 * Deletes an existing user.
 * @param {string} params.userName Identifier for the user.
 * @see 200 User successfully deleted
 * @see 400 * The user cannot be deleted. (code: `user.restricted_deletion`)
 * External users cannot be modified. (code: `user.cannot_modify_external`)
 * Built-in users cannot be modified. (code: `user.cannot_modify`). See {@link BasicFailedReply}
 * @see 404 User not found. (code: `user.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function deleteUserUrl(params: DeleteUserUrlParams): string {
  const path = encode`/users/${params.userName}`
  return `${getRawBasePath()}${path}`
}

export interface DeleteUserUrlParams {
  userName: string
}

/**
 * PATCH /api/v1/users/{user_name}
 * Operation: `update-user`
 * Updates an existing user.
 * @param {string} params.userName Identifier for the user.
 * @see Accepts a request payload of type {@link string}
 * @see 200 User successfully updated. See {@link User}
 * @see 400 * Some of the provided roles are invalid. (code: `user.roles.invalid`)
 * Some of the provided roles are forbidden. (code: `user.roles.forbidden`)
 * Trying to set a restricted field. (code: `user.restricted_field`)
 * External users cannot be modified. (code: `user.cannot_modify_external`)
 * Built-in users cannot be modified. (code: `user.cannot_modify`). See {@link BasicFailedReply}
 * @see 404 User not found. (code: `user.not_found`). See {@link BasicFailedReply}
 * @return {string} A fully-interpolated and escaped URL
 */
export function updateUserUrl(params: UpdateUserUrlParams): string {
  const path = encode`/users/${params.userName}`
  return `${getRawBasePath()}${path}`
}

export interface UpdateUserUrlParams {
  userName: string
}
